import { pgTable, text, varchar, serial, integer, date, jsonb, timestamp, index, uniqueIndex, boolean, numeric, real, customType, type AnyPgColumn } from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

const _numericFloat = customType<{ data: number; driverData: string; config: { precision?: number; scale?: number } }>({
  dataType(config) {
    const precision = config?.precision ?? 10;
    const scale = config?.scale ?? 2;
    return `numeric(${precision},${scale})`;
  },
  fromDriver(value: string): number {
    return parseFloat(value);
  },
  toDriver(value: number): string {
    return String(value);
  },
});

const numericAsText = customType<{ data: number; driverData: string }>({
  dataType() {
    return "text";
  },
  fromDriver(value: string): number {
    return parseFloat(value);
  },
  toDriver(value: number): string {
    return String(value);
  },
});

export const artists = pgTable("artists", {
  id: serial("id").primaryKey(),
  slug: varchar("slug", { length: 100 }).notNull().unique(), // migration-safety-ok — original column, present before migration tracking was added (Task #1873)
  name: text("name").notNull(), // migration-safety-ok — original column, present before migration tracking was added (Task #1873)
  role: text("role").notNull(), // migration-safety-ok — original column, present before migration tracking was added (Task #1873)
  image: text("image").notNull(), // migration-safety-ok — original column, present before migration tracking was added (Task #1873)
  detailImage: text("detail_image"),
  heroImages: jsonb("hero_images").$type<string[]>().default([]),
  heroImagePositions: jsonb("hero_image_positions").$type<Record<string, string>>().default({}),
  heroImageAdjustments: jsonb("hero_image_adjustments").$type<Record<string, { brightness?: number; contrast?: number; rotate?: number; zoom?: number }>>().default({}),
  bio: text("bio").notNull(), // migration-safety-ok — original column, present before migration tracking was added (Task #1873)
  instagram: text("instagram").notNull(), // migration-safety-ok — original column, present before migration tracking was added (Task #1873)
  specialties: text("specialties").array().notNull(), // migration-safety-ok — original column, present before migration tracking was added (Task #1873)
  accentColor: varchar("accent_color", { length: 20 }).notNull().default("#8b5cf6"),
  tattooingSince: integer("tattooing_since"),
  tattooingSinceMonth: integer("tattooing_since_month"),
  hourlyMin: integer("hourly_min").notNull().default(150),
  hourlyMax: integer("hourly_max").notNull().default(200),
  isActive: boolean("is_active").notNull().default(true),
  sortOrder: integer("sort_order").notNull().default(0),
  email: varchar("email", { length: 255 }),
  instagramAccessToken: text("instagram_access_token"),
  instagramUserId: varchar("instagram_user_id", { length: 50 }),
  instagramTokenExpiresAt: timestamp("instagram_token_expires_at"),
  commissionType: varchar("commission_type", { length: 20 }),
  commissionRate: numeric("commission_rate", { precision: 5, scale: 2 }),
  boothRentalAmount: numeric("booth_rental_amount", { precision: 10, scale: 2 }),
  boothRentalPeriod: varchar("booth_rental_period", { length: 10 }),
  serviceTypes: text("service_types").array().notNull().default(["Tattoo"]),
  portalToken: varchar("portal_token", { length: 64 }).unique(),
  portalTokenRotatedAt: timestamp("portal_token_rotated_at"),
  msgNotifyIntervalHours: integer("msg_notify_interval_hours"),
  notifyOnNewBooking: boolean("notify_on_new_booking").notNull().default(true),
  notifyOnCloseout: boolean("notify_on_closeout").notNull().default(true),
  notifyOnDailyDigest: boolean("notify_on_daily_digest").notNull().default(true),
  targetUtilizationPct: integer("target_utilization_pct").notNull().default(80),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // migration-safety-ok: nullable array — existing rows get NULL
  styleTags: text("style_tags").array(),
}, (table) => [
  // Covers getActiveArtistsFromDb() — WHERE is_active = true ORDER BY sort_order.
  // Without this index the query falls back to a seq scan; under pool pressure
  // even a tiny table scan can queue for 100+ s waiting for a connection slot.
  // INC-2026-07-06.
  index("idx_artists_is_active_sort").on(table.isActive, table.sortOrder),
]);

export const portfolioImages = pgTable("portfolio_images", {
  id: serial("id").primaryKey(),
  artistId: integer("artist_id")
    .notNull()
    .references(() => artists.id, { onDelete: "cascade" }),
  src: text("src").notNull(),
  alt: text("alt").notNull(),
  enabled: boolean("enabled").notNull().default(true),
  sortOrder: integer("sort_order").notNull().default(0),
});

export const events = pgTable("events", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  description: text("description").notNull(),
  date: date("date").notNull(),
  time: text("time").notNull(),
  location: text("location").notNull(),
  category: text("category").notNull(),
  image: text("image"),
  flyerImageUrl: text("flyer_image_url"),
  flyerThumbnailUrl: text("flyer_thumbnail_url"),
  adCopy: text("ad_copy"),
  socialCaption: text("social_caption"),
  hashtags: text("hashtags"),
  galleryUrl: text("gallery_url"),
  brandAssets: jsonb("brand_assets").$type<{ colors?: string[]; tagline?: string; cta?: string; adImages?: string[] }>(),
  isPublished: boolean("is_published").default(true).notNull(),
  rsvpEnabled: boolean("rsvp_enabled").default(false).notNull(),
  rsvpLimit: integer("rsvp_limit"),
  depositRequired: boolean("deposit_required").default(false).notNull(),
  depositAmountCents: integer("deposit_amount_cents"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_events_date").on(table.date),
]);

// ── Event RSVPs ───────────────────────────────────────────────────────────────
// Tracks per-client interest/attendance for RSVP-enabled events.
// One record per (eventId, clientEmail); upserted on status change.
// migration-safety-ok — brand-new table
export const eventRsvps = pgTable("event_rsvps", {
  // migration-safety-ok — brand-new table
  id: serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  eventId: integer("event_id").notNull().references(() => events.id, { onDelete: "cascade" }),
  // migration-safety-ok — brand-new table
  clientEmail: text("client_email").notNull(),
  // migration-safety-ok — brand-new table — 'interested' | 'going'
  status: text("status").notNull(),
  // migration-safety-ok — brand-new table
  depositPaid: boolean("deposit_paid").default(false).notNull(),
  // migration-safety-ok — brand-new table
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // migration-safety-ok — brand-new table
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_event_rsvps_event_id").on(table.eventId),
  index("idx_event_rsvps_client_email").on(table.clientEmail),
  uniqueIndex("idx_event_rsvps_unique").on(table.eventId, table.clientEmail),
]);

export type EventRsvp = typeof eventRsvps.$inferSelect;
export type InsertEventRsvp = typeof eventRsvps.$inferInsert;

export const RSVP_STATUSES = ["interested", "going"] as const;
export type RsvpStatus = typeof RSVP_STATUSES[number];

export const siteConfig = pgTable("site_config", {
  id: serial("id").primaryKey(),
  key: varchar("key", { length: 100 }).notNull().unique(),
  value: jsonb("value").notNull(),
});

export const contactSubmissions = pgTable("contact_submissions", {
  id: serial("id").primaryKey(),
  // migration-safety-ok — predates migration tracking; table created via drizzle push before pre-push-migrations.ts existed, later columns (e.g. responded_at) got ad-hoc ALTER TABLE guards which made the audit treat the whole table as tracked
  name: text("name").notNull(),
  // migration-safety-ok — predates migration tracking (see name above)
  email: text("email").notNull(),
  phone: text("phone"),
  artist: text("artist"),
  // migration-safety-ok — predates migration tracking (see name above)
  serviceType: text("service_type").notNull(),
  // migration-safety-ok — predates migration tracking (see name above)
  message: text("message").notNull(),
  source: text("source").default("website"),
  utmSource: varchar("utm_source", { length: 200 }),
  utmMedium: varchar("utm_medium", { length: 200 }),
  utmCampaign: varchar("utm_campaign", { length: 200 }),
  utmTerm: varchar("utm_term", { length: 200 }),
  utmContent: varchar("utm_content", { length: 200 }),
  referrer: varchar("referrer", { length: 1000 }),
  landingPage: varchar("landing_page", { length: 1000 }),
  // migration-safety-ok — predates migration tracking (see name above)
  createdAt: text("created_at").notNull(),
  respondedAt: timestamp("responded_at"),
  archivedAt: timestamp("archived_at"),
  aiReplyText: text("ai_reply_text"),
}, (table) => [
  index("idx_contact_source").on(table.source),
  index("idx_contact_created_at").on(table.createdAt),
]);

export const insertContactSubmissionSchema = createInsertSchema(contactSubmissions).omit({ id: true, respondedAt: true, archivedAt: true, aiReplyText: true }).extend({
  name: z.string().min(1).max(200),
  email: z.string().email().max(320),
  phone: z.string().min(1, "Phone number is required").max(30).refine((val) => {
    const digits = val.replace(/\D/g, "");
    if (digits.length < 10) return false;
    const core = digits.length === 11 && digits[0] === "1" ? digits.slice(1) : digits;
    if (core.length !== 10) return false;
    if (/^(\d)\1{9}$/.test(core)) return false;
    if (/^1234567890$/.test(core) || /^0123456789$/.test(core)) return false;
    if (/^(.{3})\1+$/.test(core)) return false;
    const areaCode = core.slice(0, 3);
    if (areaCode === "000" || areaCode === "555") return false;
    return true;
  }, "Please enter a valid 10-digit phone number"),
  artist: z.string().max(100).optional().nullable(),
  serviceType: z.string().min(1).max(100),
  message: z.string().min(1).max(5000),
  source: z.string().max(50).optional().nullable(),
  utmSource: z.string().max(200).optional().nullable(),
  utmMedium: z.string().max(200).optional().nullable(),
  utmCampaign: z.string().max(200).optional().nullable(),
  utmTerm: z.string().max(200).optional().nullable(),
  utmContent: z.string().max(200).optional().nullable(),
  referrer: z.string().max(1000).optional().nullable(),
  landingPage: z.string().max(1000).optional().nullable(),
  createdAt: z.string().max(100),
});
export type InsertContactSubmission = z.infer<typeof insertContactSubmissionSchema>;
export type ContactSubmission = typeof contactSubmissions.$inferSelect;

export const healingJourneys = pgTable("healing_journeys", {
  id: serial("id").primaryKey(),
  bookingId: integer("booking_id").references(() => bookingAppointments.id, { onDelete: "set null" }),
  tattooDescription: text("tattoo_description").notNull(),
  artist: text("artist"),
  placement: text("placement").notNull(),
  tattooDate: date("tattoo_date").notNull(),
  clientEmail: text("client_email"),
  clientName: text("client_name"),
  dripStep: integer("drip_step").notNull().default(0),
  googleReviewEmailSentAt: timestamp("google_review_email_sent_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_healing_journeys_booking").on(table.bookingId),
  index("idx_healing_journeys_drip").on(table.dripStep),
]);

export const healingEntries = pgTable("healing_entries", {
  id: serial("id").primaryKey(),
  journeyId: integer("journey_id")
    .notNull()
    .references(() => healingJourneys.id, { onDelete: "cascade" }),
  dayNumber: integer("day_number").notNull(),
  symptoms: text("symptoms").array().notNull(),
  painLevel: integer("pain_level").notNull(),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_healing_entries_journey").on(table.journeyId),
]);

export const healingActivity = pgTable("healing_activity", {
  id: serial("id").primaryKey(),
  event: varchar("event", { length: 50 }).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_healing_activity_event").on(table.event),
  index("idx_healing_activity_created").on(table.createdAt),
]);
export type HealingActivity = typeof healingActivity.$inferSelect;

export const insertHealingJourneySchema = createInsertSchema(healingJourneys).omit({ id: true, createdAt: true }).extend({
  tattooDescription: z.string().min(1).max(500),
  artist: z.string().max(200).optional().nullable(),
  placement: z.string().min(1).max(200),
  bookingId: z.number().int().optional().nullable(),
});
export type InsertHealingJourney = z.infer<typeof insertHealingJourneySchema>;
export type HealingJourney = typeof healingJourneys.$inferSelect;

export const insertHealingEntrySchema = createInsertSchema(healingEntries).omit({ id: true, createdAt: true }).extend({
  notes: z.string().max(2000).optional().nullable(),
  painLevel: z.number().int().min(0).max(10),
  dayNumber: z.number().int().min(0).max(365),
});
export type InsertHealingEntry = z.infer<typeof insertHealingEntrySchema>;
export type HealingEntry = typeof healingEntries.$inferSelect;

export const bookingAppointments = pgTable("booking_appointments", {
  id: serial("id").primaryKey(),
  artistSlug: varchar("artist_slug", { length: 100 }).notNull(), // migration-safety-ok — original column, present before migration tracking was added
  // migration-safety-ok — NOT NULL dropped via applyAllMigrations guard (soft-booking mode allows null date/time)
  date: date("date"),
  // migration-safety-ok — NOT NULL dropped via applyAllMigrations guard (soft-booking mode allows null date/time)
  time: varchar("time", { length: 50 }),
  name: text("name").notNull(), // migration-safety-ok — original column, present before migration tracking was added
  email: text("email").notNull(), // migration-safety-ok — original column, present before migration tracking was added
  phone: text("phone"),
  serviceType: text("service_type").notNull(), // migration-safety-ok — original column, present before migration tracking was added
  description: text("description"),
  status: varchar("status", { length: 20 }).notNull().default("confirmed"),
  referenceCode: varchar("reference_code", { length: 30 }).notNull(), // migration-safety-ok — original column, present before migration tracking was added
  designStyle: text("design_style"),
  estimatedCostMin: integer("estimated_cost_min"),
  estimatedCostMax: integer("estimated_cost_max"),
  estimatedHours: numericAsText("estimated_hours"),
  placement: text("placement"),
  tattooSize: text("tattoo_size"),
  complexity: text("complexity"),
  color: text("color"),
  estimateStatus: varchar("estimate_status", { length: 20 }),
  referenceImageUrl: varchar("reference_image_url", { length: 1000 }),
  referenceImageUrls: text("reference_image_urls").array(),
  afterPhotoUrls: text("after_photo_urls").array(),
  // migration-safety-ok (new nullable column — no backfill required)
  privateAfterPhotoUrls: text("private_after_photo_urls").array(),
  // migration-safety-ok (new nullable column — no backfill required)
  aiSketchUrls: text("ai_sketch_urls").array(),
  // migration-safety-ok (new nullable column — no backfill required; soft-booking mode)
  availabilityNotes: text("availability_notes"),
  rescheduleToken: varchar("reschedule_token", { length: 64 }),
  rescheduleReason: text("reschedule_reason"),
  artistConfirmed: varchar("artist_confirmed", { length: 20 }).notNull().default("pending"),
  artistConfirmedAt: timestamp("artist_confirmed_at"),
  depositStatus: varchar("deposit_status", { length: 20 }).notNull().default("none"),
  depositAmount: integer("deposit_amount"),
  depositPaidAt: timestamp("deposit_paid_at"),
  depositRequestedAt: timestamp("deposit_requested_at"),
  depositReceiptUrl: varchar("deposit_receipt_url", { length: 1000 }),
  depositNote: text("deposit_note"),
  showStatus: varchar("show_status", { length: 20 }).notNull().default("pending"),
  aftercareSentAt: timestamp("aftercare_sent_at"),
  npsToken: varchar("nps_token", { length: 64 }),
  npsSentAt: timestamp("nps_sent_at"),
  npsRespondedAt: timestamp("nps_responded_at"),
  reminderTwoWeekSentAt: timestamp("reminder_two_week_sent_at"),
  reminderOneWeekSentAt: timestamp("reminder_one_week_sent_at"),
  reminderManualSentAt: timestamp("reminder_manual_sent_at"),
  unconfirmedNagLastSentAt: timestamp("unconfirmed_nag_last_sent_at"),
  imminentAlertSentAt: timestamp("imminent_alert_sent_at"),
  actualCost: integer("actual_cost"),
  tipAmount: integer("tip_amount"),
  actualDurationMinutes: integer("actual_duration_minutes"),
  completedAt: timestamp("completed_at"),
  closeoutSentAt: timestamp("closeout_sent_at"),
  closeoutNagLastSentAt: timestamp("closeout_nag_last_sent_at"),
  closeoutNagCount: integer("closeout_nag_count").notNull().default(0),
  staleCloseout: boolean("stale_closeout").notNull().default(false),
  // migration-safety-ok — nullable boolean with DEFAULT false, safe to add to existing rows
  suspiciousCloseout: boolean("suspicious_closeout").notNull().default(false),
  // migration-safety-ok — nullable varchar, safe to add to existing rows
  suspiciousCloseoutReason: varchar("suspicious_closeout_reason", { length: 500 }),
  rebookingNudgeSentAt: timestamp("rebooking_nudge_sent_at"),
  teamupEventId: varchar("teamup_event_id", { length: 100 }),
  utmSource: varchar("utm_source", { length: 200 }),
  utmMedium: varchar("utm_medium", { length: 200 }),
  utmCampaign: varchar("utm_campaign", { length: 200 }),
  utmTerm: varchar("utm_term", { length: 200 }),
  utmContent: varchar("utm_content", { length: 200 }),
  referrer: varchar("referrer", { length: 1000 }),
  landingPage: varchar("landing_page", { length: 1000 }),
  deletedAt: timestamp("deleted_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  linkedSessionGroupId: varchar("linked_session_group_id", { length: 36 }),
  releaseFormToken: varchar("release_form_token", { length: 64 }),
  reengagementSentAt: timestamp("reengagement_sent_at"),
  googleReviewEmailSentAt: timestamp("google_review_email_sent_at"),
  suppressReengagement: boolean("suppress_reengagement").notNull().default(false),
  suppressDepositReminders: boolean("suppress_deposit_reminders").notNull().default(false),
  depositFollowUpSentAt: timestamp("deposit_follow_up_sent_at"),
  depositConfirmationSentAt: timestamp("deposit_confirmation_sent_at"),
  depositPendingEmailSentAt: timestamp("deposit_pending_email_sent_at"),
  depositReminderEmailSentAt: timestamp("deposit_reminder_email_sent_at"),
  depositExpiredEmailSentAt: timestamp("deposit_expired_email_sent_at"),
  healedPhotoToken: varchar("healed_photo_token", { length: 64 }),
  healedPhotoEmailSentAt: timestamp("healed_photo_email_sent_at"),
  editDetailsToken: varchar("edit_details_token", { length: 64 }),
  messageToken: varchar("message_token", { length: 64 }),
  bodyMapX: real("body_map_x"),
  bodyMapY: real("body_map_y"),
  bodyMapView: varchar("body_map_view", { length: 15 }),
  bodyMapRadius: real("body_map_radius"),
  bodyMapGender: varchar("body_map_gender", { length: 10 }),
  tattooSizeInches: varchar("tattoo_size_inches", { length: 50 }),
  placementNotes: varchar("placement_notes", { length: 200 }),
  proposedTimeSlot: varchar("proposed_time_slot", { length: 100 }),
  piercingType: varchar("piercing_type", { length: 100 }),
  jewelryMaterial: varchar("jewelry_material", { length: 100 }),
  jewelryGauge: varchar("jewelry_gauge", { length: 50 }),
  jewelryStyle: varchar("jewelry_style", { length: 100 }),
  piercingCount: integer("piercing_count"),
  freshInkSentAt: timestamp("fresh_ink_sent_at"),
  // Task #977 — Booking recovered from an abandoned draft via a /booking?resume=<token> email link.
  recoveredFromDraftToken: varchar("recovered_from_draft_token", { length: 64 }),
  recoveredFromDraftAt: timestamp("recovered_from_draft_at"),
  // Task #1435 — Walk-In Kiosk source tag. Values: "online" | "walk-in-kiosk" | "checkin-link" | "manual-entry"
  bookingSource: varchar("booking_source", { length: 50 }),
  // Task #1822 — Booking-flow smoke probe: marks synthetic probe bookings for immediate cleanup
  // migration-safety-ok: ADD COLUMN IF NOT EXISTS guard in scripts/pre-push-migrations.ts
  isProbe: boolean("is_probe").notNull().default(false),
  // Demo mode: marks bookings created when DEMO_MODE=true so they can be identified/cleaned up.
  // migration-safety-ok — nullable boolean with DEFAULT false, safe to add to existing rows
  isDemo: boolean("is_demo").notNull().default(false),
  // Task #2306 — Speed-to-lead / booking response time tracking.
  // first_admin_view_at: first time any admin/artist opens this booking's manage page.
  // first_response_at: first time a terminal action (confirm/decline/deposit-request) is taken.
  // booking_response_seconds: derived integer (first_response_at - createdAt) in seconds.
  // All nullable — legacy bookings that pre-date this feature keep NULL.
  // migration-safety-ok — nullable, no NOT NULL constraint, safe to add to existing rows
  firstAdminViewAt: timestamp("first_admin_view_at"),
  // migration-safety-ok — nullable, no NOT NULL constraint, safe to add to existing rows
  firstResponseAt: timestamp("first_response_at"),
  // migration-safety-ok — nullable, no NOT NULL constraint, safe to add to existing rows
  bookingResponseSeconds: integer("booking_response_seconds"),
  // Task #2505 — Structured tag for Flash Day / special-occasion bookings.
  // Stored as "flash:<label>" (e.g. "flash:July 4 Flash Day") when the client
  // arrives via ?occasion=flash&label=… so admins can filter by event.
  // migration-safety-ok — nullable, no NOT NULL constraint, safe to add to existing rows
  occasionTag: varchar("occasion_tag", { length: 200 }),
  // Task #2583 — Balance payment request flow.
  // balanceStatus: 'none' | 'requested' | 'paid' — tracks artist-initiated balance payment request.
  // balanceAmount: balance owed in dollars (actualCost - depositPaid), set when status becomes 'requested'.
  // balanceTipAmount: optional tip in dollars added by the client at checkout.
  // balanceRequestedAt: when the artist sent the balance payment request email.
  // balancePaidAt: when the webhook confirmed balance payment completed.
  // balanceReceiptUrl: URL to the payment receipt (populated by payment provider).
  // migration-safety-ok — all nullable, no NOT NULL constraint, safe to add to existing rows
  balanceStatus: varchar("balance_status", { length: 20 }).notNull().default("none"),
  // migration-safety-ok — nullable, no NOT NULL constraint, safe to add to existing rows
  balanceAmount: integer("balance_amount"),
  // migration-safety-ok — nullable, no NOT NULL constraint, safe to add to existing rows
  balanceTipAmount: integer("balance_tip_amount"),
  // migration-safety-ok — nullable, no NOT NULL constraint, safe to add to existing rows
  balanceRequestedAt: timestamp("balance_requested_at"),
  // migration-safety-ok — nullable, no NOT NULL constraint, safe to add to existing rows
  balancePaidAt: timestamp("balance_paid_at"),
  // migration-safety-ok — nullable, no NOT NULL constraint, safe to add to existing rows
  balanceReceiptUrl: varchar("balance_receipt_url", { length: 1000 }),
  // Task #2625 — Stale closeout artist weekly reminder.
  // Tracks the last time the assigned artist received a "still needs closeout" summary
  // for this booking. Null = never sent. Updated by markStaleCloseoutArtistNotifiedBatch().
  // The scheduler gate is staleCloseoutArtistLastNotifiedAt IS NULL OR < NOW() - 7 days.
  // migration-safety-ok — nullable, no NOT NULL constraint, safe to add to existing rows
  staleCloseoutArtistLastNotifiedAt: timestamp("stale_closeout_artist_last_notified_at"),
  // Task #2858 — Referral link sharing. Stores the clientPortalTokens.referralToken of
  // the referring client so the studio can attribute and reward referrals.
  // migration-safety-ok — nullable, no NOT NULL constraint, safe to add to existing rows
  referralSource: varchar("referral_source", { length: 64 }),
  // Digital release form signing via client portal (in-portal checkbox consent).
  // Separate from the email-link release_form_submissions flow — this is the simpler
  // in-portal "I agree" the client signs before arriving.
  // migration-safety-ok — nullable, no NOT NULL constraint, safe to add to existing rows
  releaseFormSignedAt: timestamp("release_form_signed_at"),
  // migration-safety-ok — nullable, safe to add to existing rows
  releaseFormIp: varchar("release_form_ip", { length: 100 }),
  // Task #2856 — Client portal tip/gratuity payment via Stripe PaymentIntent.
  // tipPaidAt: when the client completed the tip payment through the portal.
  // tipAmountCents: tip amount in cents (integer), stored separately from balanceTipAmount
  //   which tracks tips paid as part of the balance checkout flow.
  // Both nullable — only set when a portal tip has been paid.
  // migration-safety-ok — nullable, no NOT NULL constraint, safe to add to existing rows
  tipPaidAt: timestamp("tip_paid_at"),
  // migration-safety-ok — nullable, no NOT NULL constraint, safe to add to existing rows
  tipAmountCents: integer("tip_amount_cents"),
  // Task #2849 — Discount drops: which drop was applied and how many cents were saved.
  // migration-safety-ok — nullable, no NOT NULL constraint, safe to add to existing rows
  discountDropId: integer("discount_drop_id"),
  // migration-safety-ok — nullable, no NOT NULL constraint, safe to add to existing rows
  discountAppliedCents: integer("discount_applied_cents"),

  // Phase 1 Client Portal AI Features ─────────────────────────────────────────

  // D1 — Per-Piece Story Layer: client-editable story note for each completed piece.
  // Private by default; client can toggle visibility on their public portfolio.
  // migration-safety-ok — nullable, no NOT NULL constraint, safe to add to existing rows
  storyNote: text("story_note"),
  // migration-safety-ok — boolean with default TRUE; existing rows treated as private
  storyPrivate: boolean("story_private").notNull().default(true),

  // C2 — Personalized Aftercare Coach: tracks which day-N push notification has been sent.
  // Null = not yet sent. Set to timestamp when the notification fires.
  // migration-safety-ok — nullable timestamps, no NOT NULL constraint
  aftercareDay1SentAt: timestamp("aftercare_day1_sent_at"),
  // migration-safety-ok — nullable, no NOT NULL constraint
  aftercareDay4SentAt: timestamp("aftercare_day4_sent_at"),
  // migration-safety-ok — nullable, no NOT NULL constraint
  aftercareDay7SentAt: timestamp("aftercare_day7_sent_at"),
  // migration-safety-ok — nullable, no NOT NULL constraint
  aftercareDay14SentAt: timestamp("aftercare_day14_sent_at"),

  // D2 — Anniversary Notifications: year of the most recent anniversary notification sent.
  // Prevents duplicate sends across scheduler restarts. E.g. 2025 = "1-year anniv notified".
  // migration-safety-ok — nullable integer, no NOT NULL constraint
  anniversaryLastNotifiedYear: integer("anniversary_last_notified_year"),

}, (table) => [
  index("idx_booking_date_artist").on(table.date, table.artistSlug),
  index("idx_booking_artist_slug").on(table.artistSlug),
  index("idx_booking_reschedule_token").on(table.rescheduleToken),
  index("idx_booking_nps_token").on(table.npsToken),
  index("idx_booking_status").on(table.status),
  index("idx_booking_deposit_status").on(table.depositStatus),
  index("idx_booking_artist_confirmed").on(table.artistConfirmed),
  index("idx_booking_deleted_at").on(table.deletedAt),
  index("idx_booking_email").on(table.email),
  index("idx_booking_created_at").on(table.createdAt),
  index("idx_booking_linked_session_group").on(table.linkedSessionGroupId),
  uniqueIndex("idx_booking_reference_code").on(table.referenceCode),
  index("idx_booking_edit_details_token").on(table.editDetailsToken),
  uniqueIndex("idx_booking_message_token").on(table.messageToken),
  // Partial index covering only probe rows — makes DELETE WHERE is_probe=true instant
  // instead of a full table scan (20-25 s at production scale). Migration guard in
  // scripts/pre-push-migrations.ts.
  index("idx_booking_is_probe_active").on(table.isProbe).where(sql`is_probe = true`),
  // Partial index for efficient action-items suspicious closeout query.
  index("idx_booking_suspicious_closeout_active").on(table.suspiciousCloseout).where(sql`suspicious_closeout = true`),
  // Task #1903: expression index for case-insensitive email dedup in createBooking()
  index("idx_booking_email_lower").on(sql`lower(${table.email})`),
  // Task #1903: artist-leading composite index for slot-conflict check in createBooking()
  index("idx_booking_artist_date").on(table.artistSlug, table.date),
  // Task #2752: composite indexes for COUNT(*)/filter queries that were causing full table scans
  // (50–148 s observed runtimes) under pool pressure. artist_slug+status supports per-artist
  // booking-count aggregations; date+status supports date-range status filters in admin queries.
  index("idx_booking_artist_slug_status").on(table.artistSlug, table.status),
  index("idx_booking_date_status").on(table.date, table.status),
  // Task #1979 / INC-2026-05-25: idx_booking_email_lower_created_today intentionally
  // NOT defined here. Replit's deploy system uses dev-DB introspection (not schema.ts)
  // to generate DDL — schema.ts index definitions have no effect on what Replit sends to
  // production. Defining the index here AND having it in dev DB caused a double-write
  // scenario; having it only here had zero effect. The index is dropped from dev DB via
  // script/pre-push-migrations.ts to prevent Replit generating bad timestamp_ops DDL.
  // Functional index for getLatestBookingByEmail() — eliminates full-table scan on
  // lower(trim(email)) = $1 predicate used in client-portal booking lookup.
  index("idx_booking_appts_email_lower_trim").on(sql`lower(trim(${table.email}))`),
]);

export const insertBookingSchema = createInsertSchema(bookingAppointments).omit({ id: true, createdAt: true, status: true, referenceCode: true, rescheduleToken: true, npsToken: true, artistConfirmed: true, artistConfirmedAt: true, depositStatus: true, depositAmount: true, depositPaidAt: true, depositRequestedAt: true, depositReceiptUrl: true, showStatus: true, aftercareSentAt: true, npsSentAt: true, npsRespondedAt: true, reminderTwoWeekSentAt: true, reminderOneWeekSentAt: true, unconfirmedNagLastSentAt: true, imminentAlertSentAt: true, referenceImageUrl: true, referenceImageUrls: true, afterPhotoUrls: true, aiSketchUrls: true, teamupEventId: true, deletedAt: true, actualCost: true, actualDurationMinutes: true, completedAt: true, closeoutSentAt: true, closeoutNagLastSentAt: true, closeoutNagCount: true, staleCloseout: true, suspiciousCloseout: true, suspiciousCloseoutReason: true, rebookingNudgeSentAt: true, reengagementSentAt: true, healedPhotoToken: true, healedPhotoEmailSentAt: true, editDetailsToken: true, proposedTimeSlot: true, piercingType: true, jewelryMaterial: true, jewelryGauge: true, jewelryStyle: true, piercingCount: true, freshInkSentAt: true, recoveredFromDraftToken: true, recoveredFromDraftAt: true, isProbe: true, isDemo: true, depositPendingEmailSentAt: true, depositReminderEmailSentAt: true, depositExpiredEmailSentAt: true, balanceStatus: true, balanceAmount: true, balanceTipAmount: true, balanceRequestedAt: true, balancePaidAt: true, balanceReceiptUrl: true, tipPaidAt: true, tipAmountCents: true }).extend({
  name: z.string().min(1).max(200),
  email: z.string().email().max(320),
  phone: z.string().max(30).optional().nullable(),
  serviceType: z.string().min(1).max(100),
  description: z.string().max(5000).optional().nullable(),
  designStyle: z.string().max(200).optional().nullable(),
  placement: z.string().max(200).optional().nullable(),
  estimatedHours: z.coerce.number().min(0).max(9999.9).optional().nullable(),
  tattooSize: z.string().max(100).optional().nullable(),
  complexity: z.string().max(100).optional().nullable(),
  color: z.string().max(100).optional().nullable(),
  estimateStatus: z.enum(["completed"]).optional().nullable(),
  tattooSizeInches: z.string().max(50).optional().nullable(),
  placementNotes: z.string().max(200).optional().nullable(),
  piercingType: z.string().max(100).optional().nullable(),
  jewelryMaterial: z.string().max(100).optional().nullable(),
  jewelryGauge: z.string().max(50).optional().nullable(),
  jewelryStyle: z.string().max(100).optional().nullable(),
  piercingCount: z.coerce.number().int().min(1).max(20).optional().nullable(),
  bookingSource: z.string().max(50).optional().nullable(),
  date: z.string().optional().nullable(),
  time: z.string().optional().nullable(),
  availabilityNotes: z.string().max(2000).optional().nullable(),
});
export type InsertBooking = z.infer<typeof insertBookingSchema>;
export type Booking = typeof bookingAppointments.$inferSelect;

export const CLOSED_BOOKING_STATUSES = ["completed", "cancelled", "declined"] as const;
export type ClosedBookingStatus = typeof CLOSED_BOOKING_STATUSES[number];

export const piercingPricing = pgTable("piercing_pricing", {
  id: serial("id").primaryKey(),
  piercingType: varchar("piercing_type", { length: 100 }).notNull(),
  displayName: varchar("display_name", { length: 200 }).notNull(),
  priceMin: integer("price_min").notNull(),
  priceMax: integer("price_max").notNull(),
  jewelryUpcharge: integer("jewelry_upcharge").notNull().default(0),
  notes: text("notes"),
  sortOrder: integer("sort_order").notNull().default(0),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertPiercingPricingSchema = createInsertSchema(piercingPricing).omit({ id: true, createdAt: true }).extend({
  piercingType: z.string().min(1).max(100),
  displayName: z.string().min(1).max(200),
  priceMin: z.number().int().min(0),
  priceMax: z.number().int().min(0),
  jewelryUpcharge: z.number().int().min(0).optional(),
  notes: z.string().max(1000).optional().nullable(),
  sortOrder: z.number().int().optional(),
  isActive: z.boolean().optional(),
});
export const selectPiercingPricingSchema = createSelectSchema(piercingPricing);
export type InsertPiercingPricing = z.infer<typeof insertPiercingPricingSchema>;
export type PiercingPricing = typeof piercingPricing.$inferSelect;

export interface ContactGalleryImage {
  url: string;
  caption: string | null;
  uploadedAt: string;
}

export const importedContacts = pgTable("imported_contacts", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  email: text("email").notNull(),
  phone: text("phone"),
  notes: text("notes"),
  source: varchar("source", { length: 100 }).default("manual"),
  tags: text("tags").array().notNull().default([]),
  subscribedToList: boolean("subscribed_to_list").notNull().default(false),
  profilePhotoUrl: text("profile_photo_url"),
  imageGallery: jsonb("image_gallery").$type<ContactGalleryImage[]>().default([]).notNull(),
  importedAt: timestamp("imported_at").defaultNow().notNull(),
}, (table) => [
  index("idx_imported_contacts_email").on(table.email),
  index("idx_imported_contacts_source").on(table.source),
]);

export const insertImportedContactSchema = createInsertSchema(importedContacts).omit({ id: true, importedAt: true }).extend({
  name: z.string().min(1).max(200),
  email: z.string().email().max(320),
  phone: z.string().max(30).optional().nullable(),
  notes: z.string().max(2000).optional().nullable(),
  source: z.string().max(100).optional().nullable(),
  tags: z.array(z.string().max(50)).optional(),
  subscribedToList: z.boolean().optional(),
  profilePhotoUrl: z.string().nullable().optional(),
  imageGallery: z.array(z.object({
    url: z.string(),
    caption: z.string().nullable().optional(),
    uploadedAt: z.string(),
  })).optional(),
});
export type InsertImportedContact = z.infer<typeof insertImportedContactSchema>;
export type ImportedContact = typeof importedContacts.$inferSelect;

export const emailSubscribers = pgTable("email_subscribers", {
  id: serial("id").primaryKey(),
  // migration-safety-ok — original column, predates migration tracking
  email: text("email").notNull().unique(),
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  verificationToken: text("verification_token"),
  verificationTokenExpiresAt: timestamp("verification_token_expires_at"),
  verifiedAt: timestamp("verified_at"),
  unsubscribedAt: timestamp("unsubscribed_at"),
  optedInVia: varchar("opted_in_via", { length: 50 }).notNull().default("website"),
  utmSource: varchar("utm_source", { length: 200 }),
  utmMedium: varchar("utm_medium", { length: 200 }),
  utmCampaign: varchar("utm_campaign", { length: 200 }),
  utmTerm: varchar("utm_term", { length: 200 }),
  utmContent: varchar("utm_content", { length: 200 }),
  referrer: varchar("referrer", { length: 1000 }),
  landingPage: varchar("landing_page", { length: 1000 }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  subscribeEvents: boolean("subscribe_events").default(true),
  subscribeShopUpdates: boolean("subscribe_shop_updates").default(true),
  subscribeArtistUpdates: boolean("subscribe_artist_updates").default(false),
  subscribedArtistSlugs: text("subscribed_artist_slugs").array(),
  subscribeFreshInk: boolean("subscribe_fresh_ink").default(false),
  manageToken: varchar("manage_token", { length: 64 }).unique(),
  pausedUntil: timestamp("paused_until"),
  prefNewsletter: boolean("pref_newsletter").default(true),
  prefAftercareAndEvents: boolean("pref_aftercare_and_events").default(true),
  prefFlashAndArtists: boolean("pref_flash_and_artists").default(true),
  prefFreshInk: boolean("pref_fresh_ink").default(true),
  prefBookingReminders: boolean("pref_booking_reminders").default(true),
}, (table) => [
  index("idx_subscriber_status").on(table.status),
  index("idx_subscriber_created_at").on(table.createdAt),
  index("idx_subscriber_verification_token").on(table.verificationToken),
  index("idx_subscriber_manage_token").on(table.manageToken),
]);

export const insertEmailSubscriberSchema = createInsertSchema(emailSubscribers).omit({ id: true, createdAt: true, verifiedAt: true, unsubscribedAt: true, verificationTokenExpiresAt: true }).extend({
  email: z.string().email().max(320),
  status: z.enum(["pending", "verified", "unsubscribed"]).optional(),
  verificationToken: z.string().optional().nullable(),
  optedInVia: z.enum(["website", "booking", "contact", "artist-follow"]).optional(),
  utmSource: z.string().max(200).optional().nullable(),
  utmMedium: z.string().max(200).optional().nullable(),
  utmCampaign: z.string().max(200).optional().nullable(),
  utmTerm: z.string().max(200).optional().nullable(),
  utmContent: z.string().max(200).optional().nullable(),
  referrer: z.string().max(1000).optional().nullable(),
  landingPage: z.string().max(1000).optional().nullable(),
  subscribeEvents: z.boolean().optional().nullable(),
  subscribeShopUpdates: z.boolean().optional().nullable(),
  subscribeArtistUpdates: z.boolean().optional().nullable(),
  subscribedArtistSlugs: z.array(z.string()).optional().nullable(),
  subscribeFreshInk: z.boolean().optional().nullable(),
});
export type InsertEmailSubscriber = z.infer<typeof insertEmailSubscriberSchema>;
export type EmailSubscriber = typeof emailSubscribers.$inferSelect;

export const emailSends = pgTable("email_sends", {
  id: serial("id").primaryKey(),
  // migration-safety-ok — original column, predates migration tracking
  subscriberEmail: text("subscriber_email").notNull(),
  recipientName: text("recipient_name"),
  // migration-safety-ok — original column, predates migration tracking
  templateId: text("template_id").notNull(),
  campaignId: integer("campaign_id"),
  status: text("status").notNull().default("sent"),
  error: text("error"),
  sentAt: timestamp("sent_at").defaultNow().notNull(),
  resendMessageId: varchar("resend_message_id", { length: 100 }),
  deliveredAt: timestamp("delivered_at"),
  openedAt: timestamp("opened_at"),
  clickedAt: timestamp("clicked_at"),
  // Task #1670 — Resend delivery visibility (bounces, suppressions, complaints, failures)
  // migration-safety-ok: all columns are nullable; ADD COLUMN IF NOT EXISTS guards in pre-push-migrations.ts
  bouncedAt: timestamp("bounced_at"),
  bounceType: text("bounce_type"),
  bounceSubtype: text("bounce_subtype"),
  bounceMessage: text("bounce_message"),
  complainedAt: timestamp("complained_at"),
  suppressedAt: timestamp("suppressed_at"),
  suppressionType: text("suppression_type"),
  suppressionMsg: text("suppression_msg"),
  failedAt: timestamp("failed_at"),
  failedMessage: text("failed_message"),
}, (table) => [
  index("email_sends_subscriber_idx").on(table.subscriberEmail),
  index("email_sends_template_idx").on(table.templateId),
  index("email_sends_campaign_idx").on(table.campaignId),
  index("email_sends_resend_msg_idx").on(table.resendMessageId),
  index("email_sends_sent_at_idx").on(table.sentAt),
  index("email_sends_sub_tmpl_status_idx").on(table.subscriberEmail, table.templateId, table.status),
  // Partial index for booking-notification email lookups.
  // Without this, queries filtering by template_id LIKE 'booking-notify-%' full-scan the table.
  // Must live in schema.ts — not just pre-push-migrations.ts — so Drizzle pushes it to production
  // (see INC-2026-05-25 and INC-2026-05-27-schema-only-partial-index-missing).
  index("idx_email_sends_booking_notify")
    .on(table.templateId)
    .where(sql`template_id LIKE 'booking-notify-%'`),
  // Partial index for contact-notification email lookups (INC-2026-05-29).
  // Without this, getContactsWithoutNotification() does a full email_sends scan
  // on every email-queue-batch run (observed 15–78 s under pool pressure).
  // Must live in schema.ts so Drizzle pushes it to production (see INC-2026-05-25).
  index("idx_email_sends_contact_notify")
    .on(table.templateId)
    .where(sql`template_id LIKE 'contact-notify-%'`),
  // Partial index for subscriber-notification email lookups (INC-2026-05-31).
  // getSubscribersWithoutNotification() previously used LEFT JOIN with
  // template_id = 'subscriber-notify-' || es.email — a computed string that
  // prevented index use. Rewritten to NOT EXISTS backed by this index.
  index("idx_email_sends_subscriber_notify")
    .on(table.templateId)
    .where(sql`template_id LIKE 'subscriber-notify-%'`),
]);

export type EmailSend = typeof emailSends.$inferSelect;

export const emailCampaigns = pgTable("email_campaigns", {
  id: serial("id").primaryKey(),
  // migration-safety-ok — original column, predates migration tracking
  subject: text("subject").notNull(),
  // migration-safety-ok — original column, predates migration tracking
  html: text("html").notNull(),
  bodyText: text("body_text"),
  ctaLabel: varchar("cta_label", { length: 200 }),
  ctaUrl: text("cta_url"),
  status: text("status").notNull().default("draft"),
  campaignType: varchar("campaign_type", { length: 20 }).notNull().default("studio"),
  category: varchar("category", { length: 30 }),
  artistSlug: varchar("artist_slug", { length: 100 }),
  utmCampaign: varchar("utm_campaign", { length: 200 }),
  totalRecipients: integer("total_recipients").default(0),
  sentCount: integer("sent_count").default(0),
  failedCount: integer("failed_count").default(0),
  approvalToken: varchar("approval_token", { length: 64 }),
  approvedAt: timestamp("approved_at"),
  scheduledFor: timestamp("scheduled_for"),
  testSentAt: timestamp("test_sent_at"),
  testSentTo: text("test_sent_to"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
}, (table) => [
  index("idx_email_campaigns_status").on(table.status),
  index("idx_email_campaigns_approved_status").on(table.status, table.approvedAt),
]);

export type EmailCampaign = typeof emailCampaigns.$inferSelect;

export const campaignLinkClicks = pgTable("campaign_link_clicks", {
  id: serial("id").primaryKey(),
  campaignId: integer("campaign_id").notNull(),
  recipientEmail: text("recipient_email").notNull(),
  clickToken: varchar("click_token", { length: 64 }).notNull(),
  destinationUrl: text("destination_url").notNull(),
  clickedAt: timestamp("clicked_at").defaultNow().notNull(),
}, (table) => [
  index("idx_campaign_link_clicks_campaign").on(table.campaignId),
  index("idx_campaign_link_clicks_token").on(table.clickToken),
]);

export type CampaignLinkClick = typeof campaignLinkClicks.$inferSelect;

export const siteConfigSchema = z.object({
  heroOpacity: z.number().min(0).max(100).default(35),
  heroTextShadow: z.number().min(0).max(100).default(20),
  italicText: z.boolean().default(true),
  tattooItalic: z.boolean().default(false),
  tileSize: z.number().min(50).max(1000).default(400),
  tileOffsetX: z.number().min(-500).max(500).default(0),
  tileOffsetY: z.number().min(-500).max(500).default(0),
  artistCardScale: z.number().min(50).max(150).default(100).optional(),
  artistCardRadius: z.number().min(0).max(50).default(0).optional(),
  artistCardBorderWidth: z.number().min(0).max(20).default(0).optional(),
  artistCardBorderColor: z.string().default("#ffffff").optional(),
  artistCardFramePadding: z.number().min(0).max(40).default(0).optional(),
  artistCardFrameColor: z.string().default("#000000").optional(),
  magikGlowColor1: z.string().default("#8b5cf6").optional(),
  magikGlowColor2: z.string().default("#ec4899").optional(),
  magikGlowIntensity: z.number().min(0).max(100).default(20).optional(),
  magikGlowSpeed: z.number().min(1).max(20).default(4).optional(),
  textBgOpacity: z.number().min(0).max(100).default(70).optional(),
  heroCardBgOpacity: z.number().min(0).max(100).default(97).optional(),
  heroVideoOpacity: z.number().min(0).max(100).default(18).optional(),
  heroWispsEnabled: z.boolean().default(true).optional(),
  artistCardVignetteOpacity: z.number().min(0).max(100).default(0).optional(),
  artistCardVignetteCenter: z.number().min(0).max(100).default(50).optional(),
  artistCardPositions: z.record(z.string(), z.object({ x: z.number(), y: z.number() })).default({}).optional(),
  artistProfileImages: z.record(z.string(), z.string()).default({}).optional(),
  artistProfileZoom: z.record(z.string(), z.number()).default({}).optional(),
  artistHeroOrder: z.record(z.string(), z.array(z.string())).default({}).optional(),
  aboutHeroImages: z.array(z.string()).default([]).optional(),
  aboutStudioImages: z.array(z.object({ src: z.string(), alt: z.string() })).default([]).optional(),
});

export type SiteConfig = z.infer<typeof siteConfigSchema>;

export const artistAvailability = pgTable("artist_availability", {
  id: serial("id").primaryKey(),
  artistSlug: varchar("artist_slug", { length: 100 }).notNull().unique(),
  status: varchar("status", { length: 20 }).notNull().default("available"),
  nextAvailableDate: date("next_available_date"),
  minWeeksOut: integer("min_weeks_out").notNull().default(1),
  note: text("note"),
  teamupSubcalendarId: varchar("teamup_subcalendar_id", { length: 50 }),
  teamupSyncUnconfirmed: boolean("teamup_sync_unconfirmed").notNull().default(false),
  recurringBlockedDays: integer("recurring_blocked_days").array(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const artistBlockedDates = pgTable("artist_blocked_dates", {
  id: serial("id").primaryKey(),
  artistSlug: varchar("artist_slug", { length: 100 }).notNull(),
  blockedDate: date("blocked_date").notNull(),
  reason: text("reason"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_blocked_artist_date").on(table.artistSlug, table.blockedDate),
  // The booking calendar query: WHERE blocked_date >= $1 (no artist slug prefix —
  // the composite above cannot be used for date-range scans without the leading column).
  index("idx_blocked_dates_date").on(table.blockedDate),
]);

export type ArtistBlockedDate = typeof artistBlockedDates.$inferSelect;

export const availabilityTokens = pgTable("availability_tokens", {
  id: serial("id").primaryKey(),
  token: varchar("token", { length: 64 }).notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_avail_token").on(table.token),
]);

export type AvailabilityToken = typeof availabilityTokens.$inferSelect;

export const pushSubscriptions = pgTable("push_subscriptions", {
  id: serial("id").primaryKey(),
  endpoint: text("endpoint").notNull().unique(),
  p256dh: text("p256dh").notNull(),
  auth: text("auth").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Null = studio-wide subscriber (flash sales etc); set = artist-scoped subscriber
  artistSlug: varchar("artist_slug", { length: 100 }),
});

// Client portal push notification opt-in (Task #2737)
export const clientPushSubscriptions = pgTable("client_push_subscriptions", {
  id: serial("id").primaryKey(),
  email: text("email").notNull(),
  endpoint: text("endpoint").notNull().unique(),
  p256dh: text("p256dh").notNull(),
  auth: text("auth").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_client_push_sub_email").on(table.email),
]);

export const priceEstimates = pgTable("price_estimates", {
  id: serial("id").primaryKey(),
  source: varchar("source", { length: 20 }).notNull(),
  artistSlug: varchar("artist_slug", { length: 100 }),
  size: varchar("size", { length: 50 }),
  complexity: varchar("complexity", { length: 50 }),
  placement: varchar("placement", { length: 50 }),
  color: varchar("color", { length: 50 }),
  estimateLow: integer("estimate_low"),
  estimateHigh: integer("estimate_high"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const npsResponses = pgTable("nps_responses", {
  id: serial("id").primaryKey(),
  bookingId: integer("booking_id").notNull().references(() => bookingAppointments.id, { onDelete: "cascade" }),
  score: integer("score").notNull(),
  comment: text("comment"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_nps_booking").on(table.bookingId),
  index("idx_nps_created").on(table.createdAt),
]);

export const insertNpsResponseSchema = createInsertSchema(npsResponses).omit({ id: true, createdAt: true }).extend({
  score: z.number().int().min(1).max(5),
  comment: z.string().max(2000).optional().nullable(),
});
export type InsertNpsResponse = z.infer<typeof insertNpsResponseSchema>;
export type NpsResponse = typeof npsResponses.$inferSelect;

// Date the NPS rating scale was changed from 0–10 to 1–5.
// All legacy 0–10 scale responses have been remapped to 1–5 via the
// pre-push migration. The reviews dashboard uses this cutoff to exclude
// pre-scale-change responses from default averages and NPS buckets.
// Admins can opt in to the pre-cutoff data with ?includeLegacy=true.
export const NPS_SCALE_CHANGE_DATE = new Date("2026-05-02T00:00:00.000Z");

export const adminActionTokens = pgTable("admin_action_tokens", {
  id: serial("id").primaryKey(),
  bookingId: integer("booking_id").notNull(),
  action: varchar("action", { length: 30 }).notNull(),
  token: varchar("token", { length: 64 }).notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  usedAt: timestamp("used_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Role + displayName scope who generated this token and their display name.
  // Values: "artist" | "receptionist" | "manager" | "owner"
  // When null, treat as "artist" for backwards compatibility.
  role: varchar("role", { length: 30 }),
  displayName: text("display_name"),
}, (table) => [
  index("idx_admin_token").on(table.token),
  index("idx_admin_token_booking").on(table.bookingId),
  index("idx_admin_action_tokens_booking_action_expires").on(table.bookingId, table.action, table.expiresAt),
]);

export type AdminActionToken = typeof adminActionTokens.$inferSelect;

// ── Booking Notes (internal staff notes, never visible to clients) ─────────────
export const bookingNotes = pgTable("booking_notes", {
  id: serial("id").primaryKey(),
  bookingId: integer("booking_id").notNull(),
  body: text("body").notNull(),
  authorName: varchar("author_name", { length: 100 }).notNull(),
  authorRole: varchar("author_role", { length: 30 }).notNull().default("admin"),

  editedAt: timestamp("edited_at"),
  deletedAt: timestamp("deleted_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});
export type BookingNote = typeof bookingNotes.$inferSelect;
export const insertBookingNoteSchema = createInsertSchema(bookingNotes).omit({ id: true, createdAt: true, editedAt: true, deletedAt: true });
export type InsertBookingNote = z.infer<typeof insertBookingNoteSchema>;

export const tattooWorks = pgTable("tattoo_works", {
  id: serial("id").primaryKey(),
  bookingId: integer("booking_id"),
  artistSlug: varchar("artist_slug", { length: 100 }).notNull(),
  imagePath: text("image_path").notNull(),
  thumbnailPath: text("thumbnail_path").notNull(),
  altText: varchar("alt_text", { length: 300 }).notNull(),
  clientName: varchar("client_name", { length: 200 }),
  featured: integer("featured").notNull().default(1),
  isNsfw: boolean("is_nsfw").notNull().default(false),
  isPrivate: boolean("is_private").notNull().default(false),
  rotationDegrees: integer("rotation_degrees").notNull().default(0),
  cropData: text("crop_data"),
  instagramPostId: varchar("instagram_post_id", { length: 100 }),
  instagramPublishedAt: timestamp("instagram_published_at"),
  // migration-safety-ok: brand-new nullable column; no DEFAULT needed
  styleTags: text("style_tags").array(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_tattoo_works_artist").on(table.artistSlug),
  index("idx_tattoo_works_booking").on(table.bookingId),
  index("idx_tattoo_works_created").on(table.createdAt),
]);

export const insertTattooWorkSchema = createInsertSchema(tattooWorks).omit({ id: true, createdAt: true });
export type InsertTattooWork = z.infer<typeof insertTattooWorkSchema>;
export type TattooWork = typeof tattooWorks.$inferSelect;

export const healedPhotoSubmissions = pgTable("healed_photo_submissions", {
  id: serial("id").primaryKey(),
  bookingId: integer("booking_id").notNull().references(() => bookingAppointments.id, { onDelete: "cascade" }),
  photoUrl: text("photo_url").notNull(),
  thumbnailUrl: text("thumbnail_url"),
  clientNote: text("client_note"),
  submittedAt: timestamp("submitted_at").defaultNow().notNull(),
  adminNote: text("admin_note"),
  adminRepliedAt: timestamp("admin_replied_at"),
}, (table) => [
  index("idx_healed_photo_submissions_booking").on(table.bookingId),
]);
export type HealedPhotoSubmission = typeof healedPhotoSubmissions.$inferSelect;

export const blockedEmails = pgTable("blocked_emails", {
  id: serial("id").primaryKey(),
  email: varchar("email", { length: 255 }).notNull().unique(),
  reason: text("reason"),
  blockedAt: timestamp("blocked_at").defaultNow().notNull(),
  unblockedAt: timestamp("unblocked_at"),
}, (table) => [
  index("idx_blocked_emails_email").on(table.email),
]);

export const insertBlockedEmailSchema = createInsertSchema(blockedEmails).omit({ id: true, blockedAt: true, unblockedAt: true });
export type InsertBlockedEmail = z.infer<typeof insertBlockedEmailSchema>;
export type BlockedEmail = typeof blockedEmails.$inferSelect;

export interface DigestThemeConfig {
  accentColor: string;
  accentText: string;
  accentDeep: string;
  bodyBg: string;
  wrapperBg: string;
  headerBg: string;
  headerStyle: "gradient" | "solid" | "minimal";
  headerText: string;
  metaBg: string;
  border: string;
  borderInner: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  fontFamily: "serif" | "sans";
  density: "compact" | "normal" | "spacious";
}

export const digestThemes = pgTable("digest_themes", {
  id: serial("id").primaryKey(),
  name: varchar("name", { length: 100 }).notNull(),
  isBuiltIn: boolean("is_built_in").default(false).notNull(),
  isActive: boolean("is_active").default(false).notNull(),
  config: jsonb("config").$type<DigestThemeConfig>().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertDigestThemeSchema = createInsertSchema(digestThemes).omit({ id: true, createdAt: true });
export type InsertDigestTheme = z.infer<typeof insertDigestThemeSchema>;
export type DigestTheme = typeof digestThemes.$inferSelect;

export const digestRecipients = pgTable("digest_recipients", {
  id: serial("id").primaryKey(),
  email: varchar("email", { length: 255 }).notNull().unique(),
  name: text("name").notNull(),
  role: varchar("role", { length: 20 }).notNull().default("viewer"),
  active: boolean("active").notNull().default(true),
  sectionsVisible: jsonb("sections_visible").notNull().default({}),
  themeId: integer("theme_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_digest_recipients_email").on(table.email),
]);

export const insertDigestRecipientSchema = createInsertSchema(digestRecipients).omit({ id: true, createdAt: true, updatedAt: true }).extend({
  email: z.string().email().max(255),
  name: z.string().min(1).max(200),
  role: z.enum(["admin", "viewer"]),
  sectionsVisible: z.record(z.boolean()).optional(),
  themeId: z.number().int().nullable().optional(),
});
export type InsertDigestRecipient = z.infer<typeof insertDigestRecipientSchema>;
export type DigestRecipient = typeof digestRecipients.$inferSelect;

export const DIGEST_TOKEN_SCOPES = ["digest", "artist", "event", "estimate", "case-study", "flash", "studio", "netglowup", "controls", "sales"] as const;
export type DigestTokenScope = (typeof DIGEST_TOKEN_SCOPES)[number];

export const digestSettingsTokens = pgTable("digest_settings_tokens", {
  id: serial("id").primaryKey(),
  token: varchar("token", { length: 64 }).notNull(),
  scope: varchar("scope", { length: 32 }).default("digest").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_digest_settings_token").on(table.token),
]);

export type DigestSettingsToken = typeof digestSettingsTokens.$inferSelect;

export const studioConfig = pgTable("studio_config", {
  id: serial("id").primaryKey(),
  // migration-safety-ok — original column, predates migration tracking
  name: text("name").notNull(),
  // migration-safety-ok — original column, predates migration tracking
  shortName: text("short_name").notNull(),
  brandSuffix: text("brand_suffix").default("Tattoo"),
  slogan: text("slogan"),
  heroTagline: text("hero_tagline"),
  description: text("description"),
  established: integer("established"),
  // migration-safety-ok — original column, predates migration tracking
  address: text("address").notNull(),
  // migration-safety-ok — original column, predates migration tracking
  city: text("city").notNull(),
  // migration-safety-ok — original column, predates migration tracking
  state: text("state").notNull(),
  // migration-safety-ok — original column, predates migration tracking
  zip: text("zip").notNull(),
  // migration-safety-ok — original column, predates migration tracking
  phone: text("phone").notNull(),
  // migration-safety-ok — original column, predates migration tracking
  contactEmail: text("contact_email").notNull(),
  siteUrl: text("site_url"),
  heroImage: text("hero_image"),
  heroBlackBg: boolean("hero_black_bg").default(false),
  logoUrl: text("logo_url"),
  logoBlackBg: boolean("logo_black_bg").default(true),
  footerImageUrl: text("footer_image_url"),
  footerBlackBg: boolean("footer_black_bg").default(false),
  footerImageAdjustments: jsonb("footer_image_adjustments"),
  heroImageAdjustments: jsonb("hero_image_adjustments_studio"),
  teamPhotoUrl: text("team_photo_url"),
  teamBlackBg: boolean("team_black_bg").default(false),
  teamPhotoAdjustments: jsonb("team_photo_adjustments"),
  hours: text("hours"),
  hoursNote: text("hours_note"),
  instagramUrl: text("instagram_url"),
  facebookUrl: text("facebook_url"),
  externalGalleryUrl: text("external_gallery_url"),
  lat: text("lat"),
  lng: text("lng"),
  mapUrl: text("map_url"),
  defaultDeposit: integer("default_deposit").default(50),
  // migration-safety-ok: nullable integer — minimum deposit floor (dollars) for dynamic deposit calculation; null = no floor
  depositMinimumAmount: integer("deposit_minimum_amount"),
  // migration-safety-ok: nullable integer — percentage of estimated cost to charge as deposit (0–100); null = use flat defaultDeposit
  depositPercentage: integer("deposit_percentage"),
  emailFromAddress: text("email_from_address"),
  privacyPolicyUrl: text("privacy_policy_url"),
  shopUrl: text("shop_url"),
  timezone: text("timezone").default("America/Detroit"),
  googleReviewUrl: text("google_review_url"),
  calendarForwardEmail: text("calendar_forward_email"),
  calendarForwardEmails: text("calendar_forward_emails").array(),
  icalSecret: text("ical_secret"),
  teamupApiKey: text("teamup_api_key"),
  teamupCalendarKey: text("teamup_calendar_key"),
  paymentProvider: text("payment_provider"),
  paypalClientId: text("paypal_client_id"),
  paypalClientSecret: text("paypal_client_secret"),
  paypalSandbox: boolean("paypal_sandbox").default(false),
  // migration-safety-ok: nullable text column — no backfill required; existing rows get NULL
  paypalWebhookId: text("paypal_webhook_id"),
  stripeSecretKey: text("stripe_secret_key"),
  stripePublishableKey: text("stripe_publishable_key"),
  stripeWebhookSecret: text("stripe_webhook_secret"),
  paymentLink: text("payment_link"),
  onboardingComplete: boolean("onboarding_complete").default(false),
  alertRecipientEmails: text("alert_recipient_emails"),
  disabledFeatures: text("disabled_features"),
  bookingSlotStartHour: integer("booking_slot_start_hour").default(12),
  bookingSlotEndHour: integer("booking_slot_end_hour").default(18),
  bookingSlotIntervalMinutes: integer("booking_slot_interval_minutes").default(30),
  digestUpcomingLimit: integer("digest_upcoming_limit").default(5),
  digestFollowupLimit: integer("digest_followup_limit").default(5),
  digestMissingCloseoutLimit: integer("digest_missing_closeout_limit").default(5),
  digestPendingCloseoutLimit: integer("digest_pending_closeout_limit").default(5),
  digestDepositOverdueLimit: integer("digest_deposit_overdue_limit").default(5),
  digestUnansweredLimit: integer("digest_unanswered_limit").default(5),
  digestCancelledLimit: integer("digest_cancelled_limit").default(5),
  digestStaleCloseoutLimit: integer("digest_stale_closeout_limit").default(5),
  digestBlockedEmailsLimit: integer("digestBlockedEmailsLimit").default(5),
  systemStartDate: timestamp("system_start_date"),
  notifyAdminOnBookings: boolean("notify_admin_on_bookings").default(true),
  adminBccEnabled: boolean("admin_bcc_enabled").notNull().default(true),
  closeoutEmailsEnabled: boolean("closeout_emails_enabled").default(true),
  instagramAccessToken: text("instagram_access_token"),
  instagramUserId: varchar("instagram_user_id", { length: 50 }),
  instagramTokenExpiresAt: timestamp("instagram_token_expires_at"),
  ownerSurveyToken: varchar("owner_survey_token", { length: 64 }),
  monthlySoftwareCost: numeric("monthly_software_cost", { precision: 10, scale: 2 }),
  billingAnchorDay: integer("billing_anchor_day").default(1),
  billingAnchorMonth: integer("billing_anchor_month").default(1),
  billingCycle: varchar("billing_cycle", { length: 20 }).default("monthly"),
  lightroomShowcases: jsonb("lightroom_showcases").$type<Array<{ name: string; embedUrl: string; externalUrl?: string }>>(),
  heroVideoUrl: text("hero_video_url"),
  heroVideoLoopEnabled: boolean("hero_video_loop_enabled").default(true),
  heroVideoLoopStart: real("hero_video_loop_start").default(2.0),
  heroVideoLoopEnd: real("hero_video_loop_end"),
  heroVideoPlaylist: jsonb("hero_video_playlist").$type<Array<{ url: string; loopStart: number; loopEnd: number | null }>>(),
  cancellationWindowHours: integer("cancellation_window_hours").default(48),
  // migration-safety-ok: nullable integer — quiet-hours start (0–23) for admin deposit alerts; default 21 (9 PM)
  alertQuietHoursStart: integer("alert_quiet_hours_start").default(21),
  // migration-safety-ok: nullable integer — quiet-hours end (0–23) for admin deposit alerts; default 8 (8 AM)
  alertQuietHoursEnd: integer("alert_quiet_hours_end").default(8),
  // migration-safety-ok: nullable column, expand-only — no backfill required
  lastSiteUrlAlertSentAt: timestamp("last_site_url_alert_sent_at"),
  // migration-safety-ok: nullable — set when studio upgrades to a paid NGU tier to suppress PLG prompts
  plgUpgradedAt: timestamp("plg_upgraded_at"),
  // migration-safety-ok: nullable text — GA4 Property ID for the Google Analytics integration (e.g. "properties/123456789")
  ga4PropertyId: text("ga4_property_id"),
  // DM auto-responder config (Task #2447)
  instagramDmAutoresponseEnabled: boolean("instagram_dm_autoresponse_enabled").default(false),
  instagramDmBookingReply: text("instagram_dm_booking_reply"),
  instagramDmGeneralReply: text("instagram_dm_general_reply"),
  // migration-safety-ok: nullable text — Google Places ID for GMB profile monitoring (e.g. "ChIJ...")
  googlePlaceId: text("google_place_id"),
  // migration-safety-ok: nullable text — parking/arrival notes shown in the client portal prep card
  parkingNotes: text("parking_notes"),
  // migration-safety-ok: nullable text — custom referral program blurb shown in client portal; falls back to generic default
  referralDescription: text("referral_description"),
  // migration-safety-ok: nullable — soft-booking availability picker config; defaults applied at runtime when null
  softBookingDays: text("soft_booking_days").array(),
  // migration-safety-ok: nullable — soft-booking availability time slot config; defaults applied at runtime when null
  softBookingTimeSlots: text("soft_booking_time_slots").array(),
  // migration-safety-ok — nullable text; custom background tile image URL; falls back to bundled ceiling-texture.webp when null
  backgroundTileUrl: text("background_tile_url"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// ── Agency OS — per-deployment vertical configuration ─────────────────────────
// One row per deployment. If no row exists, API returns the default NGU profile.
// Use `node script/onboard-agency.cjs --vertical <slug>` to seed this row.
export const agencyConfig = pgTable("agency_config", {
  id: serial("id").primaryKey(),
  verticalSlug: varchar("vertical_slug", { length: 50 }).notNull().default("tattoo_studio"),
  agencyName: text("agency_name").notNull().default("NetGlowUp"),
  agencyTagline: text("agency_tagline").notNull().default("Agency Admin"),
  verticalProfile: jsonb("vertical_profile").$type<Record<string, unknown>>(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
export type AgencyConfig = typeof agencyConfig.$inferSelect;

export const flashDesigns = pgTable("flash_designs", {
  id: serial("id").primaryKey(),
  // migration-safety-ok — original column, predates migration tracking
  title: text("title").notNull(),
  description: text("description"),
  // migration-safety-ok — original column, predates migration tracking
  imageUrl: text("image_url").notNull(),
  thumbnailUrl: text("thumbnail_url"),
  artistSlug: varchar("artist_slug", { length: 100 }),
  price: integer("price"),
  tags: text("tags").array().notNull().default([]),
  status: varchar("status", { length: 20 }).notNull().default("available"),
  sortOrder: integer("sort_order").notNull().default(0),
  expiresAt: timestamp("expires_at"),
  notifiedAt: timestamp("notified_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_flash_designs_status").on(table.status),
  index("idx_flash_designs_artist").on(table.artistSlug),
  index("idx_flash_designs_sort").on(table.sortOrder),
]);

export const insertFlashDesignSchema = createInsertSchema(flashDesigns).omit({ id: true, createdAt: true, updatedAt: true }).extend({
  title: z.string().min(1).max(200),
  description: z.string().max(2000).optional().nullable(),
  imageUrl: z.string().min(1).max(1000),
  thumbnailUrl: z.string().max(1000).optional().nullable(),
  artistSlug: z.string().max(100).optional().nullable(),
  price: z.number().int().min(0).optional().nullable(),
  tags: z.array(z.string()).optional().default([]),
  status: z.enum(["available", "claimed"]).optional().default("available"),
  sortOrder: z.number().int().optional().default(0),
  expiresAt: z.coerce.date().optional().nullable(),
});
export type InsertFlashDesign = z.infer<typeof insertFlashDesignSchema>;
export type FlashDesign = typeof flashDesigns.$inferSelect;

export const flashNotificationQueue = pgTable("flash_notification_queue", {
  id: serial("id").primaryKey(),
  // migration-safety-ok — original column, predates migration tracking
  artistSlug: varchar("artist_slug", { length: 100 }).notNull(),
  // migration-safety-ok — original column, predates migration tracking
  scheduledSendAt: timestamp("scheduled_send_at").notNull(),
  flashDesignIds: integer("flash_design_ids").array().notNull().default([]),
  // Allowed values: 'pending' (queued), 'processing' (claimed by send-now, in-flight),
  // 'retry' (backoff after partial failure), 'sent' (delivered), 'failed' (permanent).
  // Task #1233: 'processing' is set atomically by the send-now route and resolved by
  // sendFlashNotifications() to 'sent' on success or rolled back to 'pending' on failure.
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  sentAt: timestamp("sent_at"),
  retryCount: integer("retry_count").notNull().default(0),
  retryAfter: timestamp("retry_after"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_flash_notif_queue_artist").on(table.artistSlug),
  index("idx_flash_notif_queue_status").on(table.status),
  index("idx_flash_notif_queue_scheduled").on(table.scheduledSendAt),
]);

export type FlashNotificationQueue = typeof flashNotificationQueue.$inferSelect;

export const walkinTokens = pgTable("walkin_tokens", {
  id: serial("id").primaryKey(),
  token: varchar("token", { length: 64 }).notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  type: varchar("type", { length: 20 }).notNull().default("daily"),
  clientEmail: text("client_email"),
  clientName: text("client_name"),
}, (table) => [
  index("idx_walkin_token").on(table.token),
]);

export type WalkinToken = typeof walkinTokens.$inferSelect;

export const walkInRequests = pgTable("walk_in_requests", {
  id: serial("id").primaryKey(),
  name: varchar("name", { length: 200 }).notNull(),
  email: varchar("email", { length: 320 }).notNull(),
  phone: varchar("phone", { length: 30 }),
  serviceType: varchar("service_type", { length: 100 }).notNull(),
  description: text("description"),
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  approveToken: varchar("approve_token", { length: 64 }).notNull(),
  declineToken: varchar("decline_token", { length: 64 }).notNull(),
  bookingId: integer("booking_id"),
  reviewedAt: timestamp("reviewed_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_walkin_request_approve").on(table.approveToken),
  index("idx_walkin_request_decline").on(table.declineToken),
  // Index for admin list queries ordered/filtered by created_at.
  // Was only in pre-push-migrations.ts — added here so Drizzle pushes it to production
  // (INC-2026-05-27-schema-only-partial-index-missing).
  index("idx_walkin_request_created_at").on(table.createdAt),
]);

export type WalkInRequest = typeof walkInRequests.$inferSelect;
export type InsertWalkInRequest = typeof walkInRequests.$inferInsert;

export const DIGEST_SECTIONS = [
  { id: "bookings", label: "Booking List", description: "Upcoming bookings by month" },
  { id: "performance", label: "Business Performance", description: "Show/confirm/deposit rates" },
  { id: "financial", label: "Financial Snapshot", description: "Revenue projections & pipeline" },
  { id: "underwriting", label: "Underwriting Model", description: "Estimate formula, rates & templates" },
  { id: "attribution", label: "Attribution", description: "Booking sources & referrers" },
  { id: "availability", label: "Artist Availability", description: "Status & manage availability link" },
  { id: "cancelled", label: "Cancelled / Declined", description: "Recently cancelled bookings" },
  { id: "deleted", label: "Deleted Bookings", description: "Recently deleted bookings" },
  { id: "blocked", label: "Blocked Emails", description: "Blocked email addresses" },
  { id: "changelog", label: "System Updates", description: "Recent platform changes and release notes" },
] as const;

export const insertSiteConfigSchema = createInsertSchema(siteConfig).omit({ id: true });
export type InsertSiteConfig = z.infer<typeof insertSiteConfigSchema>;

export const insertArtistSchema = createInsertSchema(artists).omit({ id: true, createdAt: true });
export const insertPortfolioImageSchema = createInsertSchema(portfolioImages).omit({ id: true });
export const insertEventSchema = createInsertSchema(events).omit({ id: true, createdAt: true, updatedAt: true });
export const insertPushSubscriptionSchema = createInsertSchema(pushSubscriptions).omit({ id: true, createdAt: true });
export const insertClientPushSubscriptionSchema = createInsertSchema(clientPushSubscriptions).omit({ id: true, createdAt: true });
export type InsertClientPushSubscription = z.infer<typeof insertClientPushSubscriptionSchema>;
export type ClientPushSubscription = typeof clientPushSubscriptions.$inferSelect;

export const ARTIST_AVAILABILITY_STATUSES = ["available", "limited", "booked"] as const;
export const insertArtistAvailabilitySchema = createInsertSchema(artistAvailability)
  .omit({ id: true, updatedAt: true })
  .extend({
    status: z.enum(ARTIST_AVAILABILITY_STATUSES).default("available"),
  });

export type InsertArtist = z.infer<typeof insertArtistSchema>;
export type Artist = typeof artists.$inferSelect;
export type InsertPortfolioImage = z.infer<typeof insertPortfolioImageSchema>;
export type PortfolioImage = typeof portfolioImages.$inferSelect;
export type InsertEvent = z.infer<typeof insertEventSchema>;
export type Event = typeof events.$inferSelect;
export type PushSubscription = typeof pushSubscriptions.$inferSelect;
export type InsertPushSubscription = z.infer<typeof insertPushSubscriptionSchema>;
export type ArtistAvailabilityRow = typeof artistAvailability.$inferSelect;
export type InsertArtistAvailability = z.infer<typeof insertArtistAvailabilitySchema>;

export const insertStudioConfigSchema = createInsertSchema(studioConfig).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertStudioConfig = z.infer<typeof insertStudioConfigSchema>;
export type StudioConfig = typeof studioConfig.$inferSelect;

export const LEAD_STATUSES = ["new", "nurturing", "qualified", "waitlisted", "converted", "unsubscribed", "contacted", "demo_scheduled", "proposal_sent", "engaged", "lost", "cold-nurture"] as const;
export type LeadStatus = typeof LEAD_STATUSES[number];

export const NGU_SEGMENTS = ["studio_owner", "solo_artist", "agency", "convention", "educator", "distributor", "adjacent", "general"] as const;
export type NguSegment = typeof NGU_SEGMENTS[number];

// Sales-cycle pipeline stages (cold → audit_booked → demo_done → proposal_sent → closed)
// Used to compute time-in-stage and conversion rates (Software-as-a-Science Magic Number).
// Distinct from `status` (which encodes drip/lifecycle state). A lead can be e.g.
// status=engaged + pipeline_stage=demo_done. "lost" is a terminal off-ramp.
export const PIPELINE_STAGES = ["cold", "audit_booked", "demo_done", "proposal_sent", "closed", "lost"] as const;
export type PipelineStage = typeof PIPELINE_STAGES[number];

export const netglowupLeads = pgTable("netglowup_leads", {
  id: serial("id").primaryKey(),
  // migration-safety-ok — original column, predates migration tracking
  email: varchar("email", { length: 255 }).notNull().unique(),
  // migration-safety-ok — original column, predates migration tracking
  token: varchar("token", { length: 64 }).notNull(),
  status: varchar("status", { length: 20 }).notNull().default("new"),
  shopName: text("shop_name"),
  ownerName: text("owner_name"),
  phone: text("phone"),
  address: text("address"),
  city: text("city"),
  state: text("state"),
  zip: text("zip"),
  artistCount: integer("artist_count"),
  instagramUrl: text("instagram_url"),
  facebookUrl: text("facebook_url"),
  hours: text("hours"),
  defaultDeposit: integer("default_deposit"),
  hourlyRate: integer("hourly_rate"),
  dripStep: integer("drip_step").notNull().default(0),
  qualificationScore: integer("qualification_score").notNull().default(0),
  // migration-safety-ok — nullable, no NOT NULL constraint, safe to add to existing rows
  leadScore: integer("lead_score"),
  drip1SentAt: timestamp("drip1_sent_at"),
  drip2SentAt: timestamp("drip2_sent_at"),
  drip3SentAt: timestamp("drip3_sent_at"),
  drip4SentAt: timestamp("drip4_sent_at"),
  drip5SentAt: timestamp("drip5_sent_at"),
  drip6SentAt: timestamp("drip6_sent_at"),
  drip6bSentAt: timestamp("drip6b_sent_at"),
  drip7SentAt: timestamp("drip7_sent_at"),
  isHotLead: boolean("is_hot_lead").notNull().default(false),
  hotLeadAt: timestamp("hot_lead_at"),
  fastTrackedAt: timestamp("fast_tracked_at"),
  profileViewedAt: timestamp("profile_viewed_at"),
  waitlistJoinedAt: timestamp("waitlist_joined_at"),
  unsubscribedAt: timestamp("unsubscribed_at"),
  callRequestedAt: timestamp("call_requested_at"),
  callRequestName: text("call_request_name"),
  callRequestPhone: text("call_request_phone"),
  callRequestNote: text("call_request_note"),
  segment: varchar("segment", { length: 30 }),
  specialties: text("specialties").array(),
  emailVerifiedAt: timestamp("email_verified_at"),
  emailVerificationToken: varchar("email_verification_token", { length: 64 }),
  closedAt: timestamp("closed_at"),
  // LAPS stage entry timestamps — captured the first time a lead transitions into
  // each stage (A = Applicant, P = Presented). S-stage uses closedAt above.
  // Used by /api/admin/ngu-pipeline/laps for accurate 7d/30d delta counts so
  // updates to other fields (e.g. notes, segment) don't pollute the deltas.
  aStageEnteredAt: timestamp("a_stage_entered_at"),
  pStageEnteredAt: timestamp("p_stage_entered_at"),
  closedMonthlyValue: integer("closed_monthly_value"),
  closeNote: text("close_note"),
  closeReason: varchar("close_reason", { length: 50 }),
  // Feature: Two-question intake (sent after email verify, before drip 1)
  intakeSentAt: timestamp("intake_sent_at"),
  intakeArtistCount: varchar("intake_artist_count", { length: 20 }),    // "just_me" | "2_3" | "4_6" | "7_plus"
  intakeHeadache: varchar("intake_headache", { length: 30 }),            // "booking_chaos" | "no_shows" | "chasing_deposits" | "all_of_above"
  intakeAnsweredAt: timestamp("intake_answered_at"),
  // Feature: Disqualification email (between drip 3 and drip 4)
  dripDisqualSentAt: timestamp("drip_disqual_sent_at"),
  // Feature: Downsell email (after all drips, for fully-disengaged leads)
  downsellSentAt: timestamp("downsell_sent_at"),
  // Feature: Referral program — each lead has a unique share code; referred leads are linked back
  referralToken: varchar("referral_token", { length: 64 }),
  referredByToken: varchar("referred_by_token", { length: 64 }),
  // migration-safety-ok — nullable FK, no NOT NULL, safe to add to existing rows
  referredByLeadId: integer("referred_by_lead_id"),
  source: varchar("source", { length: 50 }).default("netglowup-landing"),
  demoShowedAt: timestamp("demo_showed_at"),
  followupType: varchar("followup_type", { length: 20 }),
  followupSentAt: timestamp("followup_sent_at"),
  isExistingClient: boolean("is_existing_client").notNull().default(false),
  existingClientNote: text("existing_client_note"),
  panelReached: integer("panel_reached"),
  comicSlug: varchar("comic_slug", { length: 50 }),
  utmSource: text("utm_source"),
  utmMedium: text("utm_medium"),
  utmCampaign: text("utm_campaign"),
  utmTerm: text("utm_term"),
  utmContent: text("utm_content"),
  referrer: text("referrer"),
  landingPage: text("landing_page"),
  gclid: text("gclid"),
  fbclid: text("fbclid"),
  msclkid: text("msclkid"),
  wbraid: text("wbraid"),
  gbraid: text("gbraid"),
  utmId: text("utm_id"),
  chairCount: integer("chair_count"),
  existingTools: text("existing_tools").array(),
  sdrName: text("sdr_name"),
  adminNote: text("admin_note"),
  funnelEngaged: boolean("funnel_engaged").default(false),
  paidAt: timestamp("paid_at"),
  churnedAt: timestamp("churned_at"),
  stripeSessionId: varchar("stripe_session_id", { length: 200 }),
  // Booking-depth qualifying questions (collected on demo request form)
  bookedOutCurrent: varchar("booked_out_current", { length: 20 }),  // "0_2" | "2_4" | "4_6" | "6_plus"
  bookedOutTarget: varchar("booked_out_target", { length: 20 }),    // "0_2" | "2_4" | "4_6" | "6_plus"
  hasWaitlist: varchar("has_waitlist", { length: 10 }),             // "yes" | "no"
  canRebookSameDay: varchar("can_rebook_same_day", { length: 10 }), // "yes" | "no"
  // Last manual outreach (e.g. "Follow up" clicked from idle-hot-leads list).
  // Used to drop a lead off the idle-hot-leads list after the operator records
  // that they reached out, even before the lead replies.
  lastContactedAt: timestamp("last_contacted_at"),
  // Sales pipeline stage (cold → audit_booked → demo_done → proposal_sent → closed | lost).
  // Tracked separately from `status` so we can compute conversion rate per stage and
  // avg time-in-stage (Software-as-a-Science Magic Number). pipelineStageEnteredAt is
  // updated whenever pipelineStage transitions to a different value.
  pipelineStage: varchar("pipeline_stage", { length: 20 }).notNull().default("cold"),
  pipelineStageEnteredAt: timestamp("pipeline_stage_entered_at").defaultNow(),
  // Task #1336 — NGU lead magnet audit gaps: stores the yes/no answers from the studio audit quiz
  // so drip 2 can reference specific gaps by name and personalize the follow-up email.
  auditGaps: jsonb("audit_gaps"),
  // Task #1936 — DM Audit drip: stores the letter grade (A/B/C/D/F) and primary bottleneck
  // name computed by the DM Tax Audit so drip 2+ can reference them by name.
  // Both nullable — leads that pre-date this feature or entered via other sources have null here.
  dmAuditGrade: varchar("dm_audit_grade", { length: 1 }),
  dmAuditBottleneck: varchar("dm_audit_bottleneck", { length: 100 }),
  // Unit Economics ops dashboard: tracks last time a subscriber was
  // marked active by the NGU team. Used to compute "at-risk" subscribers — those
  // who haven't had any recorded activity in 30+ days. Nullable: leads that
  // pre-date this feature or have never been manually marked active have null here.
  lastActivityAt: timestamp("last_activity_at"),
  // Task #2306 — Speed-to-lead tracking for NGU leads.
  // first_call_initiated_at: first time an SDR clicked the tel: link to call this lead.
  // first_call_notes_at: first time the SDR logged a call note for this lead.
  // speed_to_lead_seconds: derived integer (first_call_initiated_at - createdAt) in seconds.
  // All nullable — leads that pre-date this feature or haven't been called keep NULL.
  // migration-safety-ok — nullable, no NOT NULL constraint, safe to add to existing rows
  firstCallInitiatedAt: timestamp("first_call_initiated_at"),
  // migration-safety-ok — nullable, no NOT NULL constraint, safe to add to existing rows
  firstCallNotesAt: timestamp("first_call_notes_at"),
  // migration-safety-ok — nullable, no NOT NULL constraint, safe to add to existing rows
  speedToLeadSeconds: integer("speed_to_lead_seconds"),
  // VSL variant attribution — stores the vsl_variants.id the lead saw on the ICP VSL page
  // before submitting. Null means the lead signed up without going through the VSL page
  // or saw the fallback control video. Used to compare conversion rates per variant.
  // migration-safety-ok — nullable FK, no NOT NULL, safe to add to existing rows
  vslVariantId: integer("vsl_variant_id"),
  // Post-booking intake triage (Task #2288 — studio_owner ICP overhaul)
  // lead_temperature: computed from intake answers — 'hot' | 'warm' | 'nurture_hold' | 'unknown'
  // Expand-contract: ADD COLUMN guard in script/pre-push-migrations.ts with DEFAULT 'unknown'
  leadTemperature: varchar("lead_temperature", { length: 20 }).default("unknown"),
  // intake_responses: raw JSON from post-booking intake form
  // migration-safety-ok — nullable jsonb, no NOT NULL, safe to add to existing rows
  intakeResponses: jsonb("intake_responses"),
  // Retention infrastructure — payment dunning, at-risk check-in, cancellation save
  // All nullable; set by admin action or scheduler. Gated by ngu_subscriber_retention_enabled flag.
  //
  // studioActivatedAt: set by admin (or future Stripe webhook) when the studio has confirmed
  // their first client booking through the platform. This is the "appointment activity" signal
  // used for at-risk churn detection. It is necessarily manual for agency-track clients because
  // each studio runs on its own isolated deployment — cross-tenant booking data is not available
  // in the central NGU leads DB. For self-serve (studioSignups) clients, this can be auto-set
  // via the checkout.session.completed webhook when activatedAt is recorded there.
  studioActivatedAt: timestamp("studio_activated_at"),
  paymentFailedAt: timestamp("payment_failed_at"),
  dunningAttempt1SentAt: timestamp("dunning_attempt1_sent_at"),
  dunningAttempt2SentAt: timestamp("dunning_attempt2_sent_at"),
  dunningAttempt3SentAt: timestamp("dunning_attempt3_sent_at"),
  atRiskCheckInSentAt: timestamp("at_risk_check_in_sent_at"),
  cancellationSaveSentAt: timestamp("cancellation_save_sent_at"),
  // migration-safety-ok — nullable integer, no NOT NULL constraint, safe to add to existing rows
  annualRecoverable: integer("annual_recoverable"),
  // migration-safety-ok — nullable integer, no NOT NULL constraint, safe to add to existing rows
  bookingsPerWeek: integer("bookings_per_week"),
  // migration-safety-ok — nullable integer, no NOT NULL constraint, safe to add to existing rows
  noShowsPerMonth: integer("no_shows_per_month"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_netglowup_leads_email").on(table.email),
  index("idx_netglowup_leads_token").on(table.token),
  index("idx_netglowup_leads_status").on(table.status),
  index("idx_netglowup_leads_drip").on(table.dripStep),
  index("idx_netglowup_leads_pipeline_stage").on(table.pipelineStage),
]);

export const netglowupFunnelEvents = pgTable("netglowup_funnel_events", {
  id: serial("id").primaryKey(),
  sessionId: varchar("session_id", { length: 64 }).notNull(),
  stepName: varchar("step_name", { length: 50 }).notNull(),
  segment: varchar("segment", { length: 30 }),
  leadId: integer("lead_id"),
  utmSource: varchar("utm_source", { length: 200 }),
  utmMedium: varchar("utm_medium", { length: 200 }),
  utmCampaign: varchar("utm_campaign", { length: 200 }),
  utmTerm: varchar("utm_term", { length: 200 }),
  utmContent: varchar("utm_content", { length: 200 }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_funnel_events_session").on(table.sessionId),
  index("idx_funnel_events_step").on(table.stepName),
  index("idx_funnel_events_created").on(table.createdAt),
  index("idx_funnel_events_utm_campaign").on(table.utmCampaign),
]);

export type NetGlowUpFunnelEvent = typeof netglowupFunnelEvents.$inferSelect;
export type InsertNetGlowUpFunnelEvent = typeof netglowupFunnelEvents.$inferInsert;

export const nguAdCopyCache = pgTable("ngu_ad_copy_cache", {
  id: serial("id").primaryKey(),
  segment: varchar("segment", { length: 50 }).notNull(),
  platform: varchar("platform", { length: 20 }).notNull(),
  campaignGoal: varchar("campaign_goal", { length: 30 }).notNull(),
  angle: text("angle"),
  adCopyJson: text("ad_copy_json").notNull(),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export type NguAdCopyCache = typeof nguAdCopyCache.$inferSelect;
export type InsertNguAdCopyCache = typeof nguAdCopyCache.$inferInsert;

export const RELEASE_FORM_SERVICE_TYPES = ["tattoo", "piercing", "stretching", "suspension", "photography", "general"] as const;
export type ReleaseFormServiceType = typeof RELEASE_FORM_SERVICE_TYPES[number];

export const releaseFormSubmissions = pgTable("release_form_submissions", {
  id: serial("id").primaryKey(),
  bookingAppointmentId: integer("booking_appointment_id").references(() => bookingAppointments.id, { onDelete: "set null" }),
  serviceType: varchar("service_type", { length: 30 }).notNull().default("general"),
  token: varchar("token", { length: 64 }).notNull().unique(),
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  expiresAt: timestamp("expires_at").notNull(),
  submittedAt: timestamp("submitted_at"),
  completedVia: varchar("completed_via", { length: 30 }),
  clientName: text("client_name"),
  clientEmail: text("client_email"),
  clientPhone: text("client_phone"),
  clientDob: text("client_dob"),
  chosenName: text("chosen_name"),
  pronouns: text("pronouns"),
  address: text("address"),
  postcode: text("postcode"),
  socialMedia: text("social_media"),
  nationality: text("nationality"),
  gender: text("gender"),
  race: text("race"),
  emergencyContact: jsonb("emergency_contact").$type<Record<string, string>>(),
  physicianInfo: jsonb("physician_info").$type<Record<string, string>>(),
  healthAnswers: jsonb("health_answers").$type<Record<string, boolean | string>>(),
  provisionResponses: jsonb("provision_responses").$type<Record<string, boolean>>(),
  clientSignature: text("client_signature"),
  artistId: text("artist_id"),
  artistSignature: text("artist_signature"),
  parentInfo: jsonb("parent_info").$type<Record<string, string>>(),
  notaryInfo: jsonb("notary_info").$type<Record<string, string>>(),
  serviceLocation: text("service_location"),
  referralSource: text("referral_source"),
  newsletterOptedIn: boolean("newsletter_opted_in").default(false),
  photoConsentOptedOut: boolean("photo_consent_opted_out").default(false),
  photoIdUrl: text("photo_id_url"),
  paperFormUrl: text("paper_form_url"),
  paperFormUrls: text("paper_form_urls").array(),
  markedOnFileAt: timestamp("marked_on_file_at"),
  markedOnFileNote: text("marked_on_file_note"),
  signerEmail: text("signer_email"),
  signerName: text("signer_name"),
  emailMismatch: boolean("email_mismatch").default(false),
  nameMismatch: boolean("name_mismatch").default(false),
  submittedIp: varchar("submitted_ip", { length: 100 }),
  submittedUserAgent: text("submitted_user_agent"),
  viewToken: varchar("view_token", { length: 64 }),
  viewTokenExpiresAt: timestamp("view_token_expires_at"),
  voidReason: text("void_reason"),
  voidedAt: timestamp("voided_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_release_form_token").on(table.token),
  index("idx_release_form_booking").on(table.bookingAppointmentId),
  index("idx_release_form_status").on(table.status),
]);

export const insertReleaseFormSchema = createInsertSchema(releaseFormSubmissions).omit({ id: true, createdAt: true }).extend({
  serviceType: z.enum(RELEASE_FORM_SERVICE_TYPES).optional().default("general"),
  status: z.enum(["pending", "completed", "expired", "on_file", "cancelled"]).optional().default("pending"),
  completedVia: z.enum(["email_link", "walk_in_link", "in_person", "on_file"]).optional().nullable(),
  clientEmail: z.string().email().max(320).optional().nullable(),
});
export type InsertReleaseForm = z.infer<typeof insertReleaseFormSchema>;
export type ReleaseFormSubmission = typeof releaseFormSubmissions.$inferSelect;

export const bookingCallLogs = pgTable("booking_call_logs", {
  id: serial("id").primaryKey(),
  bookingId: integer("booking_id").notNull().references(() => bookingAppointments.id, { onDelete: "cascade" }),
  calledAt: timestamp("called_at").notNull(),
  durationMinutes: integer("duration_minutes"),
  staffName: varchar("staff_name", { length: 100 }).notNull(),
  callReason: varchar("call_reason", { length: 100 }),
  outcome: varchar("outcome", { length: 60 }),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_call_logs_booking").on(table.bookingId),
  index("idx_call_logs_called_at").on(table.calledAt),
]);

export type BookingCallLog = typeof bookingCallLogs.$inferSelect;
export type InsertBookingCallLog = typeof bookingCallLogs.$inferInsert;

export const adminAuditLogs = pgTable("admin_audit_logs", {
  id: serial("id").primaryKey(),
  action: varchar("action", { length: 60 }).notNull(),
  entity: varchar("entity", { length: 60 }).notNull(),
  entityId: varchar("entity_id", { length: 100 }),
  entityLabel: varchar("entity_label", { length: 200 }),
  changedBy: varchar("changed_by", { length: 60 }).notNull().default("admin"),
  adminIp: varchar("admin_ip", { length: 60 }),
  userAgent: text("user_agent"),
  previousValues: jsonb("previous_values").$type<Record<string, unknown>>(),
  newValues: jsonb("new_values").$type<Record<string, unknown>>(),
  diff: jsonb("diff").$type<Array<{ field: string; from: unknown; to: unknown }>>(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_audit_logs_entity").on(table.entity),
  index("idx_audit_logs_action").on(table.action),
  index("idx_audit_logs_created").on(table.createdAt),
]);

export type AdminAuditLog = typeof adminAuditLogs.$inferSelect;
export type InsertAdminAuditLog = typeof adminAuditLogs.$inferInsert;

export const clientDeployments = pgTable("client_deployments", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  url: text("url").notNull(),
  environment: varchar("environment", { length: 20 }).notNull().default("production"),
  notes: text("notes"),
  active: boolean("active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  tierId: integer("tier_id").references((): AnyPgColumn => featureTiers.id, { onDelete: "set null" }),
  domainStage: varchar("domain_stage", { length: 20 }).notNull().default("subdomain"),
  customDomain: text("custom_domain"),
  domainMigratedAt: timestamp("domain_migrated_at"),
  managedStudioId: integer("managed_studio_id"),
  // ── Rollout sync tracking (Task #3033) ─────────────────────────────────────
  // migration-safety-ok — all new nullable columns, no NOT NULL constraint
  githubBranch: varchar("github_branch", { length: 200 }),
  // migration-safety-ok — new nullable column
  pinnedSha: varchar("pinned_sha", { length: 40 }),
  // migration-safety-ok — new nullable column
  upstreamSha: varchar("upstream_sha", { length: 40 }),
  // migration-safety-ok — new nullable column
  lastSyncAt: timestamp("last_sync_at"),
  // migration-safety-ok — new nullable column: PASS | WARN | FAIL
  lastCheckResult: varchar("last_check_result", { length: 10 }),
  // migration-safety-ok — new nullable column
  lastCheckWarnCount: integer("last_check_warn_count"),
  // migration-safety-ok — new nullable column
  lastCheckFailCount: integer("last_check_fail_count"),
  // migration-safety-ok — new nullable column
  lastCheckAt: timestamp("last_check_at"),
  // migration-safety-ok — new nullable jsonb column
  featureFlagsSnapshot: jsonb("feature_flags_snapshot").$type<Record<string, boolean>>(),
  // migration-safety-ok — new nullable column: owner-scoped API key for agency PULL of flag snapshot
  deploymentApiKey: varchar("deployment_api_key", { length: 200 }),
  // migration-safety-ok — new nullable columns: per-studio reporting credentials for agency snapshot pull
  reportingToken: varchar("reporting_token", { length: 200 }),
  reportingUrl: text("reporting_url"),
  // ── Software version tracking (Task #3168) ────────────────────────────────
  // migration-safety-ok — new nullable columns; no NOT NULL constraint
  currentVersion: varchar("current_version", { length: 100 }),
  versionUpdatedAt: timestamp("version_updated_at"),
}, (table) => [
  index("idx_client_deployments_active").on(table.active),
]);

export const DOMAIN_STAGES = ["subdomain", "pending_dns", "custom_domain"] as const;
export type DomainStage = (typeof DOMAIN_STAGES)[number];

// ── Studio Snapshots (Task #3133) ─────────────────────────────────────────────
// Persists the hourly cross-studio performance + ops-health pull. One row per
// collection attempt per deployment; latest row = current state.
// migration-safety-ok — brand-new table, no existing rows to migrate
export const studioSnapshots = pgTable("studio_snapshots", {
  id: serial("id").primaryKey(),
  deploymentId: integer("deployment_id").notNull().references(() => clientDeployments.id, { onDelete: "cascade" }),
  collectedAt: timestamp("collected_at").defaultNow().notNull(),
  collectionLatencyMs: integer("collection_latency_ms"),
  status: varchar("status", { length: 20 }).notNull().default("ok"),
  errorMessage: text("error_message"),
  payload: jsonb("payload").$type<Record<string, unknown>>(),
  opsHealth: jsonb("ops_health").$type<Record<string, unknown>>(),
}, (table) => [
  index("idx_studio_snapshots_deployment_id").on(table.deploymentId),
  index("idx_studio_snapshots_collected_at").on(table.collectedAt.desc()),
  // Composite: primary query pattern — latest snapshot per deployment
  index("idx_studio_snapshots_dep_collected").on(table.deploymentId, table.collectedAt.desc()),
  index("idx_studio_snapshots_status").on(table.status),
]);

export type StudioSnapshot = typeof studioSnapshots.$inferSelect;

export const insertClientDeploymentSchema = createInsertSchema(clientDeployments).omit({ id: true, createdAt: true }).extend({
  name: z.string().min(1).max(200),
  url: z.string().url().max(1000),
  environment: z.enum(["production", "staging"]).optional().default("production"),
  notes: z.string().max(2000).optional().nullable(),
  active: z.boolean().optional().default(true),
  tierId: z.number().int().optional().nullable(),
  domainStage: z.enum(DOMAIN_STAGES).optional().default("subdomain"),
  customDomain: z.string().max(255).optional().nullable(),
  domainMigratedAt: z.coerce.date().optional().nullable(),
  githubBranch: z.string().max(200).optional().nullable(),
  pinnedSha: z.string().max(40).optional().nullable(),
  upstreamSha: z.string().max(40).optional().nullable(),
});
export type InsertClientDeployment = z.infer<typeof insertClientDeploymentSchema>;
export type ClientDeployment = typeof clientDeployments.$inferSelect;

export const healthProbeRuns = pgTable("health_probe_runs", {
  id: serial("id").primaryKey(),
  runAt: timestamp("run_at").defaultNow().notNull(),
  // migration-safety-ok — predates migration tracking; table created via drizzle push before pre-push-migrations.ts existed, later ALTER TABLE guards for other columns made the audit treat the whole table as tracked
  probeType: varchar("probe_type", { length: 30 }).notNull(),
  // migration-safety-ok — predates migration tracking (see probeType above)
  endpoint: varchar("endpoint", { length: 100 }).notNull(),
  // migration-safety-ok — predates migration tracking (see probeType above)
  status: varchar("status", { length: 20 }).notNull(),
  httpStatus: integer("http_status"),
  // migration-safety-ok — predates migration tracking (see probeType above)
  detail: text("detail").notNull(),
  testedOrigin: text("tested_origin"),
  durationMs: integer("duration_ms"),
  alertSent: boolean("alert_sent").default(false).notNull(),
  // migration-safety-ok: NOT NULL DEFAULT false covers all existing rows without a backfill
  startupPhase: boolean("startup_phase").default(false).notNull(),
}, (table) => [
  // Compound covering index for per-endpoint queries: WHERE endpoint = ? ORDER BY run_at DESC LIMIT N.
  // Postgres can do one ordered scan per endpoint and stop at LIMIT — O(1) per endpoint.
  index("idx_probe_runs_endpoint_run_at").on(table.endpoint, table.runAt.desc().nullsFirst()),
  // Single-column index for cross-endpoint scans (getRecentProbeRuns / ops-threshold-alert pass-rate
  // calculation) where no endpoint filter is present. Without this, those queries do a full table scan.
  index("idx_probe_runs_run_at").on(table.runAt.desc().nullsFirst()),
  index("idx_probe_runs_status").on(table.status),
]);

export type HealthProbeRun = typeof healthProbeRuns.$inferSelect;
export type InsertHealthProbeRun = typeof healthProbeRuns.$inferInsert;

export const cspViolations = pgTable("csp_violations", {
  id: serial("id").primaryKey(),
  occurredAt: timestamp("occurred_at").defaultNow().notNull(),
  violatedDirective: varchar("violated_directive", { length: 100 }).notNull(),
  blockedUri: text("blocked_uri").notNull(),
  documentUri: text("document_uri").notNull().default(""),
  disposition: varchar("disposition", { length: 20 }).notNull().default("unknown"),
}, (table) => [
  index("idx_csp_violations_occurred_at").on(table.occurredAt),
]);
export type CspViolation = typeof cspViolations.$inferSelect;
export type InsertCspViolation = typeof cspViolations.$inferInsert;

export const insertNetGlowUpLeadSchema = createInsertSchema(netglowupLeads).omit({ id: true, createdAt: true, updatedAt: true }).extend({
  email: z.string().email().max(255),
  token: z.string().min(32).max(64),
  status: z.enum(LEAD_STATUSES).optional().default("new"),
  shopName: z.string().max(200).optional().nullable(),
  ownerName: z.string().max(200).optional().nullable(),
  phone: z.string().max(30).optional().nullable(),
  address: z.string().max(300).optional().nullable(),
  city: z.string().max(100).optional().nullable(),
  state: z.string().max(50).optional().nullable(),
  zip: z.string().max(20).optional().nullable(),
  artistCount: z.number().int().min(0).optional().nullable(),
  instagramUrl: z.string().max(500).optional().nullable(),
  facebookUrl: z.string().max(500).optional().nullable(),
  hours: z.string().max(500).optional().nullable(),
  defaultDeposit: z.number().int().min(0).optional().nullable(),
  hourlyRate: z.number().int().min(0).optional().nullable(),
  chairCount: z.number().int().min(0).optional().nullable(),
  existingTools: z.array(z.string().max(100)).optional().nullable(),
  sdrName: z.string().max(200).optional().nullable(),
  funnelEngaged: z.boolean().optional().nullable(),
  bookedOutCurrent: z.string().max(20).optional().nullable(),
  bookedOutTarget: z.string().max(20).optional().nullable(),
  hasWaitlist: z.string().max(10).optional().nullable(),
  canRebookSameDay: z.string().max(10).optional().nullable(),
  auditGaps: z.array(z.string()).optional().nullable(),
});
export type InsertNetGlowUpLead = z.infer<typeof insertNetGlowUpLeadSchema>;
export type NetGlowUpLead = typeof netglowupLeads.$inferSelect;

export const cancellationBlastPrompts = pgTable("cancellation_blast_prompts", {
  id: serial("id").primaryKey(),
  bookingId: integer("booking_id").notNull(), // migration-safety-ok — original column, present before migration tracking was added
  bookingDate: varchar("booking_date", { length: 20 }).notNull(), // migration-safety-ok — original column, present before migration tracking was added
  bookingTime: varchar("booking_time", { length: 10 }).notNull(), // migration-safety-ok — original column, present before migration tracking was added
  artistName: text("artist_name").notNull(), // migration-safety-ok — original column, present before migration tracking was added
  artistSlug: text("artist_slug"),
  serviceType: text("service_type"),
  clientName: text("client_name").notNull(), // migration-safety-ok — original column, present before migration tracking was added
  via: varchar("via", { length: 10 }).notNull().default("client"), // "admin" | "client"
  acknowledgedAt: timestamp("acknowledged_at"),
  // Did the admin actually send a blast after acknowledging?
  // null = unacknowledged, "yes" = sent, "no" = dismissed without sending, "later" = intends to send
  sentBlast: varchar("sent_blast", { length: 10 }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});
export type CancellationBlastPrompt = typeof cancellationBlastPrompts.$inferSelect;

export const featureFlags = pgTable("feature_flags", {
  id: serial("id").primaryKey(),
  // migration-safety-ok — original column, predates migration tracking
  key: varchar("key", { length: 100 }).notNull().unique(),
  name: text("name").notNull(),
  description: text("description").notNull().default(""),
  enabled: boolean("enabled").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow(),
  tested: boolean("tested").notNull().default(false),
  mvpComplete: boolean("mvp_complete").notNull().default(false),
  developedAt: timestamp("developed_at"),
  category: varchar("category", { length: 50 }).notNull().default("general"),
  notes: text("notes").notNull().default(""),
  scheduledEnableAt: timestamp("scheduled_enable_at", { withTimezone: true }),
  announcedAt: timestamp("announced_at", { withTimezone: true }),
  releaseNotesMd: text("release_notes_md").default(""),
  announceOnNextDeploy: boolean("announce_on_next_deploy").default(false),
}, (table) => [
  // Covers the feature-flag-scheduler job query:
  //   WHERE scheduled_enable_at <= NOW() AND enabled = false
  // Only the tiny fraction of actively-scheduled flags need to be indexed.
  // Without this index the scheduler does a full-table scan every 5 min.
  // Task #2898.
  index("idx_feature_flags_scheduled_enable_at")
    .on(table.scheduledEnableAt)
    .where(sql`scheduled_enable_at IS NOT NULL AND enabled = false`),
]);

export const insertFeatureFlagSchema = createInsertSchema(featureFlags).omit({ id: true, createdAt: true, updatedAt: true }).extend({
  key: z.string().min(1).max(100),
  name: z.string().min(1).max(200),
  description: z.string().max(500).optional().default(""),
  enabled: z.boolean().optional().default(false),
  tested: z.boolean().optional().default(false),
  mvpComplete: z.boolean().optional().default(false),
  developedAt: z.string().datetime().optional().nullable(),
  category: z.string().max(50).optional().default("general"),
  notes: z.string().max(2000).optional().default(""),
});
export type InsertFeatureFlag = z.infer<typeof insertFeatureFlagSchema>;
export type FeatureFlag = typeof featureFlags.$inferSelect;

export const featureTiers = pgTable("feature_tiers", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  description: text("description").notNull().default(""),
  color: varchar("color", { length: 20 }).notNull().default("#6366f1"),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertFeatureTierSchema = createInsertSchema(featureTiers).omit({ id: true, createdAt: true }).extend({
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional().default(""),
  color: z.string().max(20).optional().default("#6366f1"),
  sortOrder: z.number().int().optional().default(0),
});
export type InsertFeatureTier = z.infer<typeof insertFeatureTierSchema>;
export type FeatureTier = typeof featureTiers.$inferSelect;

export const featureFlagTiers = pgTable("feature_flag_tiers", {
  id: serial("id").primaryKey(),
  flagKey: varchar("flag_key", { length: 100 }).notNull(),
  tierId: integer("tier_id").notNull().references(() => featureTiers.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_feature_flag_tiers_flag_key").on(table.flagKey),
  index("idx_feature_flag_tiers_tier_id").on(table.tierId),
  uniqueIndex("uq_feature_flag_tiers_flag_key_tier_id").on(table.flagKey, table.tierId),
]);
export type FeatureFlagTier = typeof featureFlagTiers.$inferSelect;

export const featureFlagTierHistory = pgTable("feature_flag_tier_history", {
  id: serial("id").primaryKey(),
  flagKey: varchar("flag_key", { length: 100 }).notNull(),
  tierId: integer("tier_id").notNull(),
  tierName: text("tier_name").notNull(),
  action: varchar("action", { length: 10 }).notNull(), // 'added' | 'removed'
  changedAt: timestamp("changed_at").defaultNow().notNull(),
}, (table) => [
  index("idx_feature_flag_tier_history_flag_key").on(table.flagKey),
]);
export type FeatureFlagTierHistory = typeof featureFlagTierHistory.$inferSelect;

export const clientDeploymentFlagOverrides = pgTable("client_deployment_flag_overrides", {
  id: serial("id").primaryKey(),
  deploymentId: integer("deployment_id").notNull().references(() => clientDeployments.id, { onDelete: "cascade" }),
  flagKey: varchar("flag_key", { length: 100 }).notNull(),
  enabled: boolean("enabled").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_client_deployment_flag_overrides_deployment").on(table.deploymentId),
  index("idx_client_deployment_flag_overrides_flag_key").on(table.flagKey),
  uniqueIndex("uq_client_deployment_flag_overrides").on(table.deploymentId, table.flagKey),
]);

export const insertClientDeploymentFlagOverrideSchema = createInsertSchema(clientDeploymentFlagOverrides).omit({ id: true, createdAt: true, updatedAt: true }).extend({
  deploymentId: z.number().int().positive(),
  flagKey: z.string().min(1).max(100),
  enabled: z.boolean(),
});
export type InsertClientDeploymentFlagOverride = z.infer<typeof insertClientDeploymentFlagOverrideSchema>;
export type ClientDeploymentFlagOverride = typeof clientDeploymentFlagOverrides.$inferSelect;

export const SUPPORT_STATUSES = ["open", "in_progress", "resolved", "closed"] as const;
export type SupportStatus = typeof SUPPORT_STATUSES[number];

export const SUPPORT_IMPACT_LEVELS = ["critical", "high", "medium", "low"] as const;
export const SUPPORT_URGENCY_LEVELS = ["urgent", "high", "normal", "low"] as const;
export const SUPPORT_SLA_TIERS = ["P1", "P2", "P3", "P4"] as const;

export const supportTickets = pgTable("support_tickets", {
  id: serial("id").primaryKey(),
  // migration-safety-ok — original column, predates migration tracking
  ticketNumber: integer("ticket_number").notNull().unique(),
  // migration-safety-ok — original column, predates migration tracking
  title: text("title").notNull(),
  // migration-safety-ok — original column, predates migration tracking
  description: text("description").notNull(),
  imageUrls: text("image_urls").array().default([]),
  status: varchar("status", { length: 20 }).notNull().default("open"),
  impact: varchar("impact", { length: 20 }).notNull().default("medium"),
  urgency: varchar("urgency", { length: 20 }).notNull().default("normal"),
  slaTier: varchar("sla_tier", { length: 5 }).notNull().default("P3"),
  eta: text("eta"),
  // migration-safety-ok — original column, predates migration tracking
  ownerToken: varchar("owner_token", { length: 64 }).notNull(),
  ownerEmail: varchar("owner_email", { length: 320 }),
  studioName: varchar("studio_name", { length: 200 }),
  // migration-safety-ok — original column, predates migration tracking
  adminToken: varchar("admin_token", { length: 64 }).notNull().unique(),
  submitterRole: varchar("submitter_role", { length: 20 }),
  submitterEmail: varchar("submitter_email", { length: 320 }),
  submitterRef: text("submitter_ref"),
  resolvedAt: timestamp("resolved_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_support_tickets_owner_token").on(table.ownerToken),
  index("idx_support_tickets_status").on(table.status),
  index("idx_support_tickets_submitter_ref").on(table.submitterRef),
]);

export const supportTicketMessages = pgTable("support_ticket_messages", {
  id: serial("id").primaryKey(),
  ticketId: integer("ticket_id").notNull().references(() => supportTickets.id, { onDelete: "cascade" }),
  senderRole: varchar("sender_role", { length: 10 }).notNull(),
  body: text("body").notNull(),
  imageUrls: text("image_urls").array().default([]),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_support_messages_ticket").on(table.ticketId),
]);

export type SupportTicket = typeof supportTickets.$inferSelect;
export type SupportTicketMessage = typeof supportTicketMessages.$inferSelect;

export const insertSupportTicketSchema = createInsertSchema(supportTickets).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertSupportTicket = z.infer<typeof insertSupportTicketSchema>;

export const insertSupportTicketMessageSchema = createInsertSchema(supportTicketMessages).omit({
  id: true,
  createdAt: true,
});
export type InsertSupportTicketMessage = z.infer<typeof insertSupportTicketMessageSchema>;

export const COMM_TEMPLATE_CATEGORIES = ["general", "deposit", "scheduling", "consultation", "followup"] as const;
export type CommTemplateCategory = typeof COMM_TEMPLATE_CATEGORIES[number];

export const communicationTemplates = pgTable("communication_templates", {
  id: serial("id").primaryKey(),
  name: varchar("name", { length: 100 }).notNull(),
  category: varchar("category", { length: 50 }).notNull().default("general"),
  subject: varchar("subject", { length: 200 }).notNull(),
  body: text("body").notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
  isDefault: boolean("is_default").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
export type CommunicationTemplate = typeof communicationTemplates.$inferSelect;
export const insertCommunicationTemplateSchema = createInsertSchema(communicationTemplates).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertCommunicationTemplate = z.infer<typeof insertCommunicationTemplateSchema>;

export const bookingManualEmails = pgTable("booking_manual_emails", {
  id: serial("id").primaryKey(),
  bookingId: integer("booking_id").notNull().references(() => bookingAppointments.id, { onDelete: "cascade" }),
  templateId: integer("template_id").references(() => communicationTemplates.id, { onDelete: "set null" }),
  templateName: varchar("template_name", { length: 100 }),
  subject: varchar("subject", { length: 200 }).notNull(),
  body: text("body").notNull(),
  sentBy: varchar("sent_by", { length: 100 }).notNull(),
  sentAt: timestamp("sent_at").defaultNow().notNull(),
  resendId: varchar("resend_id", { length: 100 }),
  status: varchar("status", { length: 20 }).notNull().default("sent"),
}, (table) => [
  index("idx_manual_emails_booking").on(table.bookingId),
]);
export type BookingManualEmail = typeof bookingManualEmails.$inferSelect;
export type InsertBookingManualEmail = typeof bookingManualEmails.$inferInsert;

export const designReviews = pgTable("design_reviews", {
  id: serial("id").primaryKey(),
  bookingId: integer("booking_id").notNull().references(() => bookingAppointments.id, { onDelete: "cascade" }),
  revisionNumber: integer("revision_number").notNull().default(1),
  imageUrl: text("image_url").notNull(),
  artistNote: text("artist_note"),
  reviewToken: varchar("review_token", { length: 64 }).notNull().unique(),
  status: varchar("status", { length: 30 }).notNull().default("pending_review"),
  clientComment: text("client_comment"),
  notificationSentAt: timestamp("notification_sent_at"),
  respondedAt: timestamp("responded_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_design_reviews_booking").on(table.bookingId),
  index("idx_design_reviews_token").on(table.reviewToken),
]);
export type DesignReview = typeof designReviews.$inferSelect;
export type InsertDesignReview = typeof designReviews.$inferInsert;

export const bookingWaitlist = pgTable("booking_waitlist", {
  id: serial("id").primaryKey(),
  artistSlug: varchar("artist_slug", { length: 100 }).notNull(),
  requestedDate: text("requested_date"),
  clientName: varchar("client_name", { length: 200 }).notNull(),
  clientEmail: varchar("client_email", { length: 300 }).notNull(),
  clientPhone: varchar("client_phone", { length: 30 }),
  serviceType: varchar("service_type", { length: 100 }),
  notes: text("notes"),
  notifiedAt: timestamp("notified_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_booking_waitlist_artist").on(table.artistSlug),
  index("idx_booking_waitlist_email").on(table.clientEmail),
]);
export type BookingWaitlistEntry = typeof bookingWaitlist.$inferSelect;
export const insertBookingWaitlistSchema = createInsertSchema(bookingWaitlist).omit({ id: true, notifiedAt: true, createdAt: true });
export type InsertBookingWaitlistEntry = z.infer<typeof insertBookingWaitlistSchema>;

// ── Waitlist Claim Tokens — Task #916 ─────────────────────────────────────────
// One token is created per waitlist entry per cancellation/reschedule event.
// The first client to POST /api/waitlist/claim/:token wins; all others get a
// "sorry, taken" response. Tokens expire after 24 hours.
//
// SINGLE-WINNER INVARIANT: at most one token per booking_id may have a
// non-null claimed_at. This is enforced at two layers:
//   1. Application layer: claimWaitlistSlot() checks for any already-claimed
//      token for the same bookingId before performing the UPDATE, and catches
//      PostgreSQL unique constraint violation (code 23505) for concurrent races.
//   2. Database layer: a partial unique index on (booking_id) WHERE claimed_at
//      IS NOT NULL — created in migrations/0022_add_waitlist_claim_tokens.sql.
//      Drizzle does not support partial indexes in table definitions, so this
//      invariant exists only in the migration; do not remove that index.
export const waitlistClaimTokens = pgTable("waitlist_claim_tokens", {
  id: serial("id").primaryKey(),
  waitlistEntryId: integer("waitlist_entry_id").notNull(),
  bookingId: integer("booking_id").notNull(),
  token: varchar("token", { length: 64 }).notNull(),
  artistSlug: varchar("artist_slug", { length: 100 }).notNull(),
  artistName: text("artist_name").notNull(),
  bookingDate: varchar("booking_date", { length: 20 }).notNull(),
  bookingTime: varchar("booking_time", { length: 20 }).notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  claimedAt: timestamp("claimed_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  uniqueIndex("idx_waitlist_claim_token_unique").on(table.token),
  index("idx_waitlist_claim_booking_id").on(table.bookingId),
  index("idx_waitlist_claim_entry_id").on(table.waitlistEntryId),
  // NOTE: a partial unique index on (booking_id) WHERE claimed_at IS NOT NULL
  // exists in migrations/0022_add_waitlist_claim_tokens.sql to enforce the
  // single-winner invariant at the DB level. Drizzle cannot represent partial
  // indexes here, so the migration is the authoritative source for that index.
]);
export type WaitlistClaimToken = typeof waitlistClaimTokens.$inferSelect;
export const insertWaitlistClaimTokenSchema = createInsertSchema(waitlistClaimTokens).omit({ id: true, claimedAt: true, createdAt: true });
export type InsertWaitlistClaimToken = z.infer<typeof insertWaitlistClaimTokenSchema>;

export const adminTokenAccessLog = pgTable("admin_token_access_log", {
  id: serial("id").primaryKey(),
  // migration-safety-ok — predates migration tracking; table created via drizzle push before pre-push-migrations.ts existed, later ALTER TABLE guard for is_flagged made the audit treat the whole table as tracked
  tokenHash: varchar("token_hash", { length: 64 }).notNull(),
  // migration-safety-ok — predates migration tracking (see tokenHash above)
  tokenPrefix: varchar("token_prefix", { length: 8 }).notNull(),
  scope: varchar("scope", { length: 32 }).notNull().default("unknown"),
  // migration-safety-ok — predates migration tracking (see tokenHash above)
  ip: varchar("ip", { length: 64 }).notNull(),
  userAgent: text("user_agent").notNull().default(""),
  // migration-safety-ok — predates migration tracking (see tokenHash above)
  method: varchar("method", { length: 10 }).notNull(),
  // migration-safety-ok — predates migration tracking (see tokenHash above)
  path: varchar("path", { length: 500 }).notNull(),
  isFlagged: boolean("is_flagged").notNull().default(false),
  deviceUid: varchar("device_uid", { length: 12 }).notNull().default(""),
  deviceLabel: varchar("device_label", { length: 100 }).notNull().default(""),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_atal_hash").on(table.tokenHash),
  index("idx_atal_created").on(table.createdAt),
  index("idx_atal_flagged").on(table.isFlagged),
  index("idx_atal_device").on(table.deviceUid),
]);
export type AdminTokenAccessLogEntry = typeof adminTokenAccessLog.$inferSelect;

export const adminTokenIpWhitelist = pgTable("admin_token_ip_whitelist", {
  id: serial("id").primaryKey(),
  tokenHash: varchar("token_hash", { length: 64 }).notNull(),
  ip: varchar("ip", { length: 64 }).notNull(),
  label: varchar("label", { length: 100 }).notNull().default(""),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_atipwl_hash").on(table.tokenHash),
]);
export type AdminTokenIpWhitelistEntry = typeof adminTokenIpWhitelist.$inferSelect;

export const adminTokenAlertConfig = pgTable("admin_token_alert_config", {
  id: serial("id").primaryKey(),
  uniqueIpThreshold: integer("unique_ip_threshold").notNull().default(8),
  alertEnabled: boolean("alert_enabled").notNull().default(true),
  alertCooldownMinutes: integer("alert_cooldown_minutes").notNull().default(60),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
export type AdminTokenAlertConfig = typeof adminTokenAlertConfig.$inferSelect;

// Task #247 — ops threshold alerting (singleton row).
// Watched by server/ops-alerts.ts every 5 min. Each metric has a threshold and
// a "last alert" timestamp (jsonb keyed by metric name) so that cooldowns
// survive process restart.
export const opsAlertConfig = pgTable("ops_alert_config", {
  id: serial("id").primaryKey(),
  alertEnabled: boolean("alert_enabled").notNull().default(true),
  probePassRateMinPct: integer("probe_pass_rate_min_pct").notNull().default(95),
  webhookBacklogMax: integer("webhook_backlog_max").notNull().default(5),
  queueDepthMax: integer("queue_depth_max").notNull().default(50),
  p95LatencyMaxMs: integer("p95_latency_max_ms").notNull().default(2000),
  dbPoolWaitingMax: integer("db_pool_waiting_max").notNull().default(3),
  sentryFatalMax: integer("sentry_fatal_max").notNull().default(1),
  cooldownMinutes: integer("cooldown_minutes").notNull().default(1440),
  lastAlertAt: jsonb("last_alert_at").$type<Record<string, string>>().notNull().default({}),
  breachingSince: jsonb("breaching_since").$type<Record<string, string | null>>().notNull().default({}),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
export type OpsAlertConfig = typeof opsAlertConfig.$inferSelect;

// Task #1535 — Ops breach history log.
// Records each resolved breach with metric name, label, start/end timestamps,
// and duration. Queried by GET /api/admin/ops/detail (last 7 days) and shown
// in the "Recent breach history" panel on the Ops Dashboard.
// migration-safety-ok: brand-new table created in same migration
export const opsBreachLog = pgTable("ops_breach_log", {
  id: serial("id").primaryKey(),
  metric: text("metric").notNull(),
  label: text("label").notNull(),
  breachStartedAt: timestamp("breach_started_at").notNull(),
  breachEndedAt: timestamp("breach_ended_at").notNull(),
  durationSeconds: integer("duration_seconds").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_ops_breach_log_ended_at").on(table.breachEndedAt),
]);
export type OpsBreachLog = typeof opsBreachLog.$inferSelect;

export const clientPortalTokens = pgTable("client_portal_tokens", {
  id: serial("id").primaryKey(),
  email: text("email").notNull(),
  tokenHash: varchar("token_hash", { length: 64 }).notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  usedAt: timestamp("used_at"),
  // migration-safety-ok: new column with DEFAULT, backfill via DEFAULT on existing rows
  tokenType: text("token_type").notNull().default("verify"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Task #2858 — Referral link sharing. Generated on first referral card view; used as
  // the ?ref= token in booking URLs. Unique across all portal tokens so attribution is unambiguous.
  // migration-safety-ok — nullable, no NOT NULL constraint, safe to add to existing rows
  referralToken: varchar("referral_token", { length: 64 }),
  // migration-safety-ok — nullable first deploy; backfill via DEFAULT 0; SET NOT NULL in next deploy after null rows cleaned
  lastActiveAt: timestamp("last_active_at"),
  loginCount: integer("login_count").default(0),
  // migration-safety-ok: nullable — tracks when the client last read the studio news feed
  lastReadFeedAt: timestamp("last_read_feed_at"),
  // migration-safety-ok: nullable — tracks when the client dismissed the push notification banner (server-side persistence across devices)
  pushNotificationDismissedAt: timestamp("push_notification_dismissed_at"),
  // migration-safety-ok: nullable jsonb column, no DEFAULT required; existing rows get NULL
  milestoneEmailsSent: jsonb("milestone_emails_sent").$type<Record<string, string>>(),
}, (table) => [
  index("idx_client_portal_token_hash").on(table.tokenHash),
  index("idx_client_portal_email").on(table.email),
  uniqueIndex("idx_client_portal_referral_token").on(table.referralToken),
]);

export const insertClientPortalTokenSchema = createInsertSchema(clientPortalTokens).omit({ id: true, usedAt: true, createdAt: true });
export type InsertClientPortalToken = z.infer<typeof insertClientPortalTokenSchema>;
export type ClientPortalToken = typeof clientPortalTokens.$inferSelect;

export const nguConfig = pgTable("ngu_config", {
  id: serial("id").primaryKey(),
  demoSchedulingUrl: text("demo_scheduling_url"),
  demoPageHeadline: text("demo_page_headline").notNull().default("Book a Free 20-Minute Demo"),
  demoPageSubheadline: text("demo_page_subheadline").notNull().default("See exactly how NetGlowUp works — no pitch, just a live walkthrough of your setup."),
  demoCtaLabel: text("demo_cta_label").notNull().default("Book Your Demo Call"),
  nguContactEmail: text("ngu_contact_email"),
  agencyNameOverride: text("agency_name_override"),
  metaPixelId: text("meta_pixel_id"),
  metaAccessToken: text("meta_access_token"),
  gaMeasurementId: text("ga_measurement_id"),
  googleAdsConversionId: text("google_ads_conversion_id"),
  clarityProjectId: text("clarity_project_id"),
  foundingMemberCapacity: integer("founding_member_capacity").notNull().default(10),
  foundingSpotsSold: integer("founding_spots_sold").notNull().default(0),
  socialMediaManagerEmail: text("social_media_manager_email"),
  signupUrl: text("signup_url"),
  metaAdAccountId: text("meta_ad_account_id"),
  googleAnalyticsPropertyId: text("google_analytics_property_id"),
  googleAdsCustomerId: text("google_ads_customer_id"),
  googleAdsDeveloperToken: text("google_ads_developer_token"),
  googleOAuthClientId: text("google_oauth_client_id"),
  googleOAuthClientSecret: text("google_oauth_client_secret"),
  googleOAuthRefreshToken: text("google_oauth_refresh_token"),
  funnelErrorAlertThreshold: integer("funnel_error_alert_threshold").notNull().default(3),
  funnelErrorAlertCooldownMinutes: integer("funnel_error_alert_cooldown_minutes").notNull().default(60),
  funnelErrorAlertEnabled: boolean("funnel_error_alert_enabled").notNull().default(true),
  leadMagnetVslConfig: text("lead_magnet_vsl_config"),
  lastExportAt: text("last_export_at"),
  lastExportData: text("last_export_data"),
  // migration-safety-ok: nullable text — agency admin profile photo URL for the login screen and settings page
  avatarUrl: text("avatar_url"),
  // migration-safety-ok: nullable text — agency admin display name shown on the verified login screen
  agencyAdminName: text("agency_admin_name"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertNguConfigSchema = createInsertSchema(nguConfig).omit({ id: true, updatedAt: true });
export type InsertNguConfig = z.infer<typeof insertNguConfigSchema>;
export type NguConfig = typeof nguConfig.$inferSelect;

export const BOOKING_FUNNEL_STEPS = ["artist", "calendar", "time", "estimate-prompt", "form", "notes", "done"] as const;
export type BookingFunnelStep = typeof BOOKING_FUNNEL_STEPS[number];

export const bookingFunnelEvents = pgTable("booking_funnel_events", {
  id: serial("id").primaryKey(),
  // migration-safety-ok — original column, predates migration tracking
  sessionId: text("session_id").notNull(),
  // migration-safety-ok — original column, predates migration tracking
  stepName: text("step_name").notNull(),
  artistSlug: text("artist_slug"),
  durationMs: integer("duration_ms"),
  referrer: text("referrer"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});
// INC-2026-04-01 / INC-2026-04-25: DO NOT add uniqueIndex here.
// Replit's built-in migration layer reads the ACTUAL dev database (not this file)
// and generates a bare CREATE UNIQUE INDEX with no dedup step for production.
// Production has duplicate (session_id, step_name) rows → deploy blocked every time.
// The index is managed out-of-band by pre-push-migrations.ts (dedup + CREATE UNIQUE INDEX IF NOT EXISTS).
export type BookingFunnelEvent = typeof bookingFunnelEvents.$inferSelect;
export type InsertBookingFunnelEvent = typeof bookingFunnelEvents.$inferInsert;

// Abandonment email capture — Task #95
export const bookingAbandonments = pgTable("booking_abandonments", {
  id: serial("id").primaryKey(),
  sessionId: text("session_id").notNull().unique(),
  email: text("email").notNull(),
  artistSlug: text("artist_slug"),
  lastStep: text("last_step").notNull(),
  capturedAt: timestamp("captured_at").defaultNow().notNull(),
  emailSentAt: timestamp("email_sent_at"),
  bookingCompleted: boolean("booking_completed").default(false).notNull(),
}, (table) => [
  // Partial index for pending abandonment nudge lookups (INC-2026-05-31).
  // getPendingAbandonmentNudges() filters: WHERE email_sent_at IS NULL AND
  // booking_completed = false AND captured_at <= $cutoff ORDER BY captured_at LIMIT $n.
  // Without this index the query full-scans the table on every abandonment-nudge
  // scheduler run (observed 15 s under pool pressure when no index exists).
  // The partial predicate mirrors the WHERE clause exactly, so the planner can
  // perform an O(1) index range scan on captured_at for only the pending rows.
  index("idx_booking_abandonments_pending")
    .on(table.capturedAt)
    .where(sql`email_sent_at IS NULL AND booking_completed = false`),
]);
export const insertBookingAbandonmentSchema = createInsertSchema(bookingAbandonments).omit({ id: true, capturedAt: true, emailSentAt: true, bookingCompleted: true });
export type InsertBookingAbandonment = z.infer<typeof insertBookingAbandonmentSchema>;
export type BookingAbandonment = typeof bookingAbandonments.$inferSelect;

export const commissionRecords = pgTable("commission_records", {
  id: serial("id").primaryKey(),
  bookingId: integer("booking_id").notNull().references(() => bookingAppointments.id, { onDelete: "cascade" }),
  artistId: integer("artist_id").notNull().references(() => artists.id, { onDelete: "cascade" }),
  clientName: text("client_name"),
  bookingDate: text("booking_date"),
  sessionAmount: numeric("session_amount", { precision: 10, scale: 2 }).notNull(),
  tipAmount: numeric("tip_amount", { precision: 10, scale: 2 }),
  artistPayout: numeric("artist_payout", { precision: 10, scale: 2 }).notNull(),
  studioTake: numeric("studio_take", { precision: 10, scale: 2 }).notNull(),
  commissionType: varchar("commission_type", { length: 20 }).notNull(),
  commissionRate: numeric("commission_rate", { precision: 5, scale: 2 }),
  calculatedAt: timestamp("calculated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_commission_records_booking").on(table.bookingId),
  index("idx_commission_records_artist").on(table.artistId),
  index("idx_commission_records_calculated").on(table.calculatedAt),
]);

export const insertCommissionRecordSchema = createInsertSchema(commissionRecords).omit({ id: true, calculatedAt: true });
export type InsertCommissionRecord = z.infer<typeof insertCommissionRecordSchema>;
export type CommissionRecord = typeof commissionRecords.$inferSelect;

export const NGU_ONBOARDING_EMAIL_TYPES = ["ngu_onboarding_1", "ngu_onboarding_2", "ngu_onboarding_3"] as const;
export type NguOnboardingEmailType = typeof NGU_ONBOARDING_EMAIL_TYPES[number];

export const nguOnboardingSends = pgTable("ngu_onboarding_sends", {
  id: serial("id").primaryKey(),
  leadId: integer("lead_id").notNull().references(() => netglowupLeads.id, { onDelete: "cascade" }),
  emailNumber: integer("email_number").notNull(),
  scheduledFor: timestamp("scheduled_for").notNull(),
  sentAt: timestamp("sent_at"),
  error: text("error"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_ngu_onboarding_lead").on(table.leadId),
  index("idx_ngu_onboarding_scheduled").on(table.scheduledFor),
]);

export type NguOnboardingSend = typeof nguOnboardingSends.$inferSelect;
export type InsertNguOnboardingSend = typeof nguOnboardingSends.$inferInsert;

export const nguAnalyticsPeriods = pgTable("ngu_analytics_periods", {
  id: serial("id").primaryKey(),
  name: text("name").notNull().default("Campaign"),
  startedAt: timestamp("started_at", { withTimezone: true }).notNull().defaultNow(),
  endedAt: timestamp("ended_at", { withTimezone: true }),
  rollingDays: integer("rolling_days"),
  snapshot: jsonb("snapshot"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
});
export type NguAnalyticsPeriod = typeof nguAnalyticsPeriods.$inferSelect;

export const webhookEvents = pgTable("webhook_events", {
  id: serial("id").primaryKey(),
  provider: varchar("provider", { length: 20 }).notNull(),
  eventType: varchar("event_type", { length: 100 }).notNull(),
  eventId: varchar("event_id", { length: 200 }).notNull(),
  rawPayload: jsonb("raw_payload").notNull(),
  status: varchar("status", { length: 20 }).notNull().default("received"),
  attemptCount: integer("attempt_count").notNull().default(0),
  lastError: text("last_error"),
  nextRetryAt: timestamp("next_retry_at"),
  processedAt: timestamp("processed_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  uniqueIndex("idx_webhook_events_provider_event_id").on(table.provider, table.eventId),
  index("idx_webhook_events_status").on(table.status),
  index("idx_webhook_events_created_at").on(table.createdAt),
  index("idx_webhook_events_next_retry").on(table.nextRetryAt),
  // Partial index for the webhook retry job: WHERE status IN ('pending', 'failed').
  // Without this, the retry scheduler scans the full webhook_events table on every tick.
  // Must live in schema.ts — not just pre-push-migrations.ts — so Drizzle pushes it to production
  // (see INC-2026-05-25 and INC-2026-05-27-schema-only-partial-index-missing).
  index("idx_webhook_events_retry_pending")
    .on(table.nextRetryAt)
    .where(sql`status IN ('pending', 'failed')`),
]);

export type WebhookEvent = typeof webhookEvents.$inferSelect;

export const insertWebhookEventSchema = createInsertSchema(webhookEvents).omit({ id: true, createdAt: true, processedAt: true, nextRetryAt: true });
export type InsertWebhookEvent = z.infer<typeof insertWebhookEventSchema>;

export const trainingProgress = pgTable("training_progress", {
  id: serial("id").primaryKey(),
  studioId: integer("studio_id").notNull(),
  moduleId: text("module_id").notNull(),
  completedAt: timestamp("completed_at"),
}, (table) => [
  uniqueIndex("idx_training_progress_studio_module").on(table.studioId, table.moduleId),
]);

export type TrainingProgressRow = typeof trainingProgress.$inferSelect;

export const ownerSurveyQuestions = pgTable("owner_survey_questions", {
  id: serial("id").primaryKey(),
  questionKey: varchar("question_key", { length: 60 }).notNull().unique(),
  sortOrder: integer("sort_order").notNull().default(0),
  questionText: text("question_text").notNull(),
  helpText: text("help_text"),
  inputType: varchar("input_type", { length: 20 }).notNull().default("textarea"),
  options: text("options").array(),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_owner_survey_q_sort").on(table.sortOrder),
]);

export type OwnerSurveyQuestion = typeof ownerSurveyQuestions.$inferSelect;

export const ownerSurveyResponses = pgTable("owner_survey_responses", {
  id: serial("id").primaryKey(),
  surveyToken: varchar("survey_token", { length: 64 }).notNull(),
  questionKey: varchar("question_key", { length: 60 }).notNull(),
  responseText: text("response_text"),
  answeredAt: timestamp("answered_at").defaultNow().notNull(),
}, (table) => [
  uniqueIndex("idx_owner_survey_token_key").on(table.surveyToken, table.questionKey),
  index("idx_owner_survey_token").on(table.surveyToken),
]);

export type OwnerSurveyResponse = typeof ownerSurveyResponses.$inferSelect;

// ── Artist Product Survey Responses ──────────────────────────────────────────
// Stores artist product feedback collected via the artist portal survey card.
// One row per (artist_id, question_key) — unique index prevents re-submission.
// studio_id is the studio_config.id for multi-deployment scoping; nullable for
// backward compat (expand phase — backfill + NOT NULL in a future deploy).
// Questions: most_used, least_used, biggest_friction, one_wish, satisfaction.
// migration-safety-ok — brand-new table
export const artistSurveyResponses = pgTable("artist_survey_responses", {
  // migration-safety-ok — brand-new table
  id: serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  artistId: integer("artist_id").notNull(),
  // migration-safety-ok — brand-new table
  artistName: text("artist_name"),
  // migration-safety-ok — brand-new table
  questionKey: varchar("question_key", { length: 60 }).notNull(),
  // migration-safety-ok — brand-new table
  responseText: text("response_text"),
  // migration-safety-ok — brand-new table
  answeredAt: timestamp("answered_at").defaultNow().notNull(),
  // Expand phase: nullable — SET NOT NULL after backfill in a later deploy.
  // migration-safety-ok — brand-new column, no existing rows to null-guard
  studioId: integer("studio_id"),
}, (table) => [
  // All three columns wrapped in sql`` so Drizzle generates the matching
  // expression index (COALESCE(studio_id,0), artist_id, question_key) and
  // does NOT try to drop-recreate on every deploy (INC-2026-07-04 pattern).
  uniqueIndex("idx_artist_survey_studio_artist_question").on(
    sql`COALESCE(${table.studioId}, 0)`,
    sql`${table.artistId}`,
    sql`${table.questionKey}`,
  ),
  index("idx_artist_survey_artist_id").on(table.artistId),
  index("idx_artist_survey_answered_at").on(table.answeredAt),
  index("idx_artist_survey_studio_id").on(table.studioId),
]);

export type ArtistSurveyResponse = typeof artistSurveyResponses.$inferSelect;

export const NGU_VIDEO_STATUSES = ["pending", "generating_storyboard", "generating_frames", "compositing", "complete", "failed"] as const;
export type NguVideoStatus = typeof NGU_VIDEO_STATUSES[number];

export const nguVideoJobs = pgTable("ngu_video_jobs", {
  id: serial("id").primaryKey(),
  icp: text("icp").notNull(),
  prompt: text("prompt").notNull().default(""),
  aspectRatio: text("aspect_ratio").notNull().default("9:16"),
  durationSeconds: integer("duration_seconds").notNull().default(8),
  resolution: text("resolution").notNull().default("720p"),
  status: text("status").notNull().default("pending"),
  statusDetail: text("status_detail"),
  videoPath: text("video_path"),
  thumbnailPath: text("thumbnail_path"),
  storyboardJson: text("storyboard_json"),
  frameStatusJson: text("frame_status_json"),
  framePathsJson: text("frame_paths_json"),
  timingsJson: text("timings_json"),
  errorMessage: text("error_message"),
  falRequestId: text("fal_request_id"),
  falRequestIds: text("fal_request_ids"), // JSON: Array<{sceneOrder:number; requestId:string}> — used for i2v multi-clip jobs
  falModel: text("fal_model"),
  videoUrl: text("video_url"),
  thumbnailUrl: text("thumbnail_url"),
  renderHistoryJson: text("render_history_json"), // JSON: Array<{videoUrl,thumbnailUrl,falModel,completedAt}> — previous renders kept when job is regenerated
  createdAt: timestamp("created_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
}, (table) => [
  index("idx_ngu_video_jobs_status_created").on(table.status, table.createdAt),
]);

export type NguVideoJob = typeof nguVideoJobs.$inferSelect;
export type InsertNguVideoJob = typeof nguVideoJobs.$inferInsert;

export const estimateModelConfig = pgTable("estimate_model_config", {
  id: serial("id").primaryKey(),
  configJson: text("config_json").notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type EstimateModelConfig = typeof estimateModelConfig.$inferSelect;

export const artistAvailabilityEmailLog = pgTable("artist_availability_email_log", {
  id: serial("id").primaryKey(),
  artistSlug: varchar("artist_slug", { length: 100 }).notNull(),
  scheduledAt: timestamp("scheduled_at"),
  sentAt: timestamp("sent_at"),
  recipientCount: integer("recipient_count"),
  cancelledAt: timestamp("cancelled_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_avail_email_log_slug").on(table.artistSlug),
  index("idx_avail_email_log_scheduled").on(table.scheduledAt),
  // Partial index for the scheduler query: WHERE sent_at IS NULL AND cancelled_at IS NULL.
  // Without this, the query does a full table scan and takes 14+ seconds under load
  // (INC-2026-05-28). This index must live in schema.ts — not just pre-push-migrations.ts —
  // so Drizzle generates correct DDL for production (see INC-2026-05-25).
  index("idx_avail_email_log_pending")
    .on(table.scheduledAt)
    .where(sql`sent_at IS NULL AND cancelled_at IS NULL`),
]);
export type ArtistAvailabilityEmailLog = typeof artistAvailabilityEmailLog.$inferSelect;

export const EXPENSE_CATEGORIES = ["Rent", "Supplies", "Software", "Insurance", "Marketing", "Other"] as const;
export type ExpenseCategory = typeof EXPENSE_CATEGORIES[number];

export const studioExpenses = pgTable("studio_expenses", {
  id: serial("id").primaryKey(),
  date: text("date").notNull(),
  category: varchar("category", { length: 50 }).notNull(),
  amount: numeric("amount", { precision: 10, scale: 2 }).notNull(),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertExpenseSchema = createInsertSchema(studioExpenses).omit({ id: true, createdAt: true }).extend({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD"),
  category: z.enum(EXPENSE_CATEGORIES),
  amount: z.coerce.number().positive().max(9999999.99),
  notes: z.string().max(2000).optional().nullable(),
});
export type InsertExpense = z.infer<typeof insertExpenseSchema>;
export type StudioExpense = typeof studioExpenses.$inferSelect;

export const monthlyRevenueGoals = pgTable("monthly_revenue_goals", {
  id: serial("id").primaryKey(),
  year: integer("year").notNull(),
  month: integer("month").notNull(),
  targetAmount: numeric("target_amount", { precision: 10, scale: 2 }).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  uniqueIndex("idx_monthly_revenue_goals_year_month").on(table.year, table.month),
]);

export const insertMonthlyRevenueGoalSchema = createInsertSchema(monthlyRevenueGoals).omit({ id: true, createdAt: true }).extend({
  year: z.number().int().min(2000).max(2100),
  month: z.number().int().min(1).max(12),
  targetAmount: z.coerce.number().min(0),
});
export type InsertMonthlyRevenueGoal = z.infer<typeof insertMonthlyRevenueGoalSchema>;
export type MonthlyRevenueGoal = typeof monthlyRevenueGoals.$inferSelect;

// ── Activity Center ──────────────────────────────────────────────────────────
export const ACTIVITY_EVENT_TYPES = [
  "new_booking",
  "new_contact",
  "deposit_paid",
  "booking_closeout",
  "waitlist_signup",
  "release_form_submitted",
  "artist_available",
  "artist_unavailable",
  "booking_deleted",
] as const;
export type ActivityEventType = typeof ACTIVITY_EVENT_TYPES[number];

export const activityEvents = pgTable("activity_events", {
  id: serial("id").primaryKey(),
  scope: varchar("scope", { length: 20 }).default("studio").notNull(),
  eventType: varchar("event_type", { length: 50 }).notNull(),
  entityId: integer("entity_id"),
  entityType: varchar("entity_type", { length: 50 }),
  summary: text("summary").notNull(),
  metadata: text("metadata"),
  isRead: boolean("is_read").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_activity_events_created").on(table.createdAt),
  index("idx_activity_events_is_read").on(table.isRead),
  index("idx_activity_events_scope").on(table.scope),
  // Composite for the unread-count query on every admin dashboard load: WHERE is_read = $1 AND scope = $2
  index("idx_activity_events_unread").on(table.isRead, table.scope),
]);
export type ActivityEvent = typeof activityEvents.$inferSelect;
export const insertActivityEventSchema = createInsertSchema(activityEvents).omit({ id: true, createdAt: true });
export type InsertActivityEvent = z.infer<typeof insertActivityEventSchema>;

export const ANNOUNCEMENT_PRIORITIES = ["info", "warning", "critical"] as const;
export type AnnouncementPriority = typeof ANNOUNCEMENT_PRIORITIES[number];

export const adminAnnouncements = pgTable("admin_announcements", {
  id: serial("id").primaryKey(),
  scope: varchar("scope", { length: 20 }).default("studio").notNull(),
  title: varchar("title", { length: 200 }).notNull(),
  body: text("body").notNull(),
  priority: varchar("priority", { length: 20 }).default("info").notNull(),
  isRead: boolean("is_read").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  expiresAt: timestamp("expires_at"),
}, (table) => [
  // The unread-count query on every admin dashboard load: WHERE is_read = false AND scope = $1
  index("idx_admin_announcements_unread").on(table.isRead, table.scope),
]);
export type AdminAnnouncement = typeof adminAnnouncements.$inferSelect;
export const insertAnnouncementSchema = createInsertSchema(adminAnnouncements).omit({ id: true, createdAt: true }).extend({
  priority: z.enum(ANNOUNCEMENT_PRIORITIES).default("info"),
});
export type InsertAnnouncement = z.infer<typeof insertAnnouncementSchema>;

// ── Marketing Chat ─────────────────────────────────────────────────────────

export const marketingChatConversations = pgTable("marketing_chat_conversations", {
  id: serial("id").primaryKey(),
  title: varchar("title", { length: 255 }).notNull().default("New Chat"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
export type MarketingChatConversation = typeof marketingChatConversations.$inferSelect;
export const insertMarketingChatConversationSchema = createInsertSchema(marketingChatConversations).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertMarketingChatConversation = z.infer<typeof insertMarketingChatConversationSchema>;

export const marketingChatMessages = pgTable("marketing_chat_messages", {
  id: serial("id").primaryKey(),
  conversationId: integer("conversation_id").notNull().references(() => marketingChatConversations.id, { onDelete: "cascade" }),
  role: varchar("role", { length: 20 }).notNull(),
  content: text("content").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_mkt_chat_messages_conv").on(table.conversationId),
]);
export type MarketingChatMessage = typeof marketingChatMessages.$inferSelect;
export const insertMarketingChatMessageSchema = createInsertSchema(marketingChatMessages).omit({ id: true, createdAt: true });
export type InsertMarketingChatMessage = z.infer<typeof insertMarketingChatMessageSchema>;

export const dbBackupSnapshots = pgTable("db_backup_snapshots", {
  id: serial("id").primaryKey(),
  date: varchar("date", { length: 10 }).notNull(),
  filename: text("filename").notNull(),
  sizeBytes: integer("size_bytes").notNull().default(0),
  durationMs: integer("duration_ms").notNull().default(0),
  success: boolean("success").notNull().default(true),
  errorMsg: text("error_msg"),
  tableCounts: jsonb("table_counts").$type<Record<string, number>>().notNull().default({}),
  // migration-safety-ok — nullable, no NOT NULL constraint, safe to add to existing rows
  integrityChecks: jsonb("integrity_checks").$type<Array<{ name: string; passed: boolean; detail: string }>>(),
  capturedAt: timestamp("captured_at").defaultNow().notNull(),
}, (table) => [
  index("idx_db_backup_snapshots_date").on(table.date),
]);
export type DbBackupSnapshot = typeof dbBackupSnapshots.$inferSelect;
export type InsertDbBackupSnapshot = typeof dbBackupSnapshots.$inferInsert;

export const bookingMessages = pgTable("booking_messages", {
  id: serial("id").primaryKey(),
  bookingId: integer("booking_id").notNull(),
  senderType: varchar("sender_type", { length: 20 }).notNull(), // 'artist' | 'client'
  senderName: text("sender_name").notNull(),
  content: text("content").notNull().default(""),
  imageUrl: varchar("image_url", { length: 512 }),
  clientReadAt: timestamp("client_read_at"),
  staffReadAt: timestamp("staff_read_at"),
  notifiedAt: timestamp("notified_at"),
  followupSentAt: timestamp("followup_sent_at"),
  bump1hSentAt: timestamp("bump_1h_sent_at"),
  bump4hSentAt: timestamp("bump_4h_sent_at"),
  // migration-safety-ok: new table-level addition with DEFAULT; safe single-step
  visibleToClient: boolean("visible_to_client").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_booking_messages_booking").on(table.bookingId),
  index("idx_booking_messages_notified_pending").on(table.createdAt),
  index("idx_booking_messages_bump1h_pending").on(table.createdAt),
  index("idx_booking_messages_bump4h_pending").on(table.createdAt),
]);
export type BookingMessage = typeof bookingMessages.$inferSelect;
export const insertBookingMessageSchema = createInsertSchema(bookingMessages).omit({ id: true, createdAt: true, clientReadAt: true, staffReadAt: true, notifiedAt: true, followupSentAt: true, bump1hSentAt: true, bump4hSentAt: true });
export type InsertBookingMessage = z.infer<typeof insertBookingMessageSchema>;

export const adCreativeDesigns = pgTable("ad_creative_designs", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  layers: jsonb("layers").notNull().default([]),
  canvasSettings: jsonb("canvas_settings").notNull().default({}),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
export type AdCreativeDesign = typeof adCreativeDesigns.$inferSelect;
export const insertAdCreativeDesignSchema = createInsertSchema(adCreativeDesigns).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertAdCreativeDesign = z.infer<typeof insertAdCreativeDesignSchema>;

export const videoScriptRecordings = pgTable("video_script_recordings", {
  id: serial("id").primaryKey(),
  featureSlug: varchar("feature_slug", { length: 100 }).notNull().unique(),
  recordedAt: timestamp("recorded_at").defaultNow().notNull(),
  studioId: varchar("studio_id", { length: 50 }).notNull().default("ngu"),
}, (table) => [
  index("idx_video_script_recordings_slug").on(table.featureSlug),
  index("idx_video_script_recordings_studio").on(table.studioId),
]);
export type VideoScriptRecording = typeof videoScriptRecordings.$inferSelect;
export const insertVideoScriptRecordingSchema = createInsertSchema(videoScriptRecordings).omit({ id: true, recordedAt: true });
export type InsertVideoScriptRecording = z.infer<typeof insertVideoScriptRecordingSchema>;

// ─── Agency Deployment Architecture Tables ─────────────────────────────────

export const deploymentCanaryTiers = pgTable("deployment_canary_tiers", {
  id: serial("id").primaryKey(),
  deploymentId: integer("deployment_id").notNull().references(() => clientDeployments.id, { onDelete: "cascade" }).unique(),
  tier: integer("tier").notNull().default(3),
  notes: text("notes"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_canary_tiers_deployment").on(table.deploymentId),
]);
export type DeploymentCanaryTier = typeof deploymentCanaryTiers.$inferSelect;
export const insertDeploymentCanaryTierSchema = createInsertSchema(deploymentCanaryTiers).omit({ id: true, updatedAt: true }).extend({
  tier: z.number().int().min(1).max(4),
  notes: z.string().max(500).optional().nullable(),
});
export type InsertDeploymentCanaryTier = z.infer<typeof insertDeploymentCanaryTierSchema>;

export const deploymentReleaseLog = pgTable("deployment_release_log", {
  id: serial("id").primaryKey(),
  version: varchar("version", { length: 50 }).notNull(),
  releaseDate: date("release_date").notNull(),
  description: text("description").notNull(),
  affectedDeploymentIds: integer("affected_deployment_ids").array().notNull().default([]),
  deployedBy: varchar("deployed_by", { length: 200 }),
  // migration-safety-ok — new nullable column: optional link to GitHub release page
  githubReleaseUrl: varchar("github_release_url", { length: 500 }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_release_log_date").on(table.releaseDate),
]);
export type DeploymentReleaseLog = typeof deploymentReleaseLog.$inferSelect;
export const insertDeploymentReleaseLogSchema = createInsertSchema(deploymentReleaseLog).omit({ id: true, createdAt: true }).extend({
  version: z.string().min(1).max(50),
  releaseDate: z.string().min(1),
  description: z.string().min(1).max(5000),
  affectedDeploymentIds: z.array(z.number().int()).optional().default([]),
  deployedBy: z.string().max(200).optional().nullable(),
  githubReleaseUrl: z.string().url().max(500).optional().nullable(),
});
export type InsertDeploymentReleaseLog = z.infer<typeof insertDeploymentReleaseLogSchema>;

export const deploymentChecklistItems = pgTable("deployment_checklist_items", {
  id: serial("id").primaryKey(),
  slug: varchar("slug", { length: 200 }).notNull(),
  deploymentId: integer("deployment_id").notNull().default(0),
  checked: boolean("checked").notNull().default(false),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_checklist_slug").on(table.slug),
  uniqueIndex("idx_checklist_slug_dep").on(table.slug, table.deploymentId),
]);
export type DeploymentChecklistItem = typeof deploymentChecklistItems.$inferSelect;

// ─────────────────────────────────────────────────────────────────────────────

export const VIDEO_SCRIPT_UPLOAD_STATUSES = ["pending_review", "approved", "needs_rework"] as const;
export type VideoScriptUploadStatus = typeof VIDEO_SCRIPT_UPLOAD_STATUSES[number];

// ── Studio Staff ──────────────────────────────────────────────────────────────
export const STAFF_ROLES = ["receptionist", "manager", "piercer", "helper"] as const;
export type StaffRole = typeof STAFF_ROLES[number];

export const studioStaff = pgTable("studio_staff", {
  id: serial("id").primaryKey(),
  // migration-safety-ok — original column, predates migration tracking; always present since table creation
  name: varchar("name", { length: 100 }).notNull(),
  email: varchar("email", { length: 255 }),
  role: varchar("role", { length: 30 }).notNull().default("receptionist"),
  // migration-safety-ok — original column, predates migration tracking; always present since table creation
  portalToken: varchar("portal_token", { length: 64 }).notNull().unique(),
  portalTokenRotatedAt: timestamp("portal_token_rotated_at"),
  notes: text("notes"),
  active: boolean("active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export type StudioStaff = typeof studioStaff.$inferSelect;
export const insertStudioStaffSchema = createInsertSchema(studioStaff).omit({ id: true, createdAt: true });
export type InsertStudioStaff = z.infer<typeof insertStudioStaffSchema>;

export const videoScriptUploads = pgTable("video_script_uploads", {
  id: serial("id").primaryKey(),
  featureSlug: varchar("feature_slug", { length: 100 }).notNull().unique(),
  videoObjectPath: text("video_object_path").notNull(),
  videoFileName: text("video_file_name").notNull(),
  videoSize: integer("video_size"),
  videoContentType: varchar("video_content_type", { length: 50 }),
  uploadedAt: timestamp("uploaded_at").defaultNow().notNull(),
  status: varchar("status", { length: 30 }).notNull().default("pending_review"),
  reviewNotes: text("review_notes"),
  reviewedAt: timestamp("reviewed_at"),
  downloadToken: varchar("download_token", { length: 64 }).notNull().unique(),
  smEmailSentAt: timestamp("sm_email_sent_at"),
  studioId: varchar("studio_id", { length: 50 }).notNull().default("ngu"),
}, (table) => [
  index("idx_video_script_uploads_slug").on(table.featureSlug),
  index("idx_video_script_uploads_token").on(table.downloadToken),
]);
export type VideoScriptUpload = typeof videoScriptUploads.$inferSelect;
export const insertVideoScriptUploadSchema = createInsertSchema(videoScriptUploads).omit({ id: true, uploadedAt: true });
export type InsertVideoScriptUpload = z.infer<typeof insertVideoScriptUploadSchema>;

// ── Booking Arrival Status ──────────────────────────────────────────────────
export const bookingArrivalStatuses = pgTable("booking_arrival_statuses", {
  id: serial("id").primaryKey(),
  bookingId: integer("booking_id").notNull(),
  date: varchar("date", { length: 10 }).notNull(),
  status: varchar("status", { length: 20 }).notNull().default("waiting"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  uniqueIndex("idx_arrival_booking_date").on(table.bookingId, table.date),
  index("idx_arrival_booking_id").on(table.bookingId),
]);
export type BookingArrivalStatus = typeof bookingArrivalStatuses.$inferSelect;
export const insertBookingArrivalStatusSchema = createInsertSchema(bookingArrivalStatuses).omit({ id: true, updatedAt: true });
export type InsertBookingArrivalStatus = z.infer<typeof insertBookingArrivalStatusSchema>;

// ── Receptionist Training Progress ─────────────────────────────────────────
export const receptionistTrainingProgress = pgTable("receptionist_training_progress", {
  id: serial("id").primaryKey(),
  portalToken: varchar("portal_token", { length: 64 }).notNull(),
  moduleId: varchar("module_id", { length: 100 }).notNull(),
  completedAt: timestamp("completed_at").defaultNow().notNull(),
}, (table) => [
  uniqueIndex("idx_rx_progress_token_module").on(table.portalToken, table.moduleId),
  index("idx_rx_progress_token").on(table.portalToken),
]);
export type ReceptionistTrainingProgressRow = typeof receptionistTrainingProgress.$inferSelect;

// ── In-app Feature Usage Analytics ─────────────────────────────────────────
// First-party event tracking for product usage (separate from GA4/Meta marketing pixels).
// Stores page views and named high-value events with PII-redacted properties.
export const APP_EVENT_ACTOR_TYPES = ["admin", "artist", "client", "system", "anon"] as const;
export type AppEventActorType = typeof APP_EVENT_ACTOR_TYPES[number];

export const appEvents = pgTable("app_events", {
  id: serial("id").primaryKey(),
  studioId: varchar("studio_id", { length: 100 }).notNull(),
  eventName: varchar("event_name", { length: 80 }).notNull(),
  actorType: varchar("actor_type", { length: 20 }).notNull(),
  actorId: varchar("actor_id", { length: 120 }),
  properties: jsonb("properties").$type<Record<string, unknown>>().default({}).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_app_events_studio_created").on(table.studioId, table.createdAt),
  index("idx_app_events_event").on(table.eventName),
  index("idx_app_events_actor").on(table.actorType, table.actorId),
]);
export const insertAppEventSchema = createInsertSchema(appEvents).omit({ id: true, createdAt: true }).extend({
  studioId: z.string().min(1).max(100),
  eventName: z.string().min(1).max(80),
  actorType: z.enum(APP_EVENT_ACTOR_TYPES),
  actorId: z.string().max(120).optional().nullable(),
  properties: z.record(z.unknown()).optional(),
});
export type InsertAppEvent = z.infer<typeof insertAppEventSchema>;
export type AppEvent = typeof appEvents.$inferSelect;

// ── QLA Acquisition Targets ─────────────────────────────────────────────────
export const qlaTargets = pgTable("qla_targets", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  businessType: text("business_type").notNull(),
  strategy: text("strategy").notNull(),
  location: text("location"),
  status: text("status").default("researching"),
  source: text("source"),
  listingUrl: text("listing_url"),
  revenueMin: integer("revenue_min"),
  revenueMax: integer("revenue_max"),
  askingPrice: integer("asking_price"),
  sdeEstimate: integer("sde_estimate"),
  artistCount: integer("artist_count"),
  bookingMethod: text("booking_method"),
  googleRating: numeric("google_rating", { precision: 2, scale: 1 }),
  reviewCount: integer("review_count"),
  ownerTenureYrs: integer("owner_tenure_yrs"),
  notes: text("notes"),
  score: integer("score").default(0),
  scoreAnswers: jsonb("score_answers").$type<Record<string, number>>().default({}),
  contactName: text("contact_name"),
  contactEmail: text("contact_email"),
  contactPhone: text("contact_phone"),
  isPrepopulated: boolean("is_prepopulated").default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_qla_targets_score").on(table.score),
]);
export const insertQlaTargetSchema = createInsertSchema(qlaTargets).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertQlaTarget = z.infer<typeof insertQlaTargetSchema>;
export type QlaTarget = typeof qlaTargets.$inferSelect;

// ── Support Redirect Codes (one-time opaque codes for support portal redirect) ─
export const supportRedirectCodes = pgTable("support_redirect_codes", {
  id: serial("id").primaryKey(),
  code: varchar("code", { length: 64 }).notNull().unique(),
  resolvedOwnerToken: varchar("resolved_owner_token", { length: 64 }).notNull(),
  used: boolean("used").notNull().default(false),
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_support_redirect_codes_code").on(table.code),
  index("idx_support_redirect_codes_expires_at").on(table.expiresAt),
]);
export const insertSupportRedirectCodeSchema = createInsertSchema(supportRedirectCodes).omit({ id: true, createdAt: true });
export type InsertSupportRedirectCode = z.infer<typeof insertSupportRedirectCodeSchema>;
export type SupportRedirectCode = typeof supportRedirectCodes.$inferSelect;

// ── Lighthouse Audit Runs ───────────────────────────────────────────────────
export const lighthouseRuns = pgTable("lighthouse_runs", {
  id: serial("id").primaryKey(),
  // migration-safety-ok — original column, predates migration tracking
  url: text("url").notNull(),
  // migration-safety-ok — original column, predates migration tracking
  scoresJson: jsonb("scores_json").$type<Record<string, unknown>>().notNull(),
  failCount: integer("fail_count").notNull().default(0),
  warnCount: integer("warn_count").notNull().default(0),
  buildId: text("build_id"),
  runAt: timestamp("run_at").defaultNow().notNull(),
}, (table) => [
  index("idx_lighthouse_runs_run_at").on(table.runAt),
  index("idx_lighthouse_runs_build_id").on(table.buildId),
]);
export type LighthouseRun = typeof lighthouseRuns.$inferSelect;
export const insertLighthouseRunSchema = createInsertSchema(lighthouseRuns).omit({ id: true, runAt: true });
export type InsertLighthouseRun = z.infer<typeof insertLighthouseRunSchema>;

// ── Agency Licensing Contracts ───────────────────────────────────────────────
export const CONTRACT_STATUSES = ["pending", "signed", "active", "expired", "cancelled", "draft"] as const;
export type ContractStatus = typeof CONTRACT_STATUSES[number];

export const CONTRACT_ACTIVITY_EVENTS = [
  "created", "invite_sent", "invite_resent", "link_visited", "signed",
  "status_changed", "expires_at_extended",
] as const;
export type ContractActivityEvent = typeof CONTRACT_ACTIVITY_EVENTS[number];

export const ONBOARDING_STATUSES = ["not_started", "in_progress", "complete"] as const;
export type OnboardingStatus = typeof ONBOARDING_STATUSES[number];

export interface OnboardingChecklistStep {
  done: boolean;
  doneAt: string | null;
  notes?: string | null;
}
export interface OnboardingChecklist {
  seedConfig: OnboardingChecklistStep;
  domainSetup: OnboardingChecklistStep;
  artistDataImport: OnboardingChecklistStep;
  testBooking: OnboardingChecklistStep;
  onboardingCallBooked: OnboardingChecklistStep;
}
export const DEFAULT_ONBOARDING_CHECKLIST: OnboardingChecklist = {
  seedConfig: { done: false, doneAt: null },
  domainSetup: { done: false, doneAt: null },
  artistDataImport: { done: false, doneAt: null },
  testBooking: { done: false, doneAt: null },
  onboardingCallBooked: { done: false, doneAt: null },
};

export const agencyContracts = pgTable("agency_contracts", {
  id: serial("id").primaryKey(),
  token: varchar("token", { length: 64 }).notNull().unique(),
  // migration-safety-ok — nullable: standalone contracts have no lead; drop NOT NULL guard in pre-push-migrations.ts
  nguLeadId: integer("ngu_lead_id").references(() => netglowupLeads.id, { onDelete: "set null" }),
  studioName: text("studio_name").notNull(),
  ownerName: text("owner_name").notNull(),
  email: varchar("email", { length: 320 }).notNull(),
  seatsCount: integer("seats_count").notNull().default(10),
  // migration-safety-ok — new nullable column; ADD COLUMN IF NOT EXISTS guard in pre-push-migrations.ts
  monthlyValue: integer("monthly_value"),
  contractVersion: varchar("contract_version", { length: 20 }).notNull().default("1.0"),
  status: varchar("status", { length: 20 }).notNull().default("draft"),
  signatureData: text("signature_data"),
  signedIp: varchar("signed_ip", { length: 100 }),
  signedUserAgent: text("signed_user_agent"),
  signedAt: timestamp("signed_at"),
  // migration-safety-ok — nullable timestamp, safe to add to existing rows
  tokenExpiresAt: timestamp("token_expires_at"),
  // migration-safety-ok — new nullable column, no backfill required; set to signedAt + 12 months on signing
  expiresAt: timestamp("expires_at"),
  // migration-safety-ok — new nullable column, no backfill required; recorded on first contract page load
  linkVisitedAt: timestamp("link_visited_at"),
  // migration-safety-ok — new nullable column with DB-level DEFAULT; see ADD COLUMN IF NOT EXISTS guard in script/pre-push-migrations.ts
  onboardingStatus: varchar("onboarding_status", { length: 20 }).default("not_started"),
  // migration-safety-ok — new nullable timestamp
  onboardingCompletedAt: timestamp("onboarding_completed_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_agency_contracts_token").on(table.token),
  index("idx_agency_contracts_lead").on(table.nguLeadId),
  index("idx_agency_contracts_status").on(table.status),
  index("idx_agency_contracts_expires_at").on(table.expiresAt),
]);

export const insertAgencyContractSchema = createInsertSchema(agencyContracts).omit({ id: true, createdAt: true }).extend({
  status: z.enum(CONTRACT_STATUSES).optional().default("draft"),
  seatsCount: z.number().int().min(1).max(500).optional().default(10),
  monthlyValue: z.number().int().min(0).optional().nullable(),
  contractVersion: z.string().max(20).optional().default("1.0"),
});
export type InsertAgencyContract = z.infer<typeof insertAgencyContractSchema>;
export type AgencyContract = typeof agencyContracts.$inferSelect;

// ── Contract Activity Log ─────────────────────────────────────────────────────
// migration-safety-ok — brand-new table
export const contractActivityLog = pgTable("contract_activity_log", {
  // migration-safety-ok — brand-new table
  id: serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  contractId: integer("contract_id").notNull().references(() => agencyContracts.id, { onDelete: "cascade" }),
  // migration-safety-ok — brand-new table
  eventType: varchar("event_type", { length: 50 }).notNull(),
  // migration-safety-ok — brand-new table
  metadata: jsonb("metadata").$type<Record<string, unknown>>(),
  // migration-safety-ok — brand-new table
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_contract_activity_log_contract").on(table.contractId),
  index("idx_contract_activity_log_created").on(table.createdAt),
]);

export type ContractActivityLogEntry = typeof contractActivityLog.$inferSelect;

// ── Agency Onboarding Tasks ───────────────────────────────────────────────────
// One row per signed agency contract. Tracks the post-signature provisioning
// checklist and client drip state.
// migration-safety-ok — brand-new table; CREATE TABLE IF NOT EXISTS guard in script/pre-push-migrations.ts
export const agencyOnboardingTasks = pgTable("agency_onboarding_tasks", {
  // migration-safety-ok — brand-new table
  id: serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  contractId: integer("contract_id").notNull().references(() => agencyContracts.id, { onDelete: "cascade" }),
  // Structured checklist JSON; see OnboardingChecklist interface
  // migration-safety-ok — brand-new table
  checklist: jsonb("checklist").$type<OnboardingChecklist>().notNull().default({}),
  // migration-safety-ok — brand-new table
  welcomeEmailSentAt: timestamp("welcome_email_sent_at"),
  // migration-safety-ok — brand-new table
  followUpSentAt: timestamp("follow_up_sent_at"),
  // migration-safety-ok — brand-new table
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // migration-safety-ok — brand-new table
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  uniqueIndex("idx_agency_onboarding_contract").on(table.contractId),
  index("idx_agency_onboarding_created").on(table.createdAt),
]);

export type AgencyOnboardingTask = typeof agencyOnboardingTasks.$inferSelect;
export type InsertAgencyOnboardingTask = typeof agencyOnboardingTasks.$inferInsert;

// Valuation & Fund Raising dashboard config — Task #714
export const NGU_GROWTH_STAGES = ["pre-seed", "seed", "series-a", "series-b"] as const;
export type NguGrowthStage = typeof NGU_GROWTH_STAGES[number];

export const nguValuationConfig = pgTable("ngu_valuation_config", {
  id: serial("id").primaryKey(),
  // Valuation Snapshot
  mrr: integer("mrr").notNull().default(0),
  revenueMultiple: integer("revenue_multiple").notNull().default(10),
  activeStudios: integer("active_studios").notNull().default(0),
  momGrowthRate: integer("mom_growth_rate").notNull().default(0),
  // Fund Raise
  growthStage: varchar("growth_stage", { length: 20 }).notNull().default("pre-seed"),
  targetRaise: integer("target_raise").notNull().default(0),
  preMoney: integer("pre_money").notNull().default(0),
  runwayMonths: integer("runway_months").notNull().default(0),
  // JSONB arrays
  capTable: jsonb("cap_table").notNull().default([]),
  useOfFunds: jsonb("use_of_funds").notNull().default([]),
  whoNotHowPaths: jsonb("who_not_how_paths").notNull().default([]),
  roadmapMilestones: jsonb("roadmap_milestones").notNull().default([]),
  grossMarginPct: integer("gross_margin_pct").default(85),
  sasAdSpend: jsonb("sas_ad_spend").$type<Record<string, Record<string, number>>>().default({}),
  // Cold Outbound Machine efficiency benchmarks — Task #855
  // Persisted as JSON to support fractional rates (e.g. 2.5%) without an extra table.
  coldOutboundRates: jsonb("cold_outbound_rates").$type<{
    emailReplyRate: number;
    linkedinAcceptRate: number;
    callConnectRate: number;
    demoToCloseRate: number;
  }>().default({
    emailReplyRate: 2,
    linkedinAcceptRate: 28,
    callConnectRate: 6,
    demoToCloseRate: 25,
  }),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertNguValuationConfigSchema = createInsertSchema(nguValuationConfig).omit({ id: true, updatedAt: true });
export type InsertNguValuationConfig = z.infer<typeof insertNguValuationConfigSchema>;
export type NguValuationConfig = typeof nguValuationConfig.$inferSelect;

// Valuation history — append-only audit trail of valuation snapshots — Task #720
export const nguValuationHistory = pgTable("ngu_valuation_history", {
  id: serial("id").primaryKey(),
  mrr: integer("mrr").notNull().default(0),
  revenueMultiple: integer("revenue_multiple").notNull().default(10),
  activeStudios: integer("active_studios").notNull().default(0),
  momGrowthRate: integer("mom_growth_rate").notNull().default(0),
  valuation: integer("valuation").notNull().default(0),
  growthStage: varchar("growth_stage", { length: 20 }).notNull().default("pre-seed"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_ngu_valuation_history_created_at").on(table.createdAt),
]);

export const insertNguValuationHistorySchema = createInsertSchema(nguValuationHistory).omit({ id: true, createdAt: true });
export type InsertNguValuationHistory = z.infer<typeof insertNguValuationHistorySchema>;
export type NguValuationHistory = typeof nguValuationHistory.$inferSelect;

// ── Ad Spend Log — Task #806 ────────────────────────────────────────────────
// Records real paid ad campaign spend (date, channel, amount, leads generated)
// so the Software-as-a-Science Unit Economics calculator can seed CAC and the
// Magic Number's S+M spend input from logged data instead of manual estimates.
export const NGU_AD_SPEND_CHANNELS = [
  "meta",
  "google",
  "tiktok",
  "linkedin",
  "youtube",
  "reddit",
  "twitter",
  "other",
] as const;
export type NguAdSpendChannel = typeof NGU_AD_SPEND_CHANNELS[number];

export const nguAdSpendLog = pgTable("ngu_ad_spend_log", {
  id: serial("id").primaryKey(),
  spendDate: timestamp("spend_date").notNull(),
  channel: varchar("channel", { length: 30 }).notNull(),
  campaignName: varchar("campaign_name", { length: 200 }),
  amountCents: integer("amount_cents").notNull().default(0),
  leadsGenerated: integer("leads_generated").notNull().default(0),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_ngu_ad_spend_log_spend_date").on(table.spendDate),
  index("idx_ngu_ad_spend_log_channel").on(table.channel),
]);

export const insertNguAdSpendLogSchema = createInsertSchema(nguAdSpendLog).omit({ id: true, createdAt: true });
export type InsertNguAdSpendLog = z.infer<typeof insertNguAdSpendLogSchema>;
export type NguAdSpendLogRow = typeof nguAdSpendLog.$inferSelect;

export const NGU_CHANNEL_STATUSES = ["active", "testing", "not_started"] as const;
export type NguChannelStatus = typeof NGU_CHANNEL_STATUSES[number];

export const nguMarketingChannelStatus = pgTable("ngu_marketing_channel_status", {
  id: serial("id").primaryKey(),
  token: text("token").notNull(),
  channelName: text("channel_name").notNull(),
  status: varchar("status", { length: 20 }).notNull().default("not_started"),
  notes: text("notes"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  uniqueIndex("idx_ngu_channel_status_token_name").on(table.token, table.channelName),
  index("idx_ngu_channel_status_token").on(table.token),
]);

export const insertNguMarketingChannelStatusSchema = createInsertSchema(nguMarketingChannelStatus).omit({ id: true, updatedAt: true });
export type InsertNguMarketingChannelStatus = z.infer<typeof insertNguMarketingChannelStatusSchema>;
export type NguMarketingChannelStatusRow = typeof nguMarketingChannelStatus.$inferSelect;

// ── Three Levers Substeps — Task #823 ────────────────────────────────────────
export const NGU_LEVERS = ["acquisition", "retention", "monetization"] as const;
export type NguLever = typeof NGU_LEVERS[number];

export const NGU_SUBSTEP_STATUSES = ["not_started", "in_progress", "complete"] as const;
export type NguSubstepStatus = typeof NGU_SUBSTEP_STATUSES[number];

export const nguLeverSubsteps = pgTable("ngu_lever_substeps", {
  id: serial("id").primaryKey(),
  lever: varchar("lever", { length: 20 }).notNull(),
  title: varchar("title", { length: 200 }).notNull(),
  description: text("description"),
  goal: varchar("goal", { length: 500 }),
  status: varchar("status", { length: 20 }).notNull().default("not_started"),
  owner: varchar("owner", { length: 100 }),
  notes: text("notes"),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_ngu_lever_substeps_lever").on(table.lever),
  index("idx_ngu_lever_substeps_status").on(table.status),
]);

export const insertNguLeverSubstepSchema = createInsertSchema(nguLeverSubsteps).omit({ id: true, createdAt: true, updatedAt: true }).extend({
  lever: z.enum(NGU_LEVERS),
  title: z.string().min(1).max(200),
  description: z.string().max(2000).optional().nullable(),
  goal: z.string().max(500).optional().nullable(),
  status: z.enum(NGU_SUBSTEP_STATUSES).optional().default("not_started"),
  owner: z.string().max(100).optional().nullable(),
  notes: z.string().max(5000).optional().nullable(),
  sortOrder: z.number().int().optional().default(0),
});
export type InsertNguLeverSubstep = z.infer<typeof insertNguLeverSubstepSchema>;
export type NguLeverSubstep = typeof nguLeverSubsteps.$inferSelect;

// ── NGU Funnel Error Log ─────────────────────────────────────────────────────
export const NGU_FUNNEL_ERROR_TYPES = [
  "form_submission_500",
  "db_unavailable",
  "email_queue_crash",
  "probe_timeout",
] as const;
export type NguFunnelErrorType = typeof NGU_FUNNEL_ERROR_TYPES[number];

export const nguFunnelErrors = pgTable("ngu_funnel_errors", {
  id: serial("id").primaryKey(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  errorType: varchar("error_type", { length: 50 }).notNull(),
  utmSource: varchar("utm_source", { length: 200 }),
  utmCampaign: varchar("utm_campaign", { length: 200 }),
  funnelSessionId: varchar("funnel_session_id", { length: 64 }),
  detail: text("detail"),
}, (table) => [
  index("idx_ngu_funnel_errors_created_at").on(table.createdAt),
  index("idx_ngu_funnel_errors_error_type").on(table.errorType),
]);

export const insertNguFunnelErrorSchema = createInsertSchema(nguFunnelErrors).omit({ id: true, createdAt: true }).extend({
  errorType: z.enum(NGU_FUNNEL_ERROR_TYPES),
  utmSource: z.string().max(200).optional().nullable(),
  utmCampaign: z.string().max(200).optional().nullable(),
  funnelSessionId: z.string().max(64).optional().nullable(),
  detail: z.string().max(1000).optional().nullable(),
});
export type InsertNguFunnelError = z.infer<typeof insertNguFunnelErrorSchema>;
export type NguFunnelError = typeof nguFunnelErrors.$inferSelect;

// ── ICP Pain Point Survey ────────────────────────────────────────────────────
export const nguPainSurveys = pgTable("ngu_pain_surveys", {
  id:                serial("id").primaryKey(),
  // migration-safety-ok — brand-new table; CREATE TABLE IF NOT EXISTS block in script/pre-push-migrations.ts already covers these NOT NULL columns, but a later ALTER TABLE guard for another table makes the drift audit's table-name matching overly broad here
  icpSlug:           varchar("icp_slug",          { length: 64 }).notNull(),
  // migration-safety-ok — brand-new table (see icpSlug above)
  painPointId:       varchar("pain_point_id",     { length: 64 }).notNull(),
  // migration-safety-ok — brand-new table (see icpSlug above)
  featureMapping:    varchar("feature_mapping",   { length: 64 }).notNull(),
  otherText:         text("other_text"),
  respondentName:    varchar("respondent_name",   { length: 255 }),
  respondentEmail:   varchar("respondent_email",  { length: 255 }),
  qualificationData: jsonb("qualification_data"),
  createdAt:         timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_ngu_pain_surveys_icp").on(table.icpSlug),
  index("idx_ngu_pain_surveys_feature").on(table.featureMapping),
]);

export const insertNguPainSurveySchema = createInsertSchema(nguPainSurveys).omit({ id: true, createdAt: true });
export type InsertNguPainSurvey = z.infer<typeof insertNguPainSurveySchema>;
export type NguPainSurveyRow = typeof nguPainSurveys.$inferSelect;

// ── Booking Draft Recovery — Task #910 ───────────────────────────────────────
// Persists partial booking form state server-side so the scheduler can send a
// one-click resume email 30 minutes after the user goes quiet.
export const bookingDrafts = pgTable("booking_drafts", {
  id: serial("id").primaryKey(),
  token: varchar("token", { length: 64 }).notNull().unique(),
  email: text("email").notNull(),
  artistSlug: text("artist_slug"),
  lastStep: varchar("last_step", { length: 50 }).notNull().default("service-type"),
  state: jsonb("state").notNull().$type<Record<string, unknown>>(),
  recoverySentAt: timestamp("recovery_sent_at"),
  convertedAt: timestamp("converted_at"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_booking_drafts_token").on(table.token),
  index("idx_booking_drafts_email").on(table.email),
  index("idx_booking_drafts_recovery").on(table.recoverySentAt, table.convertedAt, table.updatedAt),
  // Partial index for the getPendingBookingDraftRecoveries() scan:
  //   WHERE recovery_sent_at IS NULL AND converted_at IS NULL AND updated_at BETWEEN ...
  // The plain composite above is not selective when most rows have NULL for the first two
  // columns — PostgreSQL seq-scans the table and causes 22–60 s queries on every
  // 10-min booking-draft-recovery tick. This partial index indexes only the unprocessed
  // draft rows (typically a tiny fraction of the table), making the updatedAt range scan O(1).
  index("idx_booking_drafts_recovery_pending")
    .on(table.updatedAt)
    .where(sql`recovery_sent_at IS NULL AND converted_at IS NULL`),
]);

export const insertBookingDraftSchema = createInsertSchema(bookingDrafts).omit({ id: true, createdAt: true, updatedAt: true, recoverySentAt: true, convertedAt: true });
export type InsertBookingDraft = z.infer<typeof insertBookingDraftSchema>;
export type BookingDraftRecord = typeof bookingDrafts.$inferSelect;

// ── Channel Maturity Matrix — Task #852 ──────────────────────────────────────
export const CHANNEL_MATRIX_RATINGS = ["red", "yellow", "green"] as const;
export type ChannelMatrixRatingValue = typeof CHANNEL_MATRIX_RATINGS[number];

export const channelMatrixRatings = pgTable("channel_matrix_ratings", {
  id: serial("id").primaryKey(),
  token: varchar("token", { length: 128 }).notNull(),
  channelKey: varchar("channel_key", { length: 64 }).notNull(),
  levelKey: varchar("level_key", { length: 64 }).notNull(),
  rating: varchar("rating", { length: 10 }).notNull().default("red"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  uniqueIndex("idx_channel_matrix_ratings_unique").on(table.token, table.channelKey, table.levelKey),
  index("idx_channel_matrix_ratings_token").on(table.token),
]);

export const insertChannelMatrixRatingSchema = createInsertSchema(channelMatrixRatings).omit({ id: true, updatedAt: true }).extend({
  channelKey: z.string().min(1).max(64),
  levelKey: z.string().min(1).max(64),
  rating: z.enum(CHANNEL_MATRIX_RATINGS),
});
export type InsertChannelMatrixRating = z.infer<typeof insertChannelMatrixRatingSchema>;
export type ChannelMatrixRating = typeof channelMatrixRatings.$inferSelect;

// ── Buyer Funnel Health — Task #1054 ─────────────────────────────────────────
export const FUNNEL_STAGE_KEYS = ["awareness", "consideration", "decision", "conversion"] as const;
export type FunnelStageKey = typeof FUNNEL_STAGE_KEYS[number];
export const FUNNEL_STAGE_RATINGS = ["red", "yellow", "green"] as const;
export type FunnelStageRatingValue = typeof FUNNEL_STAGE_RATINGS[number];

export const funnelStageRatings = pgTable("funnel_stage_ratings", {
  id: serial("id").primaryKey(),
  token: varchar("token", { length: 128 }).notNull(),
  stageKey: varchar("stage_key", { length: 32 }).notNull(),
  rating: varchar("rating", { length: 10 }).notNull().default("red"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  uniqueIndex("idx_funnel_stage_ratings_unique").on(table.token, table.stageKey),
  index("idx_funnel_stage_ratings_token").on(table.token),
]);

export const insertFunnelStageRatingSchema = createInsertSchema(funnelStageRatings).omit({ id: true, updatedAt: true }).extend({
  stageKey: z.enum(FUNNEL_STAGE_KEYS),
  rating: z.enum(FUNNEL_STAGE_RATINGS),
});
export type InsertFunnelStageRating = z.infer<typeof insertFunnelStageRatingSchema>;
export type FunnelStageRating = typeof funnelStageRatings.$inferSelect;

// ── Perf Samples — Task #248 ────────────────────────────────────────────────
// Per-minute aggregate of request timings, persisted so the slowest-endpoints
// tile on /admin/ops/:token survives server restarts and can show 1h / 24h /
// 7d windows. Storage is bounded by FIFO eviction (cap ~10k rows).
export const perfSamples = pgTable("perf_samples", {
  id: serial("id").primaryKey(),
  bucketStart: timestamp("bucket_start").notNull(),
  method: varchar("method", { length: 10 }).notNull(),
  path: varchar("path", { length: 500 }).notNull(),
  count: integer("count").notNull().default(0),
  totalMs: integer("total_ms").notNull().default(0),
  maxMs: integer("max_ms").notNull().default(0),
  p95Ms: integer("p95_ms").notNull().default(0),
  errorCount: integer("error_count").notNull().default(0),
}, (table) => [
  uniqueIndex("idx_perf_samples_unique").on(table.bucketStart, table.method, table.path),
  index("idx_perf_samples_bucket_start").on(table.bucketStart),
]);
export const insertPerfSampleSchema = createInsertSchema(perfSamples).omit({ id: true });
export type InsertPerfSample = z.infer<typeof insertPerfSampleSchema>;
export type PerfSample = typeof perfSamples.$inferSelect;

// ── Incident Notes — Task #909 public status page ───────────────────────────
// Short admin-posted notes surfaced on /status for clients and artists.
// No internal data (IPs, stack traces, env details) is stored here.
export const incidentNotes = pgTable("incident_notes", {
  id: serial("id").primaryKey(),
  message: text("message").notNull(),
  postedBy: varchar("posted_by", { length: 120 }).notNull().default("Admin"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_incident_notes_created_at").on(table.createdAt),
]);
export const insertIncidentNoteSchema = createInsertSchema(incidentNotes).omit({ id: true, createdAt: true });
export type InsertIncidentNote = z.infer<typeof insertIncidentNoteSchema>;
export type IncidentNote = typeof incidentNotes.$inferSelect;

// ── AI Usage — Task #911 ─────────────────────────────────────────────────────
// Every OpenAI-backed call records tokens, model, feature, and estimated cost.
// The JSONL file (attached_assets/ai_usage_log.jsonl) remains the fast append
// path; this table is the authoritative queryable record.
export const aiUsage = pgTable("ai_usage", {
  id: serial("id").primaryKey(),
  ts: timestamp("ts").defaultNow().notNull(),
  feature: varchar("feature", { length: 100 }).notNull(),
  model: varchar("model", { length: 100 }).notNull(),
  promptTokens: integer("prompt_tokens"),
  completionTokens: integer("completion_tokens"),
  imageCount: integer("image_count"),
  audioDurationSec: real("audio_duration_sec"),
  estimatedCostUsd: real("estimated_cost_usd").notNull().default(0),
  error: boolean("error").notNull().default(false),
  durationMs: integer("duration_ms"),
}, (table) => [
  index("idx_ai_usage_ts").on(table.ts),
  index("idx_ai_usage_feature_ts").on(table.feature, table.ts),
]);

export const insertAiUsageSchema = createInsertSchema(aiUsage).omit({ id: true, ts: true });
export type InsertAiUsage = z.infer<typeof insertAiUsageSchema>;
export type AiUsageRow = typeof aiUsage.$inferSelect;

// ── Secret Rotations — Task #920 ─────────────────────────────────────────────
// Ledger of every third-party secret: when it was last rotated, who owns it,
// and when it is due for rotation. Secret VALUES are never stored here.
export const secretRotations = pgTable("secret_rotations", {
  id: serial("id").primaryKey(),
  secretName: varchar("secret_name", { length: 120 }).notNull().unique(),
  provider: varchar("provider", { length: 100 }).notNull(),
  owner: varchar("owner", { length: 120 }).notNull().default("Admin"),
  lastRotatedAt: timestamp("last_rotated_at"),
  rotateEveryDays: integer("rotate_every_days").notNull().default(90),
  notes: text("notes"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_secret_rotations_provider").on(table.provider),
]);

export const insertSecretRotationSchema = createInsertSchema(secretRotations).omit({ id: true, updatedAt: true }).extend({
  secretName: z.string().min(1).max(120),
  provider: z.string().min(1).max(100),
  owner: z.string().max(120).optional(),
  lastRotatedAt: z.string().datetime({ offset: true }).nullable().optional(),
  rotateEveryDays: z.number().int().min(1).max(3650).optional(),
  notes: z.string().max(2000).nullable().optional(),
});
export type InsertSecretRotation = z.infer<typeof insertSecretRotationSchema>;
export type SecretRotation = typeof secretRotations.$inferSelect;

// ── Idempotency Keys — Task #901 ─────────────────────────────────────────────
export const idempotencyKeys = pgTable("idempotency_keys", {
  id: serial("id").primaryKey(),
  key: varchar("key", { length: 200 }).notNull(),
  scope: varchar("scope", { length: 50 }).notNull(),
  responseStatus: integer("response_status").notNull(),
  responseBody: jsonb("response_body").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  expiresAt: timestamp("expires_at").notNull(),
}, (table) => [
  uniqueIndex("idx_idempotency_key_scope").on(table.key, table.scope),
  index("idx_idempotency_expires_at").on(table.expiresAt),
]);
export type IdempotencyKey = typeof idempotencyKeys.$inferSelect;

// ── A/B Experiment Events — Task #924 ────────────────────────────────────────
// Lightweight experiment framework: one row per exposure, one row per
// conversion. visitorId is the opaque cookie-based visitor identifier.
// Both tables are append-only; results are computed at query time.
export const experimentExposures = pgTable("experiment_exposures", {
  id: serial("id").primaryKey(),
  experimentKey: varchar("experiment_key", { length: 100 }).notNull(),
  variant: varchar("variant", { length: 50 }).notNull(),
  visitorId: varchar("visitor_id", { length: 64 }).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_exp_exposures_key").on(table.experimentKey),
  index("idx_exp_exposures_visitor").on(table.visitorId, table.experimentKey),
  index("idx_exp_exposures_created_at").on(table.createdAt),
]);
export const insertExperimentExposureSchema = createInsertSchema(experimentExposures).omit({ id: true, createdAt: true });
export type InsertExperimentExposure = z.infer<typeof insertExperimentExposureSchema>;
export type ExperimentExposure = typeof experimentExposures.$inferSelect;

export const experimentConversions = pgTable("experiment_conversions", {
  id: serial("id").primaryKey(),
  experimentKey: varchar("experiment_key", { length: 100 }).notNull(),
  variant: varchar("variant", { length: 50 }).notNull(),
  visitorId: varchar("visitor_id", { length: 64 }).notNull(),
  goal: varchar("goal", { length: 100 }).notNull().default("default"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_exp_conversions_key").on(table.experimentKey),
  index("idx_exp_conversions_visitor").on(table.visitorId, table.experimentKey),
  index("idx_exp_conversions_created_at").on(table.createdAt),
]);
export const insertExperimentConversionSchema = createInsertSchema(experimentConversions).omit({ id: true, createdAt: true });
export type InsertExperimentConversion = z.infer<typeof insertExperimentConversionSchema>;
export type ExperimentConversion = typeof experimentConversions.$inferSelect;

// ── ICP Hot Buttons — awareness-stage tracker (Task #1153) ───────────────────
export const nguIcpHotButtons = pgTable("ngu_icp_hot_buttons", {
  id:                serial("id").primaryKey(),
  icpSlug:           varchar("icp_slug",            { length: 64 }).notNull(),
  rank:              integer("rank").notNull(),
  hotButton:         text("hot_button").notNull().default(""),
  emotionalFraming:  text("emotional_framing").notNull().default(""),
  googleSearchPhrase: text("google_search_phrase").notNull().default(""),
  contentAngle:      text("content_angle").notNull().default(""),
  leadMagnetIdea:    text("lead_magnet_idea"),
  updatedAt:         timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_ngu_icp_hot_buttons_icp").on(table.icpSlug),
  uniqueIndex("idx_ngu_icp_hot_buttons_icp_rank").on(table.icpSlug, table.rank),
]);

export const insertNguIcpHotButtonSchema = createInsertSchema(nguIcpHotButtons).omit({ id: true, updatedAt: true }).extend({
  icpSlug: z.string().min(1).max(64),
  rank: z.number().int().min(1).max(5),
  hotButton: z.string().max(300).optional().default(""),
  emotionalFraming: z.string().max(500).optional().default(""),
  googleSearchPhrase: z.string().max(300).optional().default(""),
  contentAngle: z.string().max(500).optional().default(""),
  leadMagnetIdea: z.string().max(2000).optional().nullable(),
});
export type InsertNguIcpHotButton = z.infer<typeof insertNguIcpHotButtonSchema>;
export type NguIcpHotButton = typeof nguIcpHotButtons.$inferSelect;

// ── Slow-Query Log — persisted ring entries (Task #1190) ─────────────────────
// Allows the nightly digest to reflect a true 24-hour window even across
// server restarts.  The in-memory ring is cleared after each digest; this
// table is the durable source of truth for the same window.
export const slowQueryLog = pgTable("slow_query_log", {
  id:            serial("id").primaryKey(),
  sqlFingerprint: text("sql_fingerprint").notNull(),
  durationMs:    integer("duration_ms").notNull(),
  capturedAt:    timestamp("captured_at").defaultNow().notNull(),
}, (table) => [
  index("idx_slow_query_log_captured_at").on(table.capturedAt),
]);

export const insertSlowQueryLogSchema = createInsertSchema(slowQueryLog).omit({ id: true });
export type InsertSlowQueryLog = z.infer<typeof insertSlowQueryLogSchema>;
export type SlowQueryLogRow = typeof slowQueryLog.$inferSelect;

// ── NGU Tool Clicks (Task #1467) ─────────────────────────────────────────────
// One row per click on a free-tool link inside a drip email. Captured via
// GET /api/netglowup/tool-click/:magnetId/:token which logs then redirects.
// Used by /api/admin/netglowup/tool-click-stats to power the admin dashboard.
export const nguToolClicks = pgTable("ngu_tool_clicks", {
  id:        serial("id").primaryKey(),
  magnetId:  varchar("magnet_id", { length: 60 }).notNull(),  // LEAD_MAGNETS key, e.g. "no_show_calculator"
  segment:   varchar("segment", { length: 30 }),              // lead.segment at click time
  dripSlot:  varchar("drip_slot", { length: 5 }),             // "d1" | "d2" | "d3" | "d5"
  leadId:    integer("lead_id"),                              // FK to netglowup_leads (nullable — unknown leads)
  clickedAt: timestamp("clicked_at").defaultNow().notNull(),
}, (table) => [
  index("idx_ngu_tool_clicks_magnet_id").on(table.magnetId),
  index("idx_ngu_tool_clicks_clicked_at").on(table.clickedAt),
  index("idx_ngu_tool_clicks_lead_id").on(table.leadId),
]);

export type NguToolClick = typeof nguToolClicks.$inferSelect;
export const insertNguToolClickSchema = createInsertSchema(nguToolClicks).omit({ id: true });
export type InsertNguToolClick = z.infer<typeof insertNguToolClickSchema>;

// ── NGU Magnet Event Counters (Task #1336) ────────────────────────────────────
// Lightweight per-slug/per-event-type counters (page_view, cta_click).
// Upserted (+1) on every client-side event; read by Hub KPI endpoint to compute
// real top-of-funnel rates: ctaClickRate = cta_clicks/page_views,
//                            gateSubmitRate = leads/cta_clicks.
export const nguMagnetStats = pgTable("ngu_magnet_stats", {
  slug:      text("slug").notNull(),
  eventType: text("event_type").notNull(),
  count:     integer("count").notNull().default(0),
}, (table) => [
  uniqueIndex("uidx_ngu_magnet_stats_slug_event").on(table.slug, table.eventType),
]);
export type NguMagnetStat = typeof nguMagnetStats.$inferSelect;

// ── Release Form Overrides (Task #1630) ───────────────────────────────────────
// Shop owners can customise (hide, remove, or re-text) any health question or
// provision clause from the hardcoded templates.  A missing row means "use the
// default".  Status = removed is the hardest delete — no DB row is ever purged.
export const RELEASE_FORM_OVERRIDE_STATUSES = ["active", "hidden", "removed"] as const;
export type ReleaseFormOverrideStatus = typeof RELEASE_FORM_OVERRIDE_STATUSES[number];

// migration-safety-ok — brand new table, no existing rows, no ALTER needed
export const releaseFormOverrides = pgTable("release_form_overrides", {
  id:          serial("id").primaryKey(),
  serviceType: varchar("service_type", { length: 30 }).notNull(),
  itemType:    varchar("item_type", { length: 20 }).notNull(),
  itemId:      varchar("item_id", { length: 60 }).notNull(),
  status:      varchar("status", { length: 20 }).notNull().default("active"),
  customText:  text("custom_text"),
  customTitle: text("custom_title"),
  sortOrder:   integer("sort_order"),
  createdAt:   timestamp("created_at").defaultNow().notNull(),
  updatedAt:   timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uidx_rfo_service_type_item").on(table.serviceType, table.itemType, table.itemId),
]);

export const insertReleaseFormOverrideSchema = createInsertSchema(releaseFormOverrides).omit({ id: true, createdAt: true, updatedAt: true }).extend({
  serviceType: z.enum(RELEASE_FORM_SERVICE_TYPES),
  itemType:    z.enum(["health_question", "provision"]),
  status:      z.enum(RELEASE_FORM_OVERRIDE_STATUSES).optional().default("active"),
  customText:  z.string().max(4000).nullable().optional(),
  customTitle: z.string().max(500).nullable().optional(),
  sortOrder:   z.number().int().nullable().optional(),
});
export type InsertReleaseFormOverride = z.infer<typeof insertReleaseFormOverrideSchema>;
export type ReleaseFormOverride = typeof releaseFormOverrides.$inferSelect;

// ── Studio Upgrade Prompts (Task #1417) ─────────────────────────────────────
// Tracks PLG deposit milestones. One row per prompt type; created the first
// time a milestone threshold is hit. Dismissed-once: once dismissedAt is set
// the banner is never shown again, even if the deposit count increases.
export const UPGRADE_PROMPT_TYPES = ["first_deposit", "three_deposits_or_14_days"] as const;
export type UpgradePromptType = typeof UPGRADE_PROMPT_TYPES[number];

// migration-safety-ok — brand new table, no existing rows, no ALTER needed
export const studioUpgradePrompts = pgTable("studio_upgrade_prompts", {
  id:           serial("id").primaryKey(),
  // studio_key scopes rows to a single deployment — always "default" in single-tenant deploys;
  // future multi-tenant or white-label deploys can use the studio's domain or config id.
  // migration-safety-ok: has DEFAULT 'default', so existing rows are backfilled automatically
  studioKey:    text("studio_key").notNull().default("default"),
  // migration-safety-ok — original column, predates migration tracking
  promptType:   varchar("prompt_type", { length: 40 }).notNull(),
  depositCount: integer("deposit_count").notNull().default(1),
  triggeredAt:  timestamp("triggered_at").defaultNow().notNull(),
  dismissedAt:  timestamp("dismissed_at"),
}, (table) => [
  uniqueIndex("uidx_studio_upgrade_prompts_key_type").on(table.studioKey, table.promptType),
]);
export type StudioUpgradePrompt = typeof studioUpgradePrompts.$inferSelect;

// ── Studio Self-Serve Signups (Task #1416) ────────────────────────────────────
// One row per studio that completes the self-serve signup flow (Tier 0 or Tier 1).
// Created at the moment the studio submits the signup form; activatedAt is set
// when Stripe confirms payment via the checkout.session.completed webhook.
// Tier 0 = $49/mo (hosted booking page only), Tier 1 = $79/mo (embed widget).
// migration-safety-ok — brand new table, no existing rows, no ALTER needed
export const STUDIO_SIGNUP_TIERS = [0, 1] as const;
export type StudioSignupTier = typeof STUDIO_SIGNUP_TIERS[number];

export const studioSignups = pgTable("studio_signups", {
  id:              serial("id").primaryKey(),
  // migration-safety-ok — brand-new table, no existing rows, no ALTER guard needed
  email:           varchar("email", { length: 255 }).notNull().unique(),
  // migration-safety-ok — brand-new table, no existing rows, no ALTER guard needed
  studioName:      text("studio_name").notNull(),
  instagramHandle: text("instagram_handle"),
  // migration-safety-ok — brand-new table, no existing rows, no ALTER guard needed
  slug:            varchar("slug", { length: 100 }).notNull().unique(),
  // migration-safety-ok — brand-new table, no existing rows, no ALTER guard needed
  tier:            integer("tier").notNull(),          // 0 or 1
  // migration-safety-ok — brand-new table, no existing rows, no ALTER guard needed
  token:           varchar("token", { length: 64 }).notNull().unique(), // opaque id, sent to Stripe as client_reference_id with "ss_" prefix
  stripeSessionId: varchar("stripe_session_id", { length: 200 }),
  activatedAt:     timestamp("activated_at"),
  leadId:          integer("lead_id"),                 // FK to netglowup_leads (nullable)
  // Task #2530 — trial expiry countdown emails (7/3/1 day before trial ends).
  // Nullable — set when studio is placed on a trial period. ADD COLUMN IF NOT EXISTS guard in script/pre-push-migrations.ts.
  // migration-safety-ok — nullable, no NOT NULL constraint
  trialEndsAt:             timestamp("trial_ends_at"),
  // Tracks which countdown days (7, 3, 1) have already been sent to prevent duplicates on restart.
  // migration-safety-ok — nullable, no NOT NULL constraint
  trialCountdownSentDays:  integer("trial_countdown_sent_days").array(),
  // migration-safety-ok — brand-new table, no existing rows, no ALTER guard needed
  createdAt:       timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_studio_signups_email").on(table.email),
  index("idx_studio_signups_token").on(table.token),
  index("idx_studio_signups_slug").on(table.slug),
]);

export type StudioSignup = typeof studioSignups.$inferSelect;

export const insertStudioSignupSchema = createInsertSchema(studioSignups).omit({ id: true, createdAt: true }).extend({
  email:           z.string().email().max(255),
  studioName:      z.string().min(1).max(200),
  instagramHandle: z.string().max(100).optional().nullable(),
  slug:            z.string().min(2).max(100).regex(/^[a-z0-9-]+$/),
  tier:            z.union([z.literal(0), z.literal(1)]),
  token:           z.string().min(32).max(64),
  stripeSessionId: z.string().max(200).optional().nullable(),
  activatedAt:     z.coerce.date().optional().nullable(),
  leadId:          z.number().int().optional().nullable(),
});
export type InsertStudioSignup = z.infer<typeof insertStudioSignupSchema>;

// ── Review link click tracking ───────────────────────────────────────────────
// One row per review email sent. firstClickedAt=null until the client taps the
// link, then set on first hit. clickCount handles multiple taps.
// migration-safety-ok — brand-new table, no existing rows, no ALTER guard needed
export const reviewLinkClicks = pgTable("review_link_clicks", {
  // migration-safety-ok — brand-new table
  id:              serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  token:           varchar("token", { length: 64 }).notNull().unique(),
  // migration-safety-ok — brand-new table — 'healing' | 'nps'
  source:          text("source").notNull(),
  // migration-safety-ok — brand-new table — healing_journeys.id or booking_appointments.id
  entityId:        integer("entity_id").notNull(),
  // migration-safety-ok — brand-new table — captured at send time so old links survive URL changes
  googleReviewUrl: text("google_review_url").notNull(),
  // migration-safety-ok — brand-new table
  firstClickedAt:  timestamp("first_clicked_at"),
  // migration-safety-ok — brand-new table
  clickCount:      integer("click_count").notNull().default(0),
  // migration-safety-ok — brand-new table
  createdAt:       timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_review_link_clicks_token").on(table.token),
  index("idx_review_link_clicks_entity").on(table.source, table.entityId),
]);

export type ReviewLinkClick = typeof reviewLinkClicks.$inferSelect;

// ── Task #1821: Persistent DB pool stats log ──────────────────────────────────
// One row per 5-minute poll; pruned to 7 days by the nightly backup scheduler.
// migration-safety-ok — brand-new table, no existing rows, no ALTER guard needed
export const poolStatsLog = pgTable("pool_stats_log", {
  id:           serial("id").primaryKey(),
  // migration-safety-ok — brand-new table, no existing rows, no ALTER guard needed
  recordedAt:   timestamp("recorded_at").defaultNow().notNull(),
  // migration-safety-ok — brand-new table, no existing rows, no ALTER guard needed
  totalCount:   integer("total_count").notNull(),
  // migration-safety-ok — brand-new table, no existing rows, no ALTER guard needed
  idleCount:    integer("idle_count").notNull(),
  // migration-safety-ok — brand-new table, no existing rows, no ALTER guard needed
  waitingCount: integer("waiting_count").notNull(),
  // migration-safety-ok — brand-new table, no existing rows, no ALTER guard needed
  inFlightJobs: text("in_flight_jobs").notNull().default(""),
}, (table) => [
  index("idx_pool_stats_log_recorded_at").on(table.recordedAt),
]);

export type PoolStatsLog = typeof poolStatsLog.$inferSelect;

export const insertPoolStatsLogSchema = createInsertSchema(poolStatsLog).omit({ id: true, recordedAt: true }).extend({
  totalCount:   z.number().int().min(0),
  idleCount:    z.number().int().min(0),
  waitingCount: z.number().int().min(0),
  inFlightJobs: z.string().optional(),
});
export type InsertPoolStatsLog = z.infer<typeof insertPoolStatsLogSchema>;

// ── Studio Feature Nudge Tracking (Task #2069) ───────────────────────────────
// Tracks the per-studio, per-feature nudge email lifecycle.
// One row per (studioId, featureKey) — upserted by the nudge scheduler.
export const STUDIO_NUDGE_STATUSES = ["pending", "sent", "tried", "timed_out"] as const;
export type StudioNudgeStatus = typeof STUDIO_NUDGE_STATUSES[number];

// migration-safety-ok — brand-new table, no existing rows, no ALTER guard needed
export const studioFeatureNudges = pgTable("studio_feature_nudges", {
  // migration-safety-ok — brand-new table
  id:               serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  studioId:         varchar("studio_id", { length: 100 }).notNull(),
  // migration-safety-ok — brand-new table
  featureKey:       varchar("feature_key", { length: 60 }).notNull(),
  // migration-safety-ok — brand-new table
  nudgeIndex:       integer("nudge_index").notNull().default(0),
  // migration-safety-ok — brand-new table
  status:           varchar("status", { length: 20 }).notNull().default("pending"),
  sentAt:           timestamp("sent_at"),
  triedAt:          timestamp("tried_at"),
  timeToTryHours:   integer("time_to_try_hours"),
  // migration-safety-ok — brand-new table
  createdAt:        timestamp("created_at").defaultNow().notNull(),
  updatedAt:        timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  uniqueIndex("idx_studio_feature_nudges_studio_feature").on(table.studioId, table.featureKey),
  index("idx_studio_feature_nudges_status").on(table.status),
  index("idx_studio_feature_nudges_studio").on(table.studioId),
]);

export type StudioFeatureNudge = typeof studioFeatureNudges.$inferSelect;
export const insertStudioFeatureNudgeSchema = createInsertSchema(studioFeatureNudges).omit({ id: true, createdAt: true, updatedAt: true }).extend({
  studioId:   z.string().min(1).max(100),
  featureKey: z.string().min(1).max(60),
  status:     z.enum(STUDIO_NUDGE_STATUSES).optional().default("pending"),
});
export type InsertStudioFeatureNudge = z.infer<typeof insertStudioFeatureNudgeSchema>;

// ── Studio Alert Transition State (Task #2069) ───────────────────────────────
// Tracks current alert state per studio so threshold-crossing fires once per
// transition, not on every daily run. Stored as a row per (studioId, alertKey).
// migration-safety-ok — brand-new table, no existing rows, no ALTER guard needed
export const studioAlertStates = pgTable("studio_alert_states", {
  // migration-safety-ok — brand-new table
  id:        serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  studioId:  varchar("studio_id", { length: 100 }).notNull(),
  // migration-safety-ok — brand-new table — 'going_cold' | 'going_dormant' | 'activity_cliff' | 'feature_gap'
  alertKey:  varchar("alert_key", { length: 40 }).notNull(),
  // migration-safety-ok — brand-new table — 'active' | 'resolved'
  state:     varchar("state", { length: 20 }).notNull().default("resolved"),
  firedAt:   timestamp("fired_at"),
  // migration-safety-ok — brand-new table
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  uniqueIndex("idx_studio_alert_states_studio_key").on(table.studioId, table.alertKey),
]);

export type StudioAlertState = typeof studioAlertStates.$inferSelect;

// ── Managed Studios (Task #2098) ─────────────────────────────────────────────
// Agency's single source of truth for its studio client roster.
// Tracks every studio from "configured" through "live" and "suspended".
export const STUDIO_STATUSES = ["configured", "live", "suspended"] as const;
export type StudioStatus = (typeof STUDIO_STATUSES)[number];

// New three-tier service model (diy/dwy/dfy). Old values kept for backward compat.
export const STUDIO_SERVICE_TIERS = ["diy", "dwy", "dfy"] as const;
export type StudioServiceTier = (typeof STUDIO_SERVICE_TIERS)[number];
export const STUDIO_TIERS = ["diy", "dwy", "dfy", "starter", "growth", "scale"] as const;
export type StudioTier = (typeof STUDIO_TIERS)[number];

export const STUDIO_ICP_TYPES = ["tattoo_studio", "body_art_studio", "appointment_studio"] as const;
export type StudioIcpType = (typeof STUDIO_ICP_TYPES)[number];

// migration-safety-ok — brand-new table, no existing rows, no ALTER guard needed
export const managedStudios = pgTable("managed_studios", {
  // migration-safety-ok — brand-new table
  id:               serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  slug:             varchar("slug", { length: 80 }).notNull().unique(),
  // migration-safety-ok — brand-new table
  displayName:      text("display_name").notNull(),
  // migration-safety-ok — brand-new table
  ownerEmail:       text("owner_email").notNull(),
  // migration-safety-ok — brand-new table — tattoo_studio | body_art_studio | appointment_studio
  icpType:          varchar("icp_type", { length: 30 }).notNull().default("tattoo_studio"),
  // migration-safety-ok — brand-new table — starter | growth | scale
  tier:             varchar("tier", { length: 20 }).notNull().default("starter"),
  // migration-safety-ok — brand-new table — configured | live | suspended
  status:           varchar("status", { length: 20 }).notNull().default("configured"),
  // migration-safety-ok — brand-new table
  adminToken:       varchar("admin_token", { length: 64 }).notNull(),
  launchedAt:       timestamp("launched_at"),
  lastActivityAt:   timestamp("last_activity_at"),
  agencyNotes:      text("agency_notes"),
  // Full deployment package JSON (studio_config seed + setup instructions)
  deploymentPackage: jsonb("deployment_package"),
  googlePlaceId:    text("google_place_id"),
  // migration-safety-ok — new nullable columns; no NOT NULL risk
  depositPercentage:     integer("deposit_percentage"),
  // migration-safety-ok — new nullable column
  depositMinimumAmount:  integer("deposit_minimum_amount"),
  // migration-safety-ok — brand-new table
  createdAt:        timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_managed_studios_status").on(table.status),
  uniqueIndex("idx_managed_studios_slug").on(table.slug),
  uniqueIndex("idx_managed_studios_admin_token").on(table.adminToken),
]);

export const insertManagedStudioSchema = createInsertSchema(managedStudios).omit({ id: true, createdAt: true }).extend({
  slug: z.string().min(2).max(80).regex(/^[a-z0-9-]+$/, "Slug must be lowercase letters, numbers, and hyphens only"),
  displayName: z.string().min(1).max(200),
  ownerEmail: z.string().email().max(200),
  icpType: z.enum(STUDIO_ICP_TYPES).optional().default("tattoo_studio"),
  tier: z.enum(STUDIO_TIERS).optional().default("diy"),
  status: z.enum(STUDIO_STATUSES).optional().default("configured"),
  adminToken: z.string().length(64),
  agencyNotes: z.string().max(5000).optional().nullable(),
  deploymentPackage: z.record(z.unknown()).optional().nullable(),
  depositPercentage: z.number().int().min(0).max(100).optional().nullable(),
  depositMinimumAmount: z.number().int().min(0).optional().nullable(),
});
export type InsertManagedStudio = z.infer<typeof insertManagedStudioSchema>;
export type ManagedStudio = typeof managedStudios.$inferSelect;

// ── Plan Change Requests ───────────────────────────────────────────────────────
// Studio owners submit requests to upgrade/downgrade their service tier.
// NGU admin reviews and approves or rejects each request.
export const PLAN_CHANGE_STATUSES = ["pending", "approved", "rejected"] as const;
export type PlanChangeStatus = (typeof PLAN_CHANGE_STATUSES)[number];

// migration-safety-ok — brand-new table, no existing rows, no ALTER guard needed
export const planChangeRequests = pgTable("plan_change_requests", {
  // migration-safety-ok — brand-new table
  id:            serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  studioId:      integer("studio_id").notNull().references(() => managedStudios.id, { onDelete: "cascade" }),
  // migration-safety-ok — brand-new table
  fromTier:      varchar("from_tier", { length: 20 }).notNull(),
  // migration-safety-ok — brand-new table
  toTier:        varchar("to_tier", { length: 20 }).notNull(),
  // Studio owner's message / reason for the request
  message:       text("message"),
  // NGU admin response message
  adminMessage:  text("admin_message"),
  // migration-safety-ok — brand-new table — pending | approved | rejected
  status:        varchar("status", { length: 20 }).notNull().default("pending"),
  // migration-safety-ok — brand-new table
  resolvedAt:    timestamp("resolved_at"),
  // migration-safety-ok — brand-new table
  createdAt:     timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_plan_change_requests_studio").on(table.studioId),
  index("idx_plan_change_requests_status").on(table.status),
]);

export const insertPlanChangeRequestSchema = createInsertSchema(planChangeRequests).omit({ id: true, createdAt: true }).extend({
  fromTier: z.string().min(1).max(20),
  toTier: z.enum(["diy", "dwy", "dfy"]),
  message: z.string().max(2000).optional().nullable(),
  adminMessage: z.string().max(2000).optional().nullable(),
  status: z.enum(PLAN_CHANGE_STATUSES).optional().default("pending"),
});
export type InsertPlanChangeRequest = z.infer<typeof insertPlanChangeRequestSchema>;
export type PlanChangeRequest = typeof planChangeRequests.$inferSelect;

// ── NGU B2B Prospects ─────────────────────────────────────────────────────────
// Cold outbound prospect accounts for NGU B2B sales. Separate from
// netglowup_leads (inbound marketing funnel). Sourced from directories,
// state license databases, and industry research.
export const NGU_B2B_ICP_TYPES = ["studio_owner", "pmu_adjacent", "barbershop", "salon", "educator"] as const;
export type NguB2bIcpType = (typeof NGU_B2B_ICP_TYPES)[number];

export const NGU_B2B_STATUSES = ["new", "contacted", "responded", "demo_booked", "closed_won", "closed_lost"] as const;
export type NguB2bStatus = (typeof NGU_B2B_STATUSES)[number];

export const NGU_B2B_TIERS = ["A", "B", "C"] as const;
export type NguB2bTier = (typeof NGU_B2B_TIERS)[number];

// migration-safety-ok — brand-new table, no existing rows, no ALTER guard needed
export const nguB2bProspects = pgTable("ngu_b2b_prospects", {
  // migration-safety-ok — brand-new table
  id:                  serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  name:                text("name").notNull(),
  // migration-safety-ok — brand-new table — studio_owner|pmu_adjacent|barbershop|salon|educator
  icp:                 varchar("icp", { length: 30 }).notNull(),
  city:                text("city"),
  state:               varchar("state", { length: 5 }),
  sizeSignal:          text("size_signal"),
  outreachChannel:     text("outreach_channel"),
  // migration-safety-ok — brand-new table
  qualificationScore:  integer("qualification_score").notNull().default(0),
  // migration-safety-ok — brand-new table — A|B|C
  tier:                varchar("tier", { length: 2 }).notNull().default("C"),
  // migration-safety-ok — brand-new table — new|contacted|responded|demo_booked|closed_won|closed_lost
  status:              varchar("status", { length: 20 }).notNull().default("new"),
  keyPainSignal:       text("key_pain_signal"),
  openingHook:         text("opening_hook"),
  instagramUrl:        text("instagram_url"),
  websiteUrl:          text("website_url"),
  ownerName:           text("owner_name"),
  email:               text("email"),
  phone:               text("phone"),
  notes:               text("notes"),
  lastContactedAt:     timestamp("last_contacted_at"),
  addedAsLeadAt:       timestamp("added_as_lead_at"),
  nguLeadId:           integer("ngu_lead_id"),
  // migration-safety-ok — brand-new table
  createdAt:           timestamp("created_at").defaultNow().notNull(),
  // migration-safety-ok — brand-new table
  updatedAt:           timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_ngu_b2b_prospects_icp").on(table.icp),
  index("idx_ngu_b2b_prospects_status").on(table.status),
  index("idx_ngu_b2b_prospects_tier").on(table.tier),
  index("idx_ngu_b2b_prospects_score").on(table.qualificationScore),
]);

export const insertNguB2bProspectSchema = createInsertSchema(nguB2bProspects).omit({ id: true, createdAt: true, updatedAt: true }).extend({
  name: z.string().min(1).max(300),
  icp: z.enum(NGU_B2B_ICP_TYPES),
  tier: z.enum(NGU_B2B_TIERS).optional().default("C"),
  status: z.enum(NGU_B2B_STATUSES).optional().default("new"),
  qualificationScore: z.number().int().min(0).max(100).optional().default(0),
  notes: z.string().max(5000).optional().nullable(),
});
export type InsertNguB2bProspect = z.infer<typeof insertNguB2bProspectSchema>;
export type NguB2bProspect = typeof nguB2bProspects.$inferSelect;

// ── Studio Report Cards — Task #2193 ─────────────────────────────────────────
// Per-studio health scorecard, computed nightly by the compute-report-cards job.
// Stores the latest snapshot per deployment for the agency owner daily digest.

export type StudioReportCardDimensionScore = {
  score: number;
  [key: string]: unknown;
};

export type StudioReportCardScores = {
  loginCadence:       {
    score: number;
    adminLogins7d: number;
    artistLogins7d: number;
    /** Consecutive days ending today with at least one admin login (0–7). */
    loginStreakDays: number;
    /** Days since last admin login (null = never logged in). Used for At-Risk: > 5 days. */
    lastLoginDaysAgo: number | null;
    /** Distinct days in the last 7d with at least one artist portal login (0–7). */
    artistPortalActiveDays: number;
  };
  bookingPipeline:    { score: number; bookings7d: number; confirmationRate: number; noShowRate: number; draftRecoveryRate: number };
  featureAdoption:    { score: number; adoptedFeatures: string[]; totalFeatures: number; adoptionPct: number };
  clientComms:        { score: number; avgResponseHours: number | null; npsResponseRate: number; messageThreads7d: number };
  aiContent:          {
    score: number;
    aiCaptionsUsed: number;
    bookingLimitConfigured: boolean;
    configCompleteness: number;
    /** Estimated 30-day AI spend attributed to this studio via pro-rata caption usage (USD). */
    aiSpendDollars: number;
    /** Monthly AI budget target for this studio (USD, default $25). */
    aiBudgetDollars: number;
  };
};

export const REPORT_CARD_GRADES = ["A", "B", "C", "D", "F"] as const;
export type ReportCardGrade = (typeof REPORT_CARD_GRADES)[number];

// ── Artist Report Cards (Task #2194) ──────────────────────────────────────────
// Per-artist nightly health scorecard: 5 dimensions × 20 pts = 100 total.
// Grades: A=85–100, B=70–84, C=50–69, D=30–49, F=0–29.

export type ArtistReportCardScores = {
  portalEngagement:  { score: number; logins7d: number; activeStreakDays: number; lastLoginDaysAgo: number | null };
  // Rates are stored as fractions (0-1). confirmationRate=0.82 means 82%.
  bookingManagement: { score: number; bookings30d: number; confirmationRate: number; noShowRate: number; cancellationRate: number; awaitingActionCount: number };
  clientExperience:  { score: number; npsAvg: number | null; npsCount: number; avgResponseHours: number | null; messageThreads7d: number };
  contentProfile:    { score: number; afterPhotos30d: number; flashDesignsPublished: number; bioComplete: boolean; instagramSet: boolean };
  featureAdoption:   { score: number; releaseFormsUsed: number; healingSessionsInitiated: number; availabilityWindowsConfigured: boolean; portalLastSeen: boolean; costEstimatorUsedCount: number };
};

// migration-safety-ok — brand-new table, no existing rows
export const artistReportCards = pgTable("artist_report_cards", {
  // migration-safety-ok — brand-new table
  id:           serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  artistId:     integer("artist_id").notNull().references(() => artists.id, { onDelete: "cascade" }),
  // migration-safety-ok — brand-new table (nullable: populated from studio_config.id for future multi-studio scoping)
  studioId:     integer("studio_id"),
  // migration-safety-ok — brand-new table
  computedAt:   timestamp("computed_at").defaultNow().notNull(),
  // migration-safety-ok — brand-new table
  scoreJson:    jsonb("score_json").$type<ArtistReportCardScores>().notNull(),
  // migration-safety-ok — brand-new table
  overallScore: integer("overall_score").notNull(),
  // migration-safety-ok — brand-new table — A|B|C|D|F
  grade:        varchar("grade", { length: 2 }).notNull(),
}, (table) => [
  index("idx_artist_report_cards_artist_computed").on(table.artistId, table.computedAt),
]);

export const insertArtistReportCardSchema = createInsertSchema(artistReportCards).omit({ id: true, computedAt: true }).extend({
  overallScore: z.number().int().min(0).max(100),
  grade: z.enum(REPORT_CARD_GRADES),
});
export type InsertArtistReportCard = z.infer<typeof insertArtistReportCardSchema>;
export type ArtistReportCard = typeof artistReportCards.$inferSelect;

// ── VSL Variants — Task #2239 ─────────────────────────────────────────────────
// Stores HeyGen-generated VSL video variants, one per ICP segment/experiment.
// Each variant is linked to a segment (e.g. "studio_owner") and an experiment
// key from shared/experiments.ts. The admin activates exactly one per segment.

export const VSL_STATUSES = ["pending", "queued", "generating", "ready", "failed"] as const;
export type VslStatus = typeof VSL_STATUSES[number];

// migration-safety-ok — brand-new table, no existing rows
export const vslVariants = pgTable("vsl_variants", {
  // migration-safety-ok — brand-new table
  id:              serial("id").primaryKey(),
  // migration-safety-ok — brand-new table — e.g. "studio_owner", "solo_artist"
  segment:         varchar("segment", { length: 64 }).notNull(),
  // migration-safety-ok — brand-new table — e.g. "control", "treatment_a"
  variantKey:      varchar("variant_key", { length: 64 }).notNull(),
  // migration-safety-ok — brand-new table — human-readable label for admin UI
  label:           text("label").notNull(),
  // migration-safety-ok — brand-new table — the HeyGen video_id returned on generation
  heygenVideoId:   text("heygen_video_id"),
  // migration-safety-ok — brand-new table — the playable CDN URL once complete
  videoUrl:        text("video_url"),
  // migration-safety-ok — brand-new table — pending | generating | ready | failed
  status:          varchar("status", { length: 32 }).notNull().default("pending"),
  // migration-safety-ok — brand-new table — VSL script text fed to HeyGen
  scriptText:      text("script_text"),
  // migration-safety-ok — brand-new table — links to EXPERIMENTS key in shared/experiments.ts
  experimentKey:   varchar("experiment_key", { length: 128 }),
  // migration-safety-ok — brand-new table — HeyGen avatar_id used for generation
  heygenAvatarId:  text("heygen_avatar_id"),
  // migration-safety-ok — brand-new table — HeyGen voice_id used for generation
  heygenVoiceId:   text("heygen_voice_id"),
  // migration-safety-ok — brand-new table — "landscape" | "portrait" | "square"
  dimension:       varchar("dimension", { length: 16 }).notNull().default("landscape"),
  // migration-safety-ok — brand-new table — exactly one row per segment should have isActive=true
  isActive:        boolean("is_active").notNull().default(false),
  // "heygen" | "fal" | "manual" — drives which generation path the job takes
  source:          varchar("source", { length: 32 }).default("heygen"),
  // generic external video ID (heygenVideoId mirrors this for heygen, falRequestId for fal)
  sourceVideoId:   text("source_video_id"),
  // video thumbnail URL — populated after generation completes
  thumbnailUrl:    text("thumbnail_url"),
  // timestamp when video generation completed
  generatedAt:     timestamp("generated_at"),
  // migration-safety-ok — brand-new table — optional JSON metadata (error details, etc.)
  metadata:        jsonb("metadata").$type<Record<string, unknown>>(),
  // migration-safety-ok — brand-new table
  createdAt:       timestamp("created_at").defaultNow().notNull(),
  // migration-safety-ok — brand-new table
  updatedAt:       timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_vsl_variants_segment_active").on(table.segment, table.isActive),
  index("idx_vsl_variants_heygen_id").on(table.heygenVideoId),
  index("idx_vsl_variants_status").on(table.status),
  // Covers listStuckVslVariants() — WHERE status IN (...) AND updated_at < cutoff.
  // The single-column status index cannot serve the updated_at predicate, causing a
  // seq scan of all rows passing the status filter. INC-2026-07-06.
  index("idx_vsl_variants_status_updated_at").on(table.status, table.updatedAt),
]);

export const insertVslVariantSchema = createInsertSchema(vslVariants).omit({ id: true, createdAt: true, updatedAt: true }).extend({
  status:    z.enum(VSL_STATUSES).optional(),
  segment:   z.string().min(1).max(64),
  variantKey: z.string().min(1).max(64),
  label:     z.string().min(1),
  dimension: z.enum(["landscape", "portrait", "square"]).optional(),
});
export type InsertVslVariant = z.infer<typeof insertVslVariantSchema>;
export type VslVariant = typeof vslVariants.$inferSelect;

// A VSL generation is considered "stuck" once it has sat in a non-terminal
// in-flight state for longer than this threshold. HeyGen renders normally finish
// in 1–3 minutes, so a variant lingering past 30 min signals a silent gap — the
// experiment is live but the video may never finish generating.
export const VSL_STUCK_THRESHOLD_MINUTES = 30;

// Non-terminal states: the variant exists but has not reached a terminal state
// ("ready"/"failed"). "pending" is the freshly-created draft, "queued" is enqueued
// for the start job, and "generating" is in-flight inside HeyGen. All three are
// "stuck" once they linger past the threshold — a variant left in "pending" after
// an experiment goes live is the same silent gap as one wedged in "generating":
// the experiment is live but no finished video will ever appear.
export const VSL_IN_FLIGHT_STATUSES = ["pending", "queued", "generating"] as const;

export function isVslVariantStuck(
  variant: { status: string; updatedAt: Date | string | null | undefined },
  now: Date = new Date(),
  thresholdMinutes: number = VSL_STUCK_THRESHOLD_MINUTES,
): boolean {
  if (!variant.updatedAt) return false;
  if (!(VSL_IN_FLIGHT_STATUSES as readonly string[]).includes(variant.status)) return false;
  const updated = variant.updatedAt instanceof Date ? variant.updatedAt : new Date(variant.updatedAt);
  if (Number.isNaN(updated.getTime())) return false;
  return now.getTime() - updated.getTime() > thresholdMinutes * 60_000;
}

// migration-safety-ok — brand-new table, no existing rows
export const studioReportCards = pgTable("studio_report_cards", {
  // migration-safety-ok — brand-new table
  id:           serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  deploymentId: integer("deployment_id").notNull().references(() => clientDeployments.id, { onDelete: "cascade" }),
  // migration-safety-ok — brand-new table
  computedAt:   timestamp("computed_at").defaultNow().notNull(),
  // migration-safety-ok — brand-new table
  scoreJson:    jsonb("score_json").$type<StudioReportCardScores>().notNull(),
  // migration-safety-ok — brand-new table
  overallScore: integer("overall_score").notNull(),
  // migration-safety-ok — brand-new table — A|B|C|D|F
  grade:        varchar("grade", { length: 2 }).notNull(),
}, (table) => [
  index("idx_studio_report_cards_deployment_computed").on(table.deploymentId, table.computedAt),
]);

export const insertStudioReportCardSchema = createInsertSchema(studioReportCards).omit({ id: true, computedAt: true }).extend({
  overallScore: z.number().int().min(0).max(100),
  grade: z.enum(REPORT_CARD_GRADES),
});
export type InsertStudioReportCard = z.infer<typeof insertStudioReportCardSchema>;
export type StudioReportCard = typeof studioReportCards.$inferSelect;

// ── NGU VSL Scripts ───────────────────────────────────────────────────────────
// AI-generated VSL (Video Sales Letter) copy, scoped per ICP.
// Each script has multiple revisions (history) and a chat thread for AI revisions.
// migration-safety-ok — brand-new tables, no existing rows, no ALTER guards needed

export const NGU_VSL_ICP_SLUGS = [
  "studio_owner", "solo_artist", "convention", "educator",
  "distributor", "adjacent", "salon_owner",
] as const;
export type NguVslIcpSlug = (typeof NGU_VSL_ICP_SLUGS)[number];

export const NGU_VSL_STATUSES = ["draft", "review", "approved"] as const;
export type NguVslStatus = (typeof NGU_VSL_STATUSES)[number];

export type VslSectionMap = {
  hook: string;
  relateAvatar: string;
  agitatePain: string;
  newOpportunity: string;
  mechanism: string;
  socialProof: string;
  offerStack: string;
  priceGuarantee: string;
  ctaUrgency: string;
};

// migration-safety-ok — brand-new table
export const nguVslScripts = pgTable("ngu_vsl_scripts", {
  // migration-safety-ok — brand-new table
  id:           serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  icpSlug:      varchar("icp_slug", { length: 50 }).notNull(),
  // migration-safety-ok — brand-new table
  title:        text("title").notNull(),
  // migration-safety-ok — brand-new table — draft|review|approved
  status:       varchar("status", { length: 20 }).notNull().default("draft"),
  parameters:   jsonb("parameters").$type<Record<string, string>>(),
  // migration-safety-ok — brand-new table
  createdAt:    timestamp("created_at").defaultNow().notNull(),
  // migration-safety-ok — brand-new table
  updatedAt:    timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_ngu_vsl_scripts_icp").on(table.icpSlug),
  index("idx_ngu_vsl_scripts_status").on(table.status),
]);

export const insertNguVslScriptSchema = createInsertSchema(nguVslScripts).omit({ id: true, createdAt: true, updatedAt: true }).extend({
  icpSlug: z.enum(NGU_VSL_ICP_SLUGS),
  title: z.string().min(1).max(300),
  status: z.enum(NGU_VSL_STATUSES).optional().default("draft"),
  parameters: z.record(z.string()).optional().nullable(),
});
export type InsertNguVslScript = z.infer<typeof insertNguVslScriptSchema>;
export type NguVslScript = typeof nguVslScripts.$inferSelect;

// migration-safety-ok — brand-new table
export const nguVslRevisions = pgTable("ngu_vsl_revisions", {
  // migration-safety-ok — brand-new table
  id:             serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  scriptId:       integer("script_id").notNull().references(() => nguVslScripts.id, { onDelete: "cascade" }),
  // migration-safety-ok — brand-new table
  versionNumber:  integer("version_number").notNull(),
  // migration-safety-ok — brand-new table
  sections:       jsonb("sections").$type<VslSectionMap>().notNull(),
  // migration-safety-ok — brand-new table
  fullText:       text("full_text").notNull(),
  notes:          text("notes"),
  qualityScore:   jsonb("quality_score"),
  // migration-safety-ok — brand-new table
  createdAt:      timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_ngu_vsl_revisions_script").on(table.scriptId, table.versionNumber),
]);

export const insertNguVslRevisionSchema = createInsertSchema(nguVslRevisions).omit({ id: true, createdAt: true }).extend({
  // Must stay in lockstep with the VslSectionMap type above (single source of truth
  // for section keys — parseAiResponseToSections/assembleSectionsToFullText produce this shape).
  sections: z.object({
    hook: z.string(),
    relateAvatar: z.string(),
    agitatePain: z.string(),
    newOpportunity: z.string(),
    mechanism: z.string(),
    socialProof: z.string(),
    offerStack: z.string(),
    priceGuarantee: z.string(),
    ctaUrgency: z.string(),
  }) satisfies z.ZodType<VslSectionMap>,
  fullText: z.string(),
  notes: z.string().max(2000).optional().nullable(),
});
export type InsertNguVslRevision = z.infer<typeof insertNguVslRevisionSchema>;
export type NguVslRevision = typeof nguVslRevisions.$inferSelect;

// ── response_time_audit_log — Task #2306 ─────────────────────────────────
// Immutable log of every response-time event (view, call-initiated, confirmed,
// declined, deposit-requested). Used for cohort reporting and raw data export
// without mutating the source tables. All entries point at either a booking or
// an NGU lead (exactly one FK per row is non-null).
// migration-safety-ok — brand-new table
export const responseTimeAuditLog = pgTable("response_time_audit_log", {
  // migration-safety-ok — brand-new table
  id: serial("id").primaryKey(),
  // migration-safety-ok — brand-new table — "booking_view" | "booking_responded" | "lead_call_initiated" | "lead_call_notes"
  eventType: varchar("event_type", { length: 40 }).notNull(),
  // migration-safety-ok — brand-new table
  bookingId: integer("booking_id"),
  // migration-safety-ok — brand-new table
  nguLeadId: integer("ngu_lead_id"),
  // migration-safety-ok — brand-new table — seconds since record creation at event time
  elapsedSeconds: integer("elapsed_seconds"),
  // migration-safety-ok — brand-new table — for booking events: "confirm"|"decline"|"deposit-request"|"view"; for lead events: "call"|"notes"
  actionDetail: varchar("action_detail", { length: 50 }),
  // migration-safety-ok — brand-new table
  occurredAt: timestamp("occurred_at").defaultNow().notNull(),
}, (table) => [
  index("idx_rtlog_booking").on(table.bookingId),
  index("idx_rtlog_ngu_lead").on(table.nguLeadId),
  index("idx_rtlog_occurred_at").on(table.occurredAt),
  index("idx_rtlog_event_type").on(table.eventType),
]);

export type ResponseTimeAuditLog = typeof responseTimeAuditLog.$inferSelect;

// migration-safety-ok — brand-new table
export const nguVslChatMessages = pgTable("ngu_vsl_chat_messages", {
  // migration-safety-ok — brand-new table
  id:        serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  scriptId:  integer("script_id").notNull().references(() => nguVslScripts.id, { onDelete: "cascade" }),
  // migration-safety-ok — brand-new table — user|assistant
  role:      varchar("role", { length: 20 }).notNull(),
  // migration-safety-ok — brand-new table
  content:   text("content").notNull(),
  // migration-safety-ok — brand-new table
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_ngu_vsl_chat_script").on(table.scriptId, table.createdAt),
]);

export const insertNguVslChatMessageSchema = createInsertSchema(nguVslChatMessages).omit({ id: true, createdAt: true }).extend({
  role: z.enum(["user", "assistant"]),
  content: z.string().min(1).max(50000),
});
export type InsertNguVslChatMessage = z.infer<typeof insertNguVslChatMessageSchema>;
export type NguVslChatMessage = typeof nguVslChatMessages.$inferSelect;

// ── Ad Creative Generations ──────────────────────────────────────────────────
// History of each time a studio owner generates/saves their ad creative pack.
// Used to enforce rate limits (10/hr, 25/day) and maintain history.
// migration-safety-ok — brand-new table
export const adCreativeGenerations = pgTable("ad_creative_generations", {
  // migration-safety-ok — brand-new table
  id: serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  studioName: text("studio_name").notNull(),
  // migration-safety-ok — brand-new table
  bookingLink: text("booking_link").notNull(),
  // migration-safety-ok — brand-new table — substituted for [N] in scarcity ad
  scarcityCount: integer("scarcity_count").notNull().default(3),
  // migration-safety-ok — brand-new table — per-angle copy overrides keyed by angle id
  copyOverrides: jsonb("copy_overrides"),
  // migration-safety-ok — brand-new table
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_ad_creative_gen_created_at").on(table.createdAt),
]);

export const insertAdCreativeGenerationSchema = createInsertSchema(adCreativeGenerations).omit({ id: true, createdAt: true });
export type InsertAdCreativeGeneration = z.infer<typeof insertAdCreativeGenerationSchema>;
export type AdCreativeGeneration = typeof adCreativeGenerations.$inferSelect;

// ── Data Portability Export Requests ─────────────────────────────────────────
// Tracks every studio-data export (clients, bookings, config) requested through
// the admin panel. Records the request time, 5-business-day due date, row
// counts, ZIP size, and whether the alert email was sent.
// migration-safety-ok — brand-new table
export const dataExportRequests = pgTable("data_export_requests", {
  // migration-safety-ok — brand-new table
  id: serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  requestedAt: timestamp("requested_at").defaultNow().notNull(),
  // migration-safety-ok — brand-new table — YYYY-MM-DD, 5 business days from requestedAt
  dueDate: text("due_date").notNull(),
  // migration-safety-ok — brand-new table — counts of exported records
  rowCounts: jsonb("row_counts").$type<{ contacts: number; bookings: number; subscribers: number }>().notNull(),
  // migration-safety-ok — brand-new table — size of the generated ZIP in bytes
  zipSizeBytes: integer("zip_size_bytes").notNull(),
  // migration-safety-ok — brand-new table — when the internal alert email was sent (null = not sent)
  notifiedAt: timestamp("notified_at"),
  // migration-safety-ok — brand-new table
  status: text("status").notNull().default("completed"),
}, (table) => [
  index("idx_data_export_requests_requested_at").on(table.requestedAt),
]);

export type DataExportRequest = typeof dataExportRequests.$inferSelect;

// ── Job Health ────────────────────────────────────────────────────────────────
// Persists pg-boss job last-success and last-failure timestamps across server
// restarts. The ops dashboard reads from in-memory stats, which are hydrated
// from this table at startup so the Job Health panel never shows "never" just
// because the process restarted.
// migration-safety-ok — brand-new table
export const jobHealth = pgTable("job_health", {
  // migration-safety-ok — brand-new table
  jobName: text("job_name").primaryKey(),
  // migration-safety-ok — brand-new table
  lastSuccessAt: timestamp("last_success_at"),
  // migration-safety-ok — brand-new table
  lastFailureAt: timestamp("last_failure_at"),
  // migration-safety-ok — brand-new table
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type JobHealth = typeof jobHealth.$inferSelect;

// ── Studio Posts (Client News Feed) ──────────────────────────────────────────
// Studio-to-client broadcast posts shown in the client portal home.
// 'update' | 'flash_drop' | 'insider' | 'event' | 'closure'
// migration-safety-ok — brand-new table
export const studioPosts = pgTable("studio_posts", {
  // migration-safety-ok — brand-new table
  id: serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  studioId: integer("studio_id").notNull(),
  // migration-safety-ok — brand-new table
  title: varchar("title", { length: 200 }).notNull(),
  // migration-safety-ok — brand-new table
  body: text("body").notNull(),
  // migration-safety-ok — brand-new table
  postType: varchar("post_type", { length: 30 }).notNull().default("update"),
  // migration-safety-ok — brand-new table
  isPinned: boolean("is_pinned").notNull().default(false),
  // migration-safety-ok — brand-new table
  publishedAt: timestamp("published_at").defaultNow().notNull(),
  // migration-safety-ok — brand-new table — null means never expires
  expiresAt: timestamp("expires_at"),
  // migration-safety-ok — brand-new table
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_studio_posts_feed").on(table.studioId, table.publishedAt.desc()),
]);

export const insertStudioPostSchema = createInsertSchema(studioPosts).omit({ id: true, createdAt: true });
export type InsertStudioPost = z.infer<typeof insertStudioPostSchema>;
export type StudioPost = typeof studioPosts.$inferSelect;

// ── Discount Drops ────────────────────────────────────────────────────────────
// Limited-quantity, time-limited discount offers admins create (manually or via
// Auto Mode) to fill short-notice slots and drive urgency. Clients claim them
// on the client portal; the discount is applied atomically at booking time.
// migration-safety-ok — brand-new table
export const discountDrops = pgTable("discount_drops", {
  // migration-safety-ok — brand-new table
  id: serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  title: text("title").notNull(),
  // migration-safety-ok — brand-new table — shown on portal below the title
  description: text("description"),
  // migration-safety-ok — brand-new table — fixed $ off in cents (null if percent-based)
  discountAmountCents: integer("discount_amount_cents"),
  // migration-safety-ok — brand-new table — % off 1-100 (null if fixed-amount-based)
  discountPercent: integer("discount_percent"),
  // migration-safety-ok — brand-new table — max clients who can claim this drop
  maxClaims: integer("max_claims").notNull().default(5),
  // migration-safety-ok — brand-new table — atomic counter incremented on each successful claim
  claimedCount: integer("claimed_count").notNull().default(0),
  // migration-safety-ok — brand-new table — optional restriction to a specific artist slug
  requiresArtistSlug: text("requires_artist_slug"),
  // migration-safety-ok — brand-new table — drop closes and can no longer be claimed after this
  expiresAt: timestamp("expires_at").notNull(),
  // migration-safety-ok — brand-new table — admin can deactivate manually before expiry
  isActive: boolean("is_active").notNull().default(true),
  // migration-safety-ok — brand-new table — true when created by Auto Mode scheduler
  autoGenerated: boolean("auto_generated").notNull().default(false),
  // migration-safety-ok — brand-new table — whether the subscriber email blast was sent for this drop
  emailBlastSent: boolean("email_blast_sent").notNull().default(false),
  // migration-safety-ok — brand-new table — when the subscriber email blast was sent
  emailBlastSentAt: timestamp("email_blast_sent_at"),
  // migration-safety-ok — brand-new table — claims that resulted in a confirmed booking
  conversionCount: integer("conversion_count").notNull().default(0),
  // migration-safety-ok — brand-new table
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_discount_drops_is_active").on(table.isActive),
  index("idx_discount_drops_expires_at").on(table.expiresAt),
  index("idx_discount_drops_created_at").on(table.createdAt),
]);

export const insertDiscountDropSchema = createInsertSchema(discountDrops).omit({
  id: true,
  claimedCount: true,
  conversionCount: true,
  createdAt: true,
  emailBlastSent: true,
  emailBlastSentAt: true,
  autoGenerated: true,
});
export type InsertDiscountDrop = z.infer<typeof insertDiscountDropSchema>;
export type DiscountDrop = typeof discountDrops.$inferSelect;

// migration-safety-ok — brand-new table
export const discountClaims = pgTable("discount_claims", {
  // migration-safety-ok — brand-new table
  id: serial("id").primaryKey(),
  // migration-safety-ok — brand-new table — which drop was claimed
  dropId: integer("drop_id").notNull(),
  // migration-safety-ok — brand-new table — client's email extracted from their portal session
  clientEmail: text("client_email").notNull(),
  // migration-safety-ok — brand-new table — short-lived token passed through the booking URL to validate claim
  claimToken: text("claim_token").notNull(),
  // migration-safety-ok — brand-new table — when the client clicked "Claim Now"
  claimedAt: timestamp("claimed_at").defaultNow().notNull(),
  // migration-safety-ok — brand-new table — set when a booking is created using this claim
  bookingId: integer("booking_id"),
  // migration-safety-ok — brand-new table — claim token expires; booking must be submitted before this
  expiresAt: timestamp("expires_at").notNull(),
}, (table) => [
  index("idx_discount_claims_drop_id").on(table.dropId),
  index("idx_discount_claims_claim_token").on(table.claimToken),
  index("idx_discount_claims_client_email").on(table.clientEmail),
]);

export type DiscountClaim = typeof discountClaims.$inferSelect;

// ── Inspiration Folders ───────────────────────────────────────────────────────
// Named folders on the shared per-client inspiration board, keyed by email.
export const inspirationFolders = pgTable("inspiration_folders", {
  id: serial("id").primaryKey(),
  clientEmail: text("client_email").notNull(),
  name: text("name").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_inspiration_folders_client_email").on(table.clientEmail),
]);

export const insertInspirationFolderSchema = createInsertSchema(inspirationFolders).omit({ id: true, createdAt: true });
export type InsertInspirationFolder = z.infer<typeof insertInspirationFolderSchema>;
export type InspirationFolder = typeof inspirationFolders.$inferSelect;

// ── Inspiration Items ─────────────────────────────────────────────────────────
// Per-client private inspiration board. Scoped by portal_token_hash so one
// client can never access another's records.
// Rows with client_email set belong to the shared board (visible to staff).
// Rows without client_email are personal AI/upload items (token-hash only).
// migration-safety-ok — brand-new table
export const inspirationItems = pgTable("inspiration_items", {
  // migration-safety-ok — brand-new table
  id: serial("id").primaryKey(),
  // migration-safety-ok — brand-new table — sha256 of the client's session token
  portalTokenHash: text("portal_token_hash").notNull(),
  // migration-safety-ok — brand-new table — 'ai_generated' | 'upload'
  type: varchar("type", { length: 20 }).notNull(),
  // migration-safety-ok — brand-new table — private object storage path
  storagePath: text("storage_path").notNull(),
  // migration-safety-ok — brand-new table — raw user prompt for AI items
  prompt: text("prompt"),
  // migration-safety-ok — brand-new table
  title: text("title"),
  // migration-safety-ok — brand-new table
  notes: text("notes"),
  // migration-safety-ok — brand-new table
  tags: text("tags").array().notNull().default([]),
  // migration-safety-ok — brand-new table — style modifier chips used during generation
  styleModifiers: text("style_modifiers").array().notNull().default([]),
  // migration-safety-ok — brand-new table
  isPinned: boolean("is_pinned").notNull().default(false),
  // migration-safety-ok — brand-new table
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Shared board extension — nullable for backward compat with existing token-hash items
  clientEmail: text("client_email"),
  folderId: integer("folder_id"),
  // 'upload' | 'link' | 'booking_reference' | 'ai_generated'
  sourceType: varchar("source_type", { length: 30 }),
  // Original external URL for link-type items
  originalUrl: text("original_url"),
  // OG image URL or stored preview path for link items
  previewUrl: text("preview_url"),
  caption: text("caption"),
  bookingId: integer("booking_id"),
}, (table) => [
  index("idx_inspiration_items_portal_token_hash").on(table.portalTokenHash),
  index("idx_inspiration_items_client_email").on(table.clientEmail),
]);

export const insertInspirationItemSchema = createInsertSchema(inspirationItems).omit({ id: true, createdAt: true });
export type InsertInspirationItem = z.infer<typeof insertInspirationItemSchema>;
export type InspirationItem = typeof inspirationItems.$inferSelect;

// ── Micro-pulse A/B Feedback System ─────────────────────────────────────────
// Binary A/B question cards embedded in artist and owner portals.
// Questions form a decision tree: each answer can trigger a branch child question
// shown immediately in the same session.
//
// Status lifecycle: draft → active → archived
//   draft:    AI-generated, pending admin approval
//   active:   Shown to portal users (only one active root per audience at a time)
//   archived: Retired, results still queryable

// migration-safety-ok — brand-new table
export const pulseQuestions = pgTable("pulse_questions", {
  // migration-safety-ok — brand-new table
  id: serial("id").primaryKey(),
  // migration-safety-ok — brand-new table — 'artist' | 'owner'
  audience: varchar("audience", { length: 20 }).notNull(),
  // migration-safety-ok — brand-new table
  questionText: text("question_text").notNull(),
  // migration-safety-ok — brand-new table — label for option A button
  optionA: varchar("option_a", { length: 120 }).notNull(),
  // migration-safety-ok — brand-new table — label for option B button
  optionB: varchar("option_b", { length: 120 }).notNull(),
  // migration-safety-ok — brand-new table — 'draft' | 'active' | 'archived'
  status: varchar("status", { length: 20 }).notNull().default("draft"),
  // migration-safety-ok — brand-new table — FK to parent question (null = root question)
  parentQuestionId: integer("parent_question_id"),
  // migration-safety-ok — brand-new table — 'a' | 'b' — which parent answer triggers this branch
  parentOption: varchar("parent_option", { length: 1 }),
  // migration-safety-ok — brand-new table — total answers collected while active
  responseCount: integer("response_count").notNull().default(0),
  // migration-safety-ok — brand-new table
  generatedAt: timestamp("generated_at").defaultNow().notNull(),
  // migration-safety-ok — brand-new table
  approvedAt: timestamp("approved_at"),
  // migration-safety-ok — brand-new table — optional admin note (e.g. rejection reason)
  adminNote: text("admin_note"),
}, (table) => [
  index("idx_pulse_questions_audience_status").on(table.audience, table.status),
  index("idx_pulse_questions_parent").on(table.parentQuestionId),
]);

export const insertPulseQuestionSchema = createInsertSchema(pulseQuestions).omit({ id: true, generatedAt: true });
export type InsertPulseQuestion = z.infer<typeof insertPulseQuestionSchema>;
export type PulseQuestion = typeof pulseQuestions.$inferSelect;

// migration-safety-ok — brand-new table
export const pulseAnswers = pgTable("pulse_answers", {
  // migration-safety-ok — brand-new table
  id: serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  questionId: integer("question_id").notNull(),
  // migration-safety-ok — brand-new table — hashed portal token so we can dedup without storing raw token
  responderTokenHash: varchar("responder_token_hash", { length: 64 }).notNull(),
  // migration-safety-ok — brand-new table — 'a' | 'b'
  chosenOption: varchar("chosen_option", { length: 1 }).notNull(),
  // migration-safety-ok — brand-new table — random UUID generated per portal load for session grouping
  sessionId: varchar("session_id", { length: 64 }),
  // migration-safety-ok — brand-new table
  answeredAt: timestamp("answered_at").defaultNow().notNull(),
}, (table) => [
  index("idx_pulse_answers_question_id").on(table.questionId),
  uniqueIndex("idx_pulse_answers_dedup").on(table.questionId, table.responderTokenHash),
]);

export const insertPulseAnswerSchema = createInsertSchema(pulseAnswers).omit({ id: true, answeredAt: true });
export type InsertPulseAnswer = z.infer<typeof insertPulseAnswerSchema>;
export type PulseAnswer = typeof pulseAnswers.$inferSelect;

// ── Phase 2 Client Portal AI Features ────────────────────────────────────────

// B1 — Style DNA Profiler: AI-analysed style profile per client email
// migration-safety-ok — brand-new table
export const clientStyleProfiles = pgTable("client_style_profiles", {
  // migration-safety-ok — brand-new table
  id: serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  clientEmail: varchar("client_email", { length: 320 }).notNull().unique(),
  // migration-safety-ok — brand-new table — JSON blob: { topStyles, topPlacements, palette, keywords, summary }
  profile: jsonb("profile").$type<{
    topStyles: string[];
    topPlacements: string[];
    palette: string[];
    keywords: string[];
    summary: string;
  }>().notNull(),
  // migration-safety-ok — brand-new table
  lastAnalyzedAt: timestamp("last_analyzed_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_client_style_profiles_email").on(table.clientEmail),
]);

export type ClientStyleProfile = typeof clientStyleProfiles.$inferSelect;

// A1 — Persistent Body Map: tattoo placement zones per client
// migration-safety-ok — brand-new table
export const clientBodyMapZones = pgTable("client_body_map_zones", {
  // migration-safety-ok — brand-new table
  id: serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  clientEmail: varchar("client_email", { length: 320 }).notNull(),
  // migration-safety-ok — brand-new table — e.g. "left_forearm", "right_chest", "spine"
  zone: varchar("zone", { length: 80 }).notNull(),
  // migration-safety-ok — brand-new table
  label: varchar("label", { length: 120 }),
  // migration-safety-ok — brand-new table — optional booking reference if this zone was tattooed
  bookingId: integer("booking_id"),
  // migration-safety-ok — brand-new table — status: 'tattooed' | 'planned' | 'interested'
  status: varchar("status", { length: 20 }).notNull().default("interested"),
  // migration-safety-ok — brand-new table — freeform notes
  notes: text("notes"),
  // migration-safety-ok — brand-new table
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_client_body_map_email").on(table.clientEmail),
]);

export type ClientBodyMapZone = typeof clientBodyMapZones.$inferSelect;
export type InsertClientBodyMapZone = typeof clientBodyMapZones.$inferInsert;

// A6 / F3 — Sleeve Progress Tracker + "Complete Your Collection" planned pieces
// migration-safety-ok — brand-new table
export const clientPlannedPieces = pgTable("client_planned_pieces", {
  // migration-safety-ok — brand-new table
  id: serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  clientEmail: varchar("client_email", { length: 320 }).notNull(),
  // migration-safety-ok — brand-new table
  label: varchar("label", { length: 200 }).notNull(),
  // migration-safety-ok — brand-new table — body zone this piece targets
  zone: varchar("zone", { length: 80 }),
  // migration-safety-ok — brand-new table — 0-100
  progressPct: integer("progress_pct").notNull().default(0),
  // migration-safety-ok — brand-new table — optional style tags for suggestion matching
  styleTags: text("style_tags").array(),
  // migration-safety-ok — brand-new table
  notes: text("notes"),
  // migration-safety-ok — brand-new table
  isComplete: boolean("is_complete").notNull().default(false),
  // migration-safety-ok — brand-new table
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_client_planned_pieces_email").on(table.clientEmail),
]);

export type ClientPlannedPiece = typeof clientPlannedPieces.$inferSelect;
export type InsertClientPlannedPiece = typeof clientPlannedPieces.$inferInsert;

// ── Phase 3 Client Portal AI — Flagship AI Features ──────────────────────────
// C1 — Healing Quality Score columns added to healedPhotoSubmissions (ALTER TABLE guards in script/pre-push-migrations.ts)
// Note: These columns are added via ALTER TABLE, not inline here, because healedPhotoSubmissions already exists in production.
// The Drizzle ORM model is updated below to match.
// See script/pre-push-migrations.ts for the ADD COLUMN IF NOT EXISTS guards.

// Augmented type for healedPhotoSubmissions with C1 quality fields (not in base schema; read via raw query or joined)
export type HealedPhotoQualityFields = {
  qualityScore: number | null;
  inkRetention: number | null;
  colorVibrancy: number | null;
  lineCrispness: number | null;
  touchupLikelihood: "low" | "medium" | "high" | null;
  aiAnalysisSummary: string | null;
  analyzedAt: Date | null;
};

// A2/A3/A4 — AI Continuation Generator: stores 3 generated variants per request
// migration-safety-ok — brand-new table
export const clientAiGenerations = pgTable("client_ai_generations", {
  // migration-safety-ok — brand-new table
  id: serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  clientEmail: varchar("client_email", { length: 320 }).notNull(),
  // migration-safety-ok — brand-new table — body zone targeted e.g. "left_forearm"
  zone: varchar("zone", { length: 80 }),
  // migration-safety-ok — brand-new table — original user prompt
  prompt: text("prompt").notNull(),
  // migration-safety-ok — brand-new table — compressed style DNA summary injected at generation time
  styleContextSummary: text("style_context_summary"),
  // migration-safety-ok — brand-new table — 3 private storage paths, one per variant
  variantStoragePaths: text("variant_storage_paths").array().notNull(),
  // migration-safety-ok — brand-new table — ids of pinned inspiration items used as context (A4)
  pinnedItemIds: integer("pinned_item_ids").array(),
  // migration-safety-ok — brand-new table — user's chosen variant index (0-2)
  selectedVariant: integer("selected_variant").notNull().default(0),
  // migration-safety-ok — brand-new table — 'color' | 'desaturated' | 'linework'
  colorMode: varchar("color_mode", { length: 20 }),
  // A2/A3 — optional reference body photo uploaded by client for inpainting (private storage path)
  basePhotoStoragePath: text("base_photo_storage_path"),
  // A3 — whether this generation used DALL-E 2 inpainting (true) or DALL-E 3 from-scratch (false)
  usedInpainting: boolean("used_inpainting").notNull().default(false),
  // migration-safety-ok — brand-new table
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_client_ai_generations_email").on(table.clientEmail),
]);

export type ClientAiGeneration = typeof clientAiGenerations.$inferSelect;
export type InsertClientAiGeneration = typeof clientAiGenerations.$inferInsert;


// G3 — Session Sequencing Advisor: AI-generated session sequence plan per client
// migration-safety-ok — brand-new table
export const clientSessionSequencePlans = pgTable("client_session_sequence_plans", {
  // migration-safety-ok — brand-new table
  id: serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  clientEmail: varchar("client_email", { length: 320 }).notNull().unique(),
  // migration-safety-ok — brand-new table — ordered list of pieces with AI rationale
  sequenceItems: jsonb("sequence_items").$type<Array<{
    pieceId: number | null;
    label: string;
    zone?: string | null;
    recommendedOrder: number;
    rationale: string;
    suggestedMonthOffset: number;
  }>>().notNull(),
  // migration-safety-ok — brand-new table — one-paragraph AI explanation
  aiSummary: text("ai_summary").notNull().default(""),
  // migration-safety-ok — brand-new table
  generatedAt: timestamp("generated_at").defaultNow().notNull(),
  // migration-safety-ok — brand-new table
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_client_session_sequence_plans_email").on(table.clientEmail),
]);

export type ClientSessionSequencePlan = typeof clientSessionSequencePlans.$inferSelect;

// ── Admin Briefings ───────────────────────────────────────────────────────────
// Stores the result of each automated daily business intelligence briefing run.
// One row per run (scheduled or manual). Latest row is the current briefing.
// migration-safety-ok — brand-new table
export const adminBriefings = pgTable("admin_briefings", {
  // migration-safety-ok — brand-new table
  id: serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  runAt: timestamp("run_at").defaultNow().notNull(),
  // migration-safety-ok — brand-new table — 0-100 composite health score
  healthScore: integer("health_score").notNull(),
  // migration-safety-ok — brand-new table — 'green' | 'amber' | 'red'
  overallStatus: varchar("overall_status", { length: 20 }).notNull(),
  // migration-safety-ok — brand-new table — KPI snapshot (all platform sources)
  kpis: jsonb("kpis").notNull().$type<{
    bookingsToday: number;
    bookingsThisWeek: number;
    bookingsThisMonth: number;
    revenueThisMonthCents: number;
    unconfirmedBookings: number;
    overdueCloseouts: number;
    pendingDeposits: number;
    cancelledThisWeek: number;
    openContacts: number;
    newLeadsThisWeek: number;
    newLeads7Days: number;
    subscriberCount: number;
    emailBouncesLast30d: number;
    emailSentLast30d: number;
    npsScore: number | null;
    openSupportTickets: number;
    probeFailuresLast24h: number;
    dlqCount: number;
    aiSpendLast30dCents: number;
    wauCount: number;
    pendingDraftCount: number;
    nguConvertedLeads30d: number;
  }>(),
  // migration-safety-ok — brand-new table
  alerts: jsonb("alerts").notNull().$type<Array<{ severity: string; message: string; category: string }>>(),
  // migration-safety-ok — brand-new table
  watchItems: jsonb("watch_items").notNull().$type<Array<{ message: string; category: string }>>(),
  // migration-safety-ok — brand-new table
  wins: jsonb("wins").notNull().$type<Array<{ message: string; category: string }>>(),
  // migration-safety-ok — brand-new table
  recommendedActions: jsonb("recommended_actions").notNull().$type<Array<{ priority: string; action: string; category: string; effort?: string; expectedImpact?: string }>>(),
  // migration-safety-ok — nullable, added via ALTER TABLE IF NOT EXISTS — one-sentence AI executive summary
  executiveSummary: text("executive_summary"),
  // migration-safety-ok — brand-new table — AI-synthesized narrative paragraph (null when AI unavailable)
  narrative: text("narrative"),
  // migration-safety-ok — brand-new table — true when AI synthesis was unavailable or errored
  synthesisFailed: boolean("synthesis_failed").notNull().default(false),
  // migration-safety-ok — brand-new table — 'cron' | 'manual'
  triggeredBy: varchar("triggered_by", { length: 20 }).notNull().default("cron"),
  // migration-safety-ok — nullable, added via ALTER TABLE IF NOT EXISTS — per-source aggregation failures
  dataSourceErrors: jsonb("data_source_errors").$type<Array<{ source: string; error: string }>>(),
  // migration-safety-ok — nullable, added via ALTER TABLE IF NOT EXISTS — raw pre-threshold query values
  rawDataSnapshot: jsonb("raw_data_snapshot").$type<Record<string, number | string | null>>(),
  // migration-safety-ok — nullable, added via ALTER TABLE IF NOT EXISTS — score + per-dimension breakdown
  businessHealthSnapshot: jsonb("business_health_snapshot").$type<{
    score: number;
    status: string;
    dimensions: Record<string, Record<string, number | string | null>>;
  }>(),
  // migration-safety-ok — brand-new table
  emailSentAt: timestamp("email_sent_at"),
}, (table) => [
  index("idx_admin_briefings_run_at").on(table.runAt.desc()),
]);

export type AdminBriefing = typeof adminBriefings.$inferSelect;
export type InsertAdminBriefing = typeof adminBriefings.$inferInsert;

// ── Studio Owner Briefing ─────────────────────────────────────────────────────
// Daily tactical briefing scoped to studio operations (booking throughput, artist
// utilization, client retention). Currently gated: email goes to agency admin only
// until feature flag 'studio_owner_briefing_live' is enabled.
export const studioOwnerBriefings = pgTable("studio_owner_briefings", {
  // migration-safety-ok — brand-new table
  id: serial("id").primaryKey(),
  // migration-safety-ok — brand-new table
  runAt: timestamp("run_at").defaultNow().notNull(),
  // migration-safety-ok — brand-new table — 0-100 composite health score
  healthScore: integer("health_score").notNull(),
  // migration-safety-ok — brand-new table — 'green' | 'amber' | 'red'
  overallStatus: varchar("overall_status", { length: 20 }).notNull(),
  // migration-safety-ok — brand-new table — studio-scoped KPI snapshot
  kpis: jsonb("kpis").notNull().$type<{
    bookingsToday: number;
    bookingsThisWeek: number;
    bookingsThisMonth: number;
    revenueThisWeekCents: number;
    revenueThisMonthCents: number;
    unconfirmedBookings: number;
    pendingDeposits: number;
    overdueCloseouts: number;
    cancellationsThisWeek: number;
    uniqueClientsThisMonth: number;
    returnClientsThisMonth: number;
    npsScoreRecent: number | null;
    npsCountRecent: number;
    healingTrackerUsersLast30d: number;
    flashBookingsThisMonth: number;
    pendingDraftCount: number;
    openContacts: number;
    artistBookingsThisWeek: Array<{ name: string; count: number }>;
  }>(),
  // migration-safety-ok — brand-new table
  alerts: jsonb("alerts").notNull().$type<Array<{ severity: string; message: string; category: string }>>(),
  // migration-safety-ok — brand-new table
  watchItems: jsonb("watch_items").notNull().$type<Array<{ message: string; category: string }>>(),
  // migration-safety-ok — brand-new table
  wins: jsonb("wins").notNull().$type<Array<{ message: string; category: string }>>(),
  // migration-safety-ok — brand-new table
  recommendedActions: jsonb("recommended_actions").notNull().$type<Array<{ priority: string; action: string; category: string; effort?: string; expectedImpact?: string }>>(),
  // migration-safety-ok — brand-new table — one-sentence AI executive summary
  executiveSummary: text("executive_summary"),
  // migration-safety-ok — brand-new table — AI-synthesized narrative paragraph
  narrative: text("narrative"),
  // migration-safety-ok — brand-new table — true when AI synthesis was unavailable
  synthesisFailed: boolean("synthesis_failed").notNull().default(false),
  // migration-safety-ok — brand-new table — 'cron' | 'manual'
  triggeredBy: varchar("triggered_by", { length: 20 }).notNull().default("cron"),
  // migration-safety-ok — brand-new table — per-source aggregation failures
  dataSourceErrors: jsonb("data_source_errors").$type<Array<{ source: string; error: string }>>(),
  // migration-safety-ok — brand-new table — when the email was dispatched
  emailSentAt: timestamp("email_sent_at"),
  // migration-safety-ok — brand-new table — true once feature flag enables owner delivery
  sentToOwner: boolean("sent_to_owner").notNull().default(false),
}, (table) => [
  index("idx_studio_owner_briefings_run_at").on(table.runAt.desc()),
]);

export type StudioOwnerBriefing = typeof studioOwnerBriefings.$inferSelect;
export type InsertStudioOwnerBriefing = typeof studioOwnerBriefings.$inferInsert;

// ── Studio Onboarding Sessions ────────────────────────────────────────────────
// One row per prospective studio client. The agency admin creates a session and
// shares the token-gated survey link; the client fills it in and submits.
// On submission the server generates a complete seed JSON stored in generated_seed.
// The agency admin then reviews and optionally triggers a one-click deploy.
// migration-safety-ok — brand-new table
export const studioOnboardingSessions = pgTable("studio_onboarding_sessions", {
  // migration-safety-ok — brand-new table
  id: serial("id").primaryKey(),
  // migration-safety-ok — brand-new table — 64-char hex token used in the shareable survey URL
  token: varchar("token", { length: 64 }).notNull().unique(),
  // migration-safety-ok — brand-new table — 'draft' | 'submitted' | 'deployed'
  status: varchar("status", { length: 20 }).notNull().default("draft"),
  // migration-safety-ok — brand-new table — ICP slug: tattoo_studio | body_art_studio | appointment_studio
  icp: varchar("icp", { length: 50 }),
  // migration-safety-ok — brand-new table — full survey payload; auto-saved on each step
  answers: jsonb("answers").$type<Record<string, unknown>>(),
  // migration-safety-ok — brand-new table — complete seed JSON generated on submit
  generatedSeed: jsonb("generated_seed").$type<Record<string, unknown>>(),
  // migration-safety-ok — brand-new table
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // migration-safety-ok — brand-new table
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  // migration-safety-ok — brand-new table — optional expiry; null = never expires
  expiresAt: timestamp("expires_at"),
  nguLeadId: integer("ngu_lead_id"),
}, (table) => [
  index("idx_studio_onboarding_token").on(table.token),
  index("idx_studio_onboarding_status").on(table.status),
  index("idx_studio_onboarding_created_at").on(table.createdAt),
]);

export const insertStudioOnboardingSessionSchema = createInsertSchema(studioOnboardingSessions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertStudioOnboardingSession = z.infer<typeof insertStudioOnboardingSessionSchema>;
export type StudioOnboardingSession = typeof studioOnboardingSessions.$inferSelect;
